import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  FileCheck2, Sparkles, ChevronLeft, ChevronRight, Search, MapPin, Send, ExternalLink,
  ShieldCheck, Copy, Check, X, Eye, Pencil, Paperclip, ImageIcon, FileText, Trash2,
  Stethoscope, Scale, Utensils, Printer, Save, FolderOpen, ChevronDown, ChevronUp, User2, Heart, ClipboardList,
  Activity, Cigarette, History, LineChart as LineChartIcon, TrendingUp,
  AlertCircle,
} from "lucide-react";
import { toast } from "sonner";
import SynapseLoader from "@/components/SynapseLoader";
import { IntakePreviewDialog } from "@/components/IntakePreviewDialog";
import { VISIT_MODULES, INSTRUMENTAL_TYPES_BY_SPEC, GynVisitBlock, InstrumentalExamsBuilder, serializeGynGroup, serializeInstrumental } from "@/components/GynVisitFields";
import { ORL_REASONS, VISIT_TYPES, ORL_FAM_FIELDS, ORL_PERS_FIELDS, ORL_HISTORY_FIELDS, ORL_EXPO_FIELDS, ORL_EXAMS_FIELDS, ORL_PRIORITY } from "@/components/OrlVisitFields";
import { DERMA_REASONS, DERMA_FAM_FIELDS, DERMA_PERS_FIELDS, DERMA_HISTORY_FIELDS, DERMA_SUN_FIELDS, DERMA_PROD_FIELDS, DERMA_TRIC_FIELDS, DERMA_PRIORITY } from "@/components/DermaVisitFields";
import { AudiogramEditor, serializeAudiogram, audiogramHasData } from "@/components/Audiogram";
import { BodyMap, bodyMapToSvg, bodyMapSitesText } from "@/components/BodyMap";
import { DermaPhotos } from "@/components/DermaPhotos";
import { Mail as MailIcon, CheckCircle2 as CheckCircle2Icon } from "lucide-react";
import SignaturePad from "@/components/SignaturePad";
import { calcCodiceFiscale, calcAge, COMUNI_LIST } from "@/lib/codiceFiscale";
import { VISIT_MOTIVES } from "@/lib/nutritionMotives";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ReferenceLine, ResponsiveContainer, CartesianGrid,
} from "recharts";

// Riquadri colorati della sezione VISITA (tutte le specializzazioni; il nutrizionista usa il layout piatto)
// Palette monocromatica istituzionale (allineata al PDF): sfondo quasi bianco, bordo grigio-azzurro, barra/titolo navy
const VISIT_BLOCK_THEME = { border: "border-slate-300", bg: "bg-white", head: "bg-slate-50", badge: "bg-blue-900", title: "text-blue-900" };
const VisitBlock = ({ n, title, hint, children, testid, plain }) => {
  if (plain) return <>{children}</>;
  const t = VISIT_BLOCK_THEME;
  return (
    <div className={`rounded-lg border ${t.border} border-l-4 border-l-blue-900 ${t.bg} shadow-sm overflow-hidden`} data-testid={testid}>
      <div className={`flex items-center gap-2 px-3 md:px-4 py-2 ${t.head} border-b ${t.border}`}>
        <span className={`h-7 w-7 rounded-full ${t.badge} text-white text-xs font-bold flex items-center justify-center flex-shrink-0`}>{n}</span>
        <h3 className={`text-sm font-bold ${t.title} uppercase tracking-wide`}>{title}</h3>
        {hint && <span className="text-[11px] text-slate-600 hidden md:inline">· {hint}</span>}
      </div>
      <div className="p-3 md:p-4">{children}</div>
    </div>
  );
};

export default function NuovoRefertoPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isSpecialist = user?.profession === "medico_specialista" || user?.profession === "medico_medicina_generale" || user?.profession === "biologo_nutrizionista";
  const isNutritionist = user?.profession === "biologo_nutrizionista";
  const isDoctor = !isNutritionist; // specialista o MMG: flusso "valutazione clinica"

  // Modulo della visita (specialista)
  const [modules, setModules] = useState([]);
  const [moduleId, setModuleId] = useState("");
  const [moduleDef, setModuleDef] = useState(null);
  const [moduleAnswers, setModuleAnswers] = useState({});
  const [moduleSuggestBusy, setModuleSuggestBusy] = useState(false);
  const [moduleSuggest, setModuleSuggest] = useState(null);
  const [examsToMmg, setExamsToMmg] = useState(false);
  const [therapyToMmg, setTherapyToMmg] = useState(false);
  // Riconoscimento paziente già visitato (specialista) + riassunto ultima visita
  const [knownPatient, setKnownPatient] = useState(null); // {count, items, patient}
  const [knownDismissed, setKnownDismissed] = useState(false);
  const [showLastVisit, setShowLastVisit] = useState(false);
  // Anamnesi ginecologica strutturata (solo specialisti in Ginecologia)
  const isGyn = isDoctor && /ginecolog|ostetric/i.test(user?.specialization || "");
  const isOrl = isDoctor && /otorino|\borl\b/i.test(user?.specialization || "");
  const isDerma = isDoctor && /dermatolog/i.test(user?.specialization || "");
  // Specializzazioni con MOTIVO strutturato (problema principale + tipo di visita)
  const hasSpecReason = isOrl || isDerma;
  const SPEC_REASONS = isOrl ? ORL_REASONS : DERMA_REASONS;
  const [visitType, setVisitType] = useState("prima");
  const [orlReason, setOrlReason] = useState("");
  const [orlFam, setOrlFam] = useState({});
  const [orlPers, setOrlPers] = useState({});
  const [orlHist, setOrlHist] = useState({});
  const [orlExpo, setOrlExpo] = useState({});
  const [orlExams, setOrlExams] = useState({});
  const [openOrl, setOpenOrl] = useState(true);
  const [openExams, setOpenExams] = useState(false);
  // Anamnesi dermatologica strutturata (solo Dermatologia)
  const [dermFam, setDermFam] = useState({});
  const [dermPers, setDermPers] = useState({});
  const [dermHist, setDermHist] = useState({});
  const [dermSun, setDermSun] = useState({});
  const [dermProd, setDermProd] = useState({});
  const [dermTric, setDermTric] = useState({});
  const [openDerm, setOpenDerm] = useState(true);
  const [openSun, setOpenSun] = useState(false);
  const [openProd, setOpenProd] = useState(false);
  const [openTric, setOpenTric] = useState(false);
  // Dermatologia: mappa corporea (marker sulla sagoma) + documentazione fotografica (Object Storage)
  const [bodyMarkers, setBodyMarkers] = useState([]);
  const [showBodyMap, setShowBodyMap] = useState(false);
  const [dermaPhotos, setDermaPhotos] = useState([]);
  const [gyn, setGyn] = useState({});
  const [obst, setObst] = useState({});
  const [sexual, setSexual] = useState({});
  const [screen, setScreen] = useState({});
  const [gynExtra, setGynExtra] = useState({}); // ricoveri, alcol, attività fisica, alimentazione
  const [openGyn, setOpenGyn] = useState(false);
  const [openObst, setOpenObst] = useState(false);
  const [openSexual, setOpenSexual] = useState(false);
  const [openScreen, setOpenScreen] = useState(false);
  const GYN_FIELDS = [["menarca", "Menarca"], ["regolarita", "Regolarità del ciclo"], ["durata_ciclo", "Durata del ciclo"],
    ["durata_flusso", "Durata del flusso"], ["um", "Ultima mestruazione"], ["dolore", "Dolore mestruale"],
    ["patologie", "Precedenti patologie ginecologiche (endometriosi, fibromi, cisti, PCOS)"],
    ["infezioni", "Precedenti infezioni / IST"], ["interventi", "Precedenti interventi ginecologici"]];
  const OBST_FIELDS = [["gravidanze", "Gravidanze"], ["parti", "Parti"], ["aborti", "Abortività"], ["ectopiche", "Gravidanze ectopiche"],
    ["complicanze", "Complicanze nelle gravidanze precedenti"], ["modalita", "Modalità dei parti"], ["neonatali", "Complicanze neonatali"]];
  const SEX_FIELDS = [["metodo", "Metodo contraccettivo"], ["precedenti", "Precedenti contraccettivi"], ["tollerabilita", "Tollerabilità"],
    ["desiderio", "Desiderio di gravidanza"], ["sessuali", "Problematiche sessuali"]];
  const SCREEN_FIELDS = [["pap", "Pap test"], ["hpv", "HPV test"], ["mammo", "Mammografia / eco mammaria"],
    ["eco", "Ecografie ginecologiche"], ["altri", "Altri controlli"]];
  const flatAn = (obj, fields) => fields.filter(([k]) => (obj[k] || "").trim()).map(([k, l]) => `${l}: ${obj[k].trim()}`).join(" · ");
  // Priorità anamnesi in base al modulo scelto
  const gynPriority = isOrl ? (ORL_PRIORITY[moduleId] || []) : isDerma ? (DERMA_PRIORITY[moduleId] || []) : ({
    gravidanza: ["obst"], ginecologia: ["gyn"], followup_ginecologico: ["gyn"],
    menopausa: ["gyn", "screen"], contraccezione: ["sexual", "gyn"], fertilita: ["sexual", "obst", "gyn"],
    controllo_ginecologico: ["screen"],
  }[moduleId] || []);
  useEffect(() => {
    if (isOrl) { setOpenOrl(true); setOpenExams(gynPriority.includes("exams")); setOpenHabits(gynPriority.includes("expo")); setOpenFam(gynPriority.includes("fam")); return; }
    if (isDerma) { setOpenDerm(true); setOpenSun(gynPriority.includes("sun")); setOpenProd(gynPriority.includes("prod")); setOpenTric(gynPriority.includes("tric")); setOpenFam(gynPriority.includes("fam")); return; }
    if (!isGyn) return;
    setOpenObst(gynPriority.includes("obst")); setOpenGyn(gynPriority.includes("gyn"));
    setOpenSexual(gynPriority.includes("sexual")); setOpenScreen(gynPriority.includes("screen"));
  }, [moduleId, isGyn]); // eslint-disable-line react-hooks/exhaustive-deps
  const PriorityBadge = ({ k }) => gynPriority.includes(k)
    ? <span className="ml-2 text-[10px] font-semibold uppercase tracking-wide text-[#1E88E5] bg-[#1E88E5]/10 rounded px-1.5 py-0.5" data-testid={`an-priority-${k}`}>consigliata per il modulo</span>
    : null;
  const AnFields = ({ obj, setObj, fields, prefix }) => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
      {fields.map(([k, l]) => (
        <div key={k} className="flex items-center gap-2">
          <span className="text-[11px] text-slate-500 w-40 flex-shrink-0 leading-tight">{l}</span>
          <Input value={obj[k] || ""} onChange={(e) => setObj({ ...obj, [k]: e.target.value })} className="h-8 text-sm"
                 data-testid={`${prefix}-${k}`} />
        </div>
      ))}
    </div>
  );
  const AnAccordion = ({ title, k, open, setOpen, children }) => (
    <div className={`rounded-lg border bg-white overflow-hidden ${gynPriority.includes(k) ? "border-[#1E88E5]/50" : "border-slate-200"}`} data-testid={`an-section-${k}`}>
      <button type="button" onClick={() => setOpen(!open)} data-testid={`an-toggle-${k}`} className="w-full px-3 py-2.5 flex items-center justify-between hover:bg-slate-50">
        <span className="text-sm font-semibold text-slate-900">{title}<PriorityBadge k={k} /></span>
        {open ? <ChevronUp className="h-4 w-4 text-slate-500" /> : <ChevronDown className="h-4 w-4 text-slate-500" />}
      </button>
      {open && <div className="px-3 py-3 border-t border-slate-100 space-y-3">{children}</div>}
    </div>
  );
  const [activeSection, setActiveSection] = useState("sec-anagrafica");
  useEffect(() => {
    const cfParam = new URLSearchParams(window.location.search).get("cf");
    if (cfParam && cfParam.length === 16) { setPfiscalCode(cfParam.toUpperCase()); setPfiscalCodeManual(true); }
  }, []);

  const [pfirst, setPfirst] = useState("");
  const [plast, setPlast] = useState("");
  const [psex, setPsex] = useState("");
  const [pbirth, setPbirth] = useState("");
  const [pbirthPlace, setPbirthPlace] = useState("");
  const [pbirthProvince, setPbirthProvince] = useState("");
  const [pfiscalCode, setPfiscalCode] = useState("");
  const [pfiscalCodeManual, setPfiscalCodeManual] = useState(false);
  const [pphone, setPphone] = useState("");
  const [pemail, setPemail] = useState("");

  // Screening precedenti + anamnesi + abitudini
  const [previousScreenings, setPreviousScreenings] = useState("");
  const [presentedDocumentation, setPresentedDocumentation] = useState("");
  const [famHasIssues, setFamHasIssues] = useState(false);
  const [familyHistory, setFamilyHistory] = useState("");
  const [hasConditions, setHasConditions] = useState(false);
  const [personalConditions, setPersonalConditions] = useState("");
  const [hasMeds, setHasMeds] = useState(false);
  const [currentMedications, setCurrentMedications] = useState("");
  const [hasSurgeries, setHasSurgeries] = useState(false);
  const [previousSurgeries, setPreviousSurgeries] = useState("");
  const [hasAllergies, setHasAllergies] = useState(false);
  const [allergies, setAllergies] = useState("");
  const [pathologyHistory, setPathologyHistory] = useState("");
  const [smokes, setSmokes] = useState(false);
  const [smoking, setSmoking] = useState("");
  const [diuresis, setDiuresis] = useState("");
  const [bowel, setBowel] = useState("");
  const [otherHabits, setOtherHabits] = useState("");
  const [aiPolishAnamnesis, setAiPolishAnamnesis] = useState(true);
  // AI-generated preview modals (on-demand)
  const [aiPreview, setAiPreview] = useState(null); // { kind, title, text, color }
  const [aiPreviewBusy, setAiPreviewBusy] = useState(false);

  // Visita
  const [visitReason, setVisitReason] = useState("");
  const [observations, setObservations] = useState("");
  // Motivo della visita nutrizionale + risposte adattive (spostato in alto perché usato in canSubmit)
  const [visitMotive, setVisitMotive] = useState("");
  const [motiveAnswers, setMotiveAnswers] = useState({});
  const [instrumentalExams, setInstrumentalExams] = useState("");
  const [instrStruct, setInstrStruct] = useState({ types: [], values: {} });
  const [gynVisitAll, setGynVisitAll] = useState({});
  const [eoMode, setEoMode] = useState("moduli"); // "moduli" = campi strutturati, "manuale" = solo testo libero
  const [aiDxBusy, setAiDxBusy] = useState(false);
  const gynVisitModule = isDoctor && VISIT_MODULES[moduleId] ? moduleId : (isOrl && !moduleId ? "orl_generale" : (isDerma && !moduleId ? "derma_generale" : ""));
  const instrTypes = isOrl ? INSTRUMENTAL_TYPES_BY_SPEC.orl : isDerma ? INSTRUMENTAL_TYPES_BY_SPEC.derma : INSTRUMENTAL_TYPES_BY_SPEC.gyn;
  const [audiogram, setAudiogram] = useState(null); // null = non richiesto
  const [prevAudiogram, setPrevAudiogram] = useState(null);
  const [audiogramCompare, setAudiogramCompare] = useState(true);
  useEffect(() => {
    const cf = pfiscalCode.trim().toUpperCase();
    if (!isOrl || cf.length !== 16) { setPrevAudiogram(null); return; }
    api.get("/referto/last-audiogram", { params: { patient_fiscal_code: cf } })
      .then((r) => setPrevAudiogram(r.data.found ? r.data : null)).catch(() => setPrevAudiogram(null));
  }, [isOrl, pfiscalCode]);
  // valori strutturati separati per modulo: cambiando modulo non restano dati "fantasma"
  const gynVisit = gynVisitAll[gynVisitModule] || {};
  const setGynVisit = (v) => setGynVisitAll((all) => ({ ...all, [gynVisitModule]: typeof v === "function" ? v(all[gynVisitModule] || {}) : v }));
  const gynEO = gynVisitModule && eoMode === "moduli" ? serializeGynGroup(gynVisitModule, ["eo"], gynVisit) : "";
  const gynTerInTherapy = !!gynVisitModule && VISIT_MODULES[gynVisitModule].terIn === "therapy";
  const suggestDiagnosisAI = async () => {
    setAiDxBusy(true);
    try {
      const p = buildFormPayload();
      const an = [p.family_history && `Familiarità: ${p.family_history}`, p.personal_conditions && `Patologie: ${p.personal_conditions}`,
        p.current_medications && `Farmaci: ${p.current_medications}`, p.allergies && `Allergie: ${p.allergies}`,
        p.pathology_history, p.gyn_history, p.obstetric_history].filter(Boolean).join(" · ");
      const { data } = await api.post("/referto/ai-suggest-diagnosis", {
        visit_reason: p.visit_reason, observations: p.observations, instrumental_exams: p.instrumental_exams,
        presented_documentation: p.presented_documentation, module_label: p.visit_motive_label, module_answers: p.motive_answers,
        anamnesis: an, age: p.patient_birth_date ? Math.floor((Date.now() - new Date(p.patient_birth_date)) / 31557600000) : null, sex: p.patient_sex,
      });
      const s = (data.suggestion || "").trim();
      if (!s) { toast.warning("Nessun suggerimento generato"); return; }
      if (diagnosis.trim() && !window.confirm("Sostituisco il testo di valutazione/diagnosi con il suggerimento AI?")) return;
      setDiagnosis(s); toast.success("Suggerimento AI inserito — rivedi e modifica");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Suggerimento AI non riuscito"); }
    finally { setAiDxBusy(false); }
  };
  const [lastVisitStruct, setLastVisitStruct] = useState(null);
  useEffect(() => {
    const cf = pfiscalCode.trim().toUpperCase();
    if (!gynVisitModule || cf.length !== 16) { setLastVisitStruct(null); return; }
    api.get("/referto/last-visit-struct", { params: { patient_fiscal_code: cf, symptom_module: gynVisitModule } })
      .then((r) => setLastVisitStruct(r.data.found ? r.data : null)).catch(() => setLastVisitStruct(null));
  }, [gynVisitModule, pfiscalCode]);
  const copyLastVisit = () => {
    if (!lastVisitStruct) return;
    const hasData = Object.keys(gynVisit).length > 0 || (instrStruct.types || []).length > 0;
    if (hasData && !window.confirm("Sovrascrivo i campi strutturati della visita con quelli dell'ultima visita?")) return;
    setGynVisit(lastVisitStruct.gyn || {});
    setInstrStruct(lastVisitStruct.instr || { types: [], values: {} });
    toast.success(`Campi copiati dalla visita del ${new Date(lastVisitStruct.created_at).toLocaleDateString("it-IT")} — rivedi e aggiorna i valori`);
  };
  const [diagnosis, setDiagnosis] = useState("");
  const [recommendedExams, setRecommendedExams] = useState("");
  const [therapy, setTherapy] = useState("");
  const [recommendations, setRecommendations] = useState("");
  const [nextCheck, setNextCheck] = useState("");
  const [useAi, setUseAi] = useState(true);
  const [signature, setSignature] = useState(null);
  const [useProfileSig, setUseProfileSig] = useState(true);
  const [attachments, setAttachments] = useState([]);   // [{filename, mime_type, size, data_b64}]
  const [attachBusy, setAttachBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [preview, setPreview] = useState(null);   // { referto_id, download_url, prescription_pending, prescription_mmg_name }
  const [previewTab, setPreviewTab] = useState("pdf");   // "pdf" | "mmg" | "ref"
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  // Sezione richiesta prescrizione al MMG del paziente
  const [mmgQuery, setMmgQuery] = useState("");
  const [mmgProvince, setMmgProvince] = useState("all");
  const [mmgResults, setMmgResults] = useState([]);
  const [selectedMmg, setSelectedMmg] = useState(null);
  const [prescriptionText, setPrescriptionText] = useState("");
  const [sendPrescription, setSendPrescription] = useState(false);
  const [provinces, setProvinces] = useState([]);
  const [rxBusy, setRxBusy] = useState(false);

  // Sede da cui il medico sta emettendo il referto (solo se ha più sedi registrate)
  const hasMultipleLocations = (user?.locations || []).length > 1;
  const [locationIndex, setLocationIndex] = useState(0);

  // Piano nutrizionale AI (solo per biologo nutrizionista)
  const [nutritionActivity, setNutritionActivity] = useState("moderate");
  const [nutritionOverrideKcal, setNutritionOverrideKcal] = useState("");
  const [nutritionNotes, setNutritionNotes] = useState("");
  const [nutritionDietGuidance, setNutritionDietGuidance] = useState("");     // indicazioni preliminari del nutrizionista prima dell'AI
  const [nutritionPatientMessage, setNutritionPatientMessage] = useState(""); // motivazione della dieta per il paziente
  // Piano dettagliato multi-settimana (default 1 settimana; se attivato, il nutrizionista sceglie 2..8 settimane)
  const [nutritionMultiWeek, setNutritionMultiWeek] = useState(false);
  const [nutritionWeeksCount, setNutritionWeeksCount] = useState(2);
  // Razionale AI clinico ("Ho pensato di impostare così perché…") + feedback iterativo del nutrizionista
  const [nutritionAiRationale, setNutritionAiRationale] = useState("");
  const [nutritionRationaleFeedback, setNutritionRationaleFeedback] = useState("");
  const [nutritionRationaleAccepted, setNutritionRationaleAccepted] = useState(false);
  // Chat AI sulla settimana tipo (modifiche mirate mantenendo il target kcal)
  const [nutritionWeeklyFeedback, setNutritionWeeklyFeedback] = useState("");
  const [nutritionWeeklyBusy, setNutritionWeeklyBusy] = useState(false);
  const [nutritionWeeklyHistory, setNutritionWeeklyHistory] = useState([]);   // [{q, ts}]

  // Misure aggiuntive per nutrizionista (addome, braccio, coscia) + gravidanza
  const [abdomenCm, setAbdomenCm] = useState("");
  const [armCm, setArmCm] = useState("");
  const [thighCm, setThighCm] = useState("");
  const [bellyCm, setBellyCm] = useState("");
  const [isPregnant, setIsPregnant] = useState(false);

  // BIA collassabile (di default chiusa)
  const [biaExpanded, setBiaExpanded] = useState(false);

  // Lettera al MMG del paziente da consegnare a mano (quando MMG NON su Synappsi)
  const [mmgHandLetter, setMmgHandLetter] = useState("");
  const [mmgHandLetterBusy, setMmgHandLetterBusy] = useState(false);
  const [wantHandLetter, setWantHandLetter] = useState(false);
  // AI Suggerisci esami (nutrizionista) — analizza screening precedenti + anamnesi
  const [aiSuggestExamsBusy, setAiSuggestExamsBusy] = useState(false);

  // Anamnesi alimentare (nutrizionista) — checkbox multipli + note
  const FOOD_HABITS_OPTIONS = [
    { id: "colazione_regolare", label: "Fa sempre colazione" },
    { id: "salta_colazione", label: "Salta spesso la colazione" },
    { id: "mangia_fuori", label: "Mangia spesso fuori casa" },
    { id: "snack_notturni", label: "Fa snack notturni / spilucca la sera" },
    { id: "mangia_veloce", label: "Mangia velocemente / distratto" },
    { id: "porzioni_grandi", label: "Porzioni abbondanti" },
    { id: "dolci_frequenti", label: "Consuma dolci quotidianamente" },
    { id: "bevande_zuccherate", label: "Beve bevande zuccherate / gassate" },
    { id: "poca_acqua", label: "Beve meno di 1,5 L d'acqua al giorno" },
    { id: "vegetariano", label: "Dieta vegetariana" },
    { id: "vegano", label: "Dieta vegana" },
    { id: "intolleranze", label: "Intolleranze/allergie alimentari" },
  ];
  const LIFESTYLE_OPTIONS = [
    { id: "sedentario", label: "Vita sedentaria (>6h seduto/die)" },
    { id: "sport_regolare", label: "Attività sportiva regolare" },
    { id: "cammino_quotidiano", label: "Cammina almeno 30 min al giorno" },
    { id: "fumo", label: "Fuma" },
    { id: "alcol_saltuario", label: "Beve alcolici saltuariamente" },
    { id: "alcol_regolare", label: "Beve alcolici quotidianamente" },
    { id: "sonno_irregolare", label: "Sonno irregolare / poche ore" },
    { id: "sonno_qualita", label: "Sonno di buona qualità (7-8h)" },
    { id: "stress_alto", label: "Livello di stress alto" },
    { id: "turnista", label: "Lavora su turni / notturno" },
  ];
  const [foodHabits, setFoodHabits] = useState([]);
  const [foodHabitsNotes, setFoodHabitsNotes] = useState("");
  const [lifestyleHabits, setLifestyleHabits] = useState([]);
  const [lifestyleHabitsNotes, setLifestyleHabitsNotes] = useState("");
  const [openAlim, setOpenAlim] = useState(false);
  const [openLife, setOpenLife] = useState(false);
  const toggleInList = (list, id) =>
    list.includes(id) ? list.filter((x) => x !== id) : [...list, id];

  const [nutritionPlanBusy, setNutritionPlanBusy] = useState(false);
  const [nutritionPlanData, setNutritionPlanData] = useState(null);
  const [nutritionWeekly, setNutritionWeekly] = useState("");
  const [nutritionRecommended, setNutritionRecommended] = useState("");
  const [nutritionAvoid, setNutritionAvoid] = useState("");
  const [nutritionPhases, setNutritionPhases] = useState("");
  const [nutritionFollowUp, setNutritionFollowUp] = useState("");
  const [nutritionPlanNotes, setNutritionPlanNotes] = useState("");
  // Tipo di dieta consigliata (AI-suggerito, biologo può modificare)
  const [nutritionDietType, setNutritionDietType] = useState("");
  const [nutritionDietTypeRationale, setNutritionDietTypeRationale] = useState("");
  // ============================================================
  // VISITA DI CONTROLLO (follow-up) — attivabile quando esistono visite precedenti
  // ============================================================
  const [isFollowupVisit, setIsFollowupVisit] = useState(false);
  const [slideDir, setSlideDir] = useState("right");
  const sectionTabs = useMemo(() => (isDoctor
    ? [{ id: "sec-anagrafica", l: "Anagrafica", I: User2 }, { id: "sec-motivo", l: "Motivo", I: ClipboardList },
       ...(isFollowupVisit ? [] : [{ id: "sec-anamnesi", l: "Anamnesi", I: Heart }]),
       { id: "sec-modulo", l: "Modulo", I: Stethoscope }, { id: "sec-visita", l: "Visita", I: Activity },
       { id: "sec-comunicazioni", l: "Comunicaz.", I: Send }]
    : [{ id: "sec-anagrafica", l: "Anagrafica", I: User2 }, { id: "sec-anamnesi", l: "Anamnesi", I: Heart },
       { id: "sec-motivo-nutri", l: "Motivo", I: ClipboardList }, { id: "sec-antropometria", l: "Antropometria", I: Scale },
       { id: "sec-visita", l: "Valutazione", I: Activity }, { id: "sec-piano", l: "Piano", I: Utensils },
       { id: "sec-comunicazioni", l: "Comunicaz.", I: Send }]), [isDoctor, isFollowupVisit]);
  const sectionIndex = Math.max(0, sectionTabs.findIndex((t) => t.id === activeSection));
  // sec-piano è annidata dentro sec-comunicazioni: restano visibili insieme
  const sectionVisible = useMemo(() => {
    const v = new Set([activeSection]);
    if (activeSection === "sec-piano") v.add("sec-comunicazioni");
    return v;
  }, [activeSection]);
  const goToSection = (id) => {
    const from = sectionTabs.findIndex((t) => t.id === activeSection);
    const to = sectionTabs.findIndex((t) => t.id === id);
    setSlideDir(to < from ? "left" : "right");
    setActiveSection(id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const stepSection = (delta) => {
    const next = sectionTabs[sectionIndex + delta];
    if (next) goToSection(next.id);
  };
  const [includeFirstVisitSummary, setIncludeFirstVisitSummary] = useState(false);
  const [followupCtx, setFollowupCtx] = useState(null);   // response da /followup-context
  const [followupBusy, setFollowupBusy] = useState(false);
  const [followupAvailable, setFollowupAvailable] = useState(false); // ci sono visite precedenti?
  // Percentuali dinamiche del "Piatto Salutare" adattate al tipo di dieta
  const [platePctVeg, setPlatePctVeg] = useState(50);
  const [platePctProtein, setPlatePctProtein] = useState(25);
  const [platePctCarbs, setPlatePctCarbs] = useState(25);
  // Conferma finale: la valutazione nutrizionale è completa (verrà salvata in cartella)
  const [evaluationComplete, setEvaluationComplete] = useState(false);

  // Bioimpedenziometria (BIA) — solo nutrizionista
  const [biaLeanMassKg, setBiaLeanMassKg] = useState("");
  const [biaLeanMassPct, setBiaLeanMassPct] = useState("");
  const [biaFatMassKg, setBiaFatMassKg] = useState("");
  const [biaFatMassPct, setBiaFatMassPct] = useState("");
  const [biaTbwPct, setBiaTbwPct] = useState("");
  const [biaPhaseAngle, setBiaPhaseAngle] = useState("");
  const [biaMuscleMassKg, setBiaMuscleMassKg] = useState("");

  // Valutazione dello stato nutrizionale (sostituisce "Esame obiettivo" per nutrizionista)
  const [nutritionalStateEvaluation, setNutritionalStateEvaluation] = useState("");

  // Lista della spesa settimanale (generata da AI col piano)
  const [nutritionShoppingList, setNutritionShoppingList] = useState("");

  // Info sintetiche del piano precedente (auto-caricate quando il paziente è già visto)
  // { lastWeight, lastTargetWeight, lastTargetKcal, lastDate, lastDateLabel }
  const [previousPlanInfo, setPreviousPlanInfo] = useState(null);
  const [sendReferral, setSendReferral] = useState(false);
  const [refQuery, setRefQuery] = useState("");
  const [refProvince, setRefProvince] = useState("all");
  const [refSpecialization, setRefSpecialization] = useState("");
  const [refResults, setRefResults] = useState([]);
  const [selectedRef, setSelectedRef] = useState(null);
  const [referralReason, setReferralReason] = useState("");

  useEffect(() => {
    api.get("/contacts/provinces").then(({ data }) => setProvinces(data || []))
       .catch(() => {});
  }, []);

  // Età calcolata dal DOB
  const patientAge = useMemo(() => calcAge(pbirth), [pbirth]);
  // CF calcolato automaticamente (se abbastanza dati) — override manuale possibile
  useEffect(() => {
    if (pfiscalCodeManual) return;
    const cf = calcCodiceFiscale({
      firstName: pfirst, lastName: plast,
      sex: psex, dob: pbirth, birthPlace: pbirthPlace,
    });
    if (cf) setPfiscalCode(cf);
  }, [pfirst, plast, psex, pbirth, pbirthPlace, pfiscalCodeManual]);

  // Drafts
  const [drafts, setDrafts] = useState([]);
  const [currentDraftId, setCurrentDraftId] = useState(null);
  const [showDrafts, setShowDrafts] = useState(false);
  const loadDrafts = async () => {
    try {
      const { data } = await api.get("/referto/drafts");
      setDrafts(data.items || []);
    } catch {}
  };
  useEffect(() => { if (isSpecialist) loadDrafts(); }, [isSpecialist]);

  // Accordion open state
  const [openFam, setOpenFam] = useState(false);
  const [openPers, setOpenPers] = useState(false);
  const [openHabits, setOpenHabits] = useState(false);
  const [openVisit, setOpenVisit] = useState(true);

  const hasSearch = mmgQuery.trim() || (mmgProvince && mmgProvince !== "all");
  useEffect(() => {
    if (!hasSearch) { setMmgResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const params = { role: "medico_medicina_generale" };
        if (mmgQuery.trim()) params.q = mmgQuery.trim();
        if (mmgProvince && mmgProvince !== "all") params.province = mmgProvince;
        const { data } = await api.get("/contacts", { params });
        setMmgResults(data || []);
      } catch { setMmgResults([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [mmgQuery, mmgProvince, hasSearch]);

  // Ricerca specialisti per consulto
  const hasRefSearch = refQuery.trim() || (refProvince && refProvince !== "all") || refSpecialization.trim();
  useEffect(() => {
    if (!hasRefSearch) { setRefResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const params = { role: "medico_specialista" };
        const qParts = [refQuery.trim(), refSpecialization.trim()].filter(Boolean);
        if (qParts.length) params.q = qParts.join(" ");
        if (refProvince && refProvince !== "all") params.province = refProvince;
        const { data } = await api.get("/contacts", { params });
        // Escludi lo specialista corrente
        setRefResults((data || []).filter((c) => c.id !== user?.id));
      } catch { setRefResults([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [refQuery, refProvince, refSpecialization, hasRefSearch, user?.id]);

  // ---- Modulo della visita (specialista) ----
  useEffect(() => {
    if (!isDoctor) return;
    api.get("/cases/modules").then(({ data }) => setModules(data.modules || [])).catch(() => setModules([]));
  }, [isDoctor]);
  const selectModule = async (id) => {
    setModuleId(id); setModuleAnswers({});
    if (!id) { setModuleDef(null); return; }
    try { const { data } = await api.get(`/cases/modules/${id}`); setModuleDef(data); }
    catch { setModuleDef(null); toast.error("Modulo non disponibile"); }
  };
  const suggestModuleAI = async () => {
    setModuleSuggestBusy(true);
    try {
      const { data } = await api.post("/cases/ai-suggest-module", { text: [hasSpecReason ? orlReason : "", visitReason.trim()].filter(Boolean).join(" — ") });
      setModuleSuggest(data);
      if (data.module_id && !moduleId) await selectModule(data.module_id);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Suggerimento non disponibile"); }
    finally { setModuleSuggestBusy(false); }
  };
  // Collega esami/terapia alla prescrizione del MMG
  useEffect(() => {
    if (!isDoctor) return;
    const parts = [];
    if (examsToMmg && recommendedExams.trim()) parts.push(`ESAMI:\n${recommendedExams.trim()}`);
    if (therapyToMmg && therapy.trim()) parts.push(`TERAPIA:\n${therapy.trim()}`);
    if (parts.length) { setPrescriptionText(parts.join("\n\n")); setSendPrescription(true); }
    else if (examsToMmg === false && therapyToMmg === false) { /* lascia il testo manuale */ }
  }, [examsToMmg, therapyToMmg, recommendedExams, therapy, isDoctor]);

  // ---- Riconoscimento automatico paziente già visitato (specialista, dal CF) ----
  useEffect(() => {
    if (!isDoctor) return;
    const cf = pfiscalCode.trim().toUpperCase();
    if (cf.length !== 16) { setKnownPatient(null); setKnownDismissed(false); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/patients/timeline", { params: { fiscal_code: cf } });
        setKnownPatient(data.count > 0 ? data : null);
        setKnownDismissed(false);
      } catch { setKnownPatient(null); }
    }, 400);
    return () => clearTimeout(t);
  }, [pfiscalCode, isDoctor]);
  const lastVisit = knownPatient?.items?.[0] || null;

  const canSubmit = pfirst.trim() && plast.trim() && pemail.trim()
                    && (isNutritionist
                        ? (!!visitMotive && nutritionalStateEvaluation.trim())
                        : (((visitReason.trim() || (hasSpecReason && orlReason))) && (observations.trim() || gynEO)));

  // Lista dei campi obbligatori mancanti — mostrata quando "Anteprima referto PDF" è disabilitato
  const missingRequired = [
    !pfirst.trim() && "Nome del paziente",
    !plast.trim() && "Cognome del paziente",
    !pemail.trim() && "Email del paziente",
    isNutritionist
      ? (!visitMotive && "Motivo della valutazione nutrizionale (selezionalo nella card dedicata)")
      : (!visitReason.trim() && !(hasSpecReason && orlReason) && "Motivo della visita"),
    isNutritionist
      ? (!nutritionalStateEvaluation.trim() && "Valutazione dello stato nutrizionale")
      : (!observations.trim() && !gynEO && "Rilevazione della visita"),
  ].filter(Boolean);
  const joinTxt = (...parts) => parts.map((p) => (p || "").trim()).filter(Boolean).join("\n");

  const buildFormPayload = () => ({
    symptom_module: isDoctor ? moduleId : "",
    symptom_answers: isDoctor && moduleDef
      ? moduleDef.questions
          .map((q) => ({ question_id: q.id, question_label: q.label, answer: (moduleAnswers[q.id] || "").trim() }))
          .filter((a) => a.answer)
      : [],
    patient_first_name: pfirst.trim(),
    patient_last_name: plast.trim(),
    patient_sex: psex,
    patient_birth_date: pbirth || "",
    patient_birth_place: pbirthPlace.trim(),
    patient_birth_province: pbirthProvince,
    patient_fiscal_code: pfiscalCode.trim().toUpperCase(),
    patient_phone: pphone.trim(),
    patient_email: pemail.trim(),
    previous_screenings: [previousScreenings.trim(), isGyn ? flatAn(screen, SCREEN_FIELDS) : "", isOrl ? flatAn(orlExams, ORL_EXAMS_FIELDS) : ""].filter(Boolean).join("\n"),
    gyn_history: isGyn ? flatAn(gyn, GYN_FIELDS) : "",
    obstetric_history: isGyn ? flatAn(obst, OBST_FIELDS) : "",
    sexual_health: isGyn ? flatAn(sexual, SEX_FIELDS) : "",
    presented_documentation: presentedDocumentation.trim(),
    family_history: [famHasIssues ? familyHistory.trim() : "", isOrl ? flatAn(orlFam, ORL_FAM_FIELDS) : "", isDerma ? flatAn(dermFam, DERMA_FAM_FIELDS) : ""].filter(Boolean).join(" · "),
    personal_conditions: [hasConditions ? personalConditions.trim() : "", isOrl ? flatAn(orlPers, ORL_PERS_FIELDS) : "", isDerma ? flatAn(dermPers, DERMA_PERS_FIELDS) : ""].filter(Boolean).join(" · "),
    current_medications: [hasMeds ? currentMedications.trim() : "", isDerma ? flatAn(dermProd, DERMA_PROD_FIELDS) : ""].filter(Boolean).join(" · "),
    previous_surgeries: [hasSurgeries ? previousSurgeries.trim() : "", isGyn && gynExtra.ricoveri?.trim() ? `Ricoveri: ${gynExtra.ricoveri.trim()}` : ""].filter(Boolean).join(" · "),
    allergies: hasAllergies ? allergies.trim() : "",
    pathology_history: [pathologyHistory.trim(),
      isOrl && Object.values(orlHist).some((v) => (v || "").trim()) ? `Anamnesi ORL: ${flatAn(orlHist, ORL_HISTORY_FIELDS)}` : "",
      isDerma && Object.values(dermHist).some((v) => (v || "").trim()) ? `Anamnesi dermatologica: ${flatAn(dermHist, DERMA_HISTORY_FIELDS)}` : "",
      isDerma && Object.values(dermSun).some((v) => (v || "").trim()) ? `Esposizione solare: ${flatAn(dermSun, DERMA_SUN_FIELDS)}` : "",
      isDerma && Object.values(dermTric).some((v) => (v || "").trim()) ? `Anamnesi tricologica: ${flatAn(dermTric, DERMA_TRIC_FIELDS)}` : ""].filter(Boolean).join("\n"),
    smoking: smokes ? smoking.trim() : "",
    diuresis: diuresis.trim(),
    bowel: bowel.trim(),
    other_habits: [otherHabits.trim(), isGyn ? flatAn(gynExtra, [["alcol", "Alcol"], ["attivita", "Attività fisica"], ["alimentazione", "Alimentazione"]]) : "", isOrl ? flatAn(orlExpo, ORL_EXPO_FIELDS) : ""].filter(Boolean).join(" · "),
    ai_polish_anamnesis: aiPolishAnamnesis,
    report_type: isNutritionist ? "valutazione_nutrizionale" : "referto",
    weight_kg: weightKg ? parseFloat(weightKg) : null,
    height_cm: heightCm ? parseFloat(heightCm) : null,
    waist_cm: waistCm ? parseFloat(waistCm) : null,
    hip_cm: hipCm ? parseFloat(hipCm) : null,
    body_fat_pct: bodyFatPct ? parseFloat(bodyFatPct) : null,
    target_weight_kg: targetWeightKg ? parseFloat(targetWeightKg) : null,
    diet_plan: dietPlan.trim(),
    nutritional_goals: nutritionalGoals.trim(),
    clinical_history_summary: clinicalHistoryText.trim(),
    visit_reason: isNutritionist
      ? ((VISIT_MOTIVES.find(m => m.id === visitMotive)?.label || "Valutazione nutrizionale").trim())
      : (hasSpecReason
          ? [orlReason, visitReason.trim()].filter(Boolean).join(" — ") + (visitType !== "prima" ? ` (${VISIT_TYPES.find((t) => t.id === visitType)?.label || ""})` : "")
          : visitReason.trim()),
    visit_type: isDoctor ? visitType : "",
    observations: isNutritionist
      ? (nutritionalStateEvaluation.trim() || "Valutazione nutrizionale — vedere dati antropometrici allegati.")
      : joinTxt(gynEO, isDerma ? bodyMapSitesText(bodyMarkers) : "", observations),
    instrumental_exams: joinTxt(serializeGynGroup(gynVisitModule, ["strum"], gynVisit), isDoctor ? serializeInstrumental(instrStruct, instrTypes) : "", isOrl ? serializeAudiogram(audiogram) : "", instrumentalExams),
    audiogram: isOrl && audiogramHasData(audiogram) ? audiogram : null,
    audiogram_prev: isOrl && audiogramHasData(audiogram) && audiogramCompare && prevAudiogram ? { date: prevAudiogram.created_at, audiogram: prevAudiogram.audiogram } : null,
    diagnosis: joinTxt(serializeGynGroup(gynVisitModule, ["diag"], gynVisit), diagnosis),
    recommendations: joinTxt(gynTerInTherapy ? "" : serializeGynGroup(gynVisitModule, ["ter"], gynVisit), recommendations),
    recommended_exams: joinTxt(serializeGynGroup(gynVisitModule, ["esami"], gynVisit), recommendedExams),
    therapy: joinTxt(gynTerInTherapy ? serializeGynGroup(gynVisitModule, ["ter"], gynVisit) : "", therapy),
    next_check: joinTxt(serializeGynGroup(gynVisitModule, ["fu"], gynVisit).replace(/^Controllo: /, ""), nextCheck).replace(/\n/g, " · "),
    visit_struct: { gyn: gynVisit, gyn_module: gynVisitModule, instr: instrStruct, audiogram: isOrl ? audiogram : null,
                    body_map: isDerma ? bodyMarkers : null, photos: isDerma ? dermaPhotos.map((p) => p.id) : null },
    derma_photos: isDerma ? dermaPhotos.map(({ id, kind, site, note, compare_with, body_marker }) => ({ id, kind, site: site || "", note: note || "", compare_with: compare_with || null, body_marker: body_marker || null })) : null,
    body_map_svg: isDerma && bodyMarkers.length ? bodyMapToSvg(bodyMarkers) : "",
    body_map_sites: isDerma ? bodyMapSitesText(bodyMarkers) : "",
    use_ai: useAi,
    signature_b64: (useProfileSig && !signature) ? null : (signature || null),
    send_prescription: !!(sendPrescription && selectedMmg && prescriptionText.trim()),
    mmg_id: selectedMmg?.id || null,
    prescription_items: prescriptionText.trim() || "",
    send_exams_to_mmg: !!(selectedMmg && recommendedExams.trim()),
    send_therapy_to_mmg: !!(selectedMmg && therapy.trim()),
    send_specialist_referral: !!(sendReferral && selectedRef && referralReason.trim()),
    referral_specialist_id: selectedRef?.id || null,
    referral_reason: referralReason.trim() || "",
    // Gli allegati vengono usati SOLO come sorgente per l'OCR AI che compila la
    // "Documentazione clinica presentata" — non vengono acclusi al referto PDF.
    attachments: [],
    // Sede da cui viene emesso il referto (solo se il medico ha più sedi)
    location_index: hasMultipleLocations ? locationIndex : null,
    // Campi specifici nutrizionista: motivo + risposte adattive
    visit_motive: isNutritionist ? (visitMotive || "") : "",
    visit_motive_label: isNutritionist
      ? (VISIT_MOTIVES.find(m => m.id === visitMotive)?.label || "")
      : (moduleDef?.label || ""),
    motive_answers: isNutritionist && visitMotive
      ? (VISIT_MOTIVES.find(m => m.id === visitMotive)?.questions || [])
          .map(q => ({ label: q.label, answer: (motiveAnswers[q.id] || "").trim() }))
          .filter(qa => qa.answer)
      : (isDoctor && moduleDef
          ? moduleDef.questions
              .map((q) => ({ label: q.label, answer: (moduleAnswers[q.id] || "").trim() }))
              .filter((qa) => qa.answer)
          : []),
    // Lettera al MMG per esami di completamento (nutrizionista)
    mmg_completion_exams: isNutritionist ? (mmgCompletionExams || "").trim() : "",
    mmg_completion_letter: isNutritionist ? (mmgCompletionLetter || "").trim() : "",
    send_mmg_completion_letter: !!(isNutritionist && selectedMmg
      && (mmgCompletionLetter || "").trim() && sendMmgCompletionLetter),
    // Piano nutrizionale AI (solo nutrizionista)
    nutrition_plan_target_kcal: isNutritionist && nutritionPlanData?.target_kcal
      ? Number(nutritionPlanData.target_kcal) : null,
    nutrition_plan_macros_summary: isNutritionist ? (nutritionPlanData?.macros_summary || "") : "",
    nutrition_plan_weekly: isNutritionist ? (nutritionWeekly || "").trim() : "",
    nutrition_plan_recommended_foods: isNutritionist ? (nutritionRecommended || "").trim() : "",
    nutrition_plan_foods_to_avoid: isNutritionist ? (nutritionAvoid || "").trim() : "",
    nutrition_plan_phases: isNutritionist ? (nutritionPhases || "").trim() : "",
    nutrition_plan_notes: isNutritionist ? (nutritionPlanNotes || "").trim() : "",
    nutrition_plan_follow_up: isNutritionist ? (nutritionFollowUp || "").trim() : "",
    nutrition_plan_shopping_list: isNutritionist ? (nutritionShoppingList || "").trim() : "",
    nutrition_plan_diet_type: isNutritionist ? (nutritionDietType || "").trim() : "",
    nutrition_plan_diet_type_rationale: isNutritionist ? (nutritionDietTypeRationale || "").trim() : "",
    // Percentuali del piatto salutare (dinamiche per tipo di dieta)
    nutrition_plan_plate_veg_pct: isNutritionist ? Number(platePctVeg) || null : null,
    nutrition_plan_plate_protein_pct: isNutritionist ? Number(platePctProtein) || null : null,
    nutrition_plan_plate_carbs_pct: isNutritionist ? Number(platePctCarbs) || null : null,
    // Conferma finale del nutrizionista: salva referto in cartella paziente
    evaluation_complete: isNutritionist ? !!evaluationComplete : true,
    // Visita di controllo (follow-up)
    is_followup_visit: isNutritionist && !!isFollowupVisit,
    include_first_visit_summary: isNutritionist && !!isFollowupVisit && !!includeFirstVisitSummary,
    followup_first_visit_at: (isFollowupVisit && followupCtx?.first_visit_at) || null,
    followup_visit_number: (isFollowupVisit && followupCtx?.visit_number) || null,
    followup_first_weight_kg: (isFollowupVisit && followupCtx?.first_weight_kg) || null,
    followup_last_weight_kg: (isFollowupVisit && followupCtx?.last_weight_kg) || null,
    followup_first_bmi: (isFollowupVisit && followupCtx?.first_bmi) || null,
    followup_target_weight_kg: (isFollowupVisit && followupCtx?.target_weight_kg) || null,
    followup_delta_days: (isFollowupVisit && followupCtx?.delta_days) || null,
    followup_first_diet_type: (isFollowupVisit && followupCtx?.first_diet_type) || "",
    followup_first_visit_summary: (isFollowupVisit && followupCtx?.first_visit_summary) || "",
    followup_ai_evaluation: (isFollowupVisit && followupCtx?.ai_evaluation) || "",
    followup_weight_series: (isFollowupVisit && followupCtx?.weight_series) || [],
    followup_previous_visits: (isFollowupVisit && followupCtx?.previous_visits) || [],
    // Bioimpedenziometria (BIA) — solo nutrizionista
    bia_lean_mass_kg: isNutritionist && biaLeanMassKg ? parseFloat(biaLeanMassKg) : null,
    bia_lean_mass_pct: isNutritionist && biaLeanMassPct ? parseFloat(biaLeanMassPct) : null,
    bia_fat_mass_kg: isNutritionist && biaFatMassKg ? parseFloat(biaFatMassKg) : null,
    bia_fat_mass_pct: isNutritionist && biaFatMassPct ? parseFloat(biaFatMassPct) : null,
    bia_tbw_pct: isNutritionist && biaTbwPct ? parseFloat(biaTbwPct) : null,
    bia_phase_angle: isNutritionist && biaPhaseAngle ? parseFloat(biaPhaseAngle) : null,
    bia_muscle_mass_kg: isNutritionist && biaMuscleMassKg ? parseFloat(biaMuscleMassKg) : null,
    // Valutazione stato nutrizionale (sostituisce esame obiettivo per il biologo)
    nutritional_state_evaluation: isNutritionist ? (nutritionalStateEvaluation || "").trim() : "",
    // Anamnesi alimentare + stile di vita (nutrizionista): checkbox multipli + note libere
    food_habits: isNutritionist ? foodHabits : [],
    food_habits_notes: isNutritionist ? (foodHabitsNotes || "").trim() : "",
    lifestyle_habits: isNutritionist ? lifestyleHabits : [],
    lifestyle_habits_notes: isNutritionist ? (lifestyleHabitsNotes || "").trim() : "",
    // Guidance clinica per l'AI (passata al prompt del piano dietetico)
    nutrition_diet_guidance: isNutritionist ? (nutritionDietGuidance || "").trim() : "",
    // Messaggio motivazionale al paziente (razionale della dieta con obiettivo finale)
    nutrition_patient_message: isNutritionist ? (nutritionPatientMessage || "").trim() : "",
    // Lettera cartacea al MMG (quando il MMG non è su Synappsi) — pagina PDF aggiuntiva
    mmg_hand_letter: isNutritionist ? (mmgHandLetter || "").trim() : "",
    want_mmg_hand_letter: !!(isNutritionist && wantHandLetter && (mmgHandLetter || "").trim()),
  });

  const saveDraft = async () => {
    const data = buildFormPayload();
    // Rimuovi allegati (troppo pesanti per bozza — l'utente li riaggiunge dopo)
    delete data.attachments;
    const label = `${plast.trim()} ${pfirst.trim()}`.trim() || "Bozza senza nome";
    try {
      if (currentDraftId) {
        await api.put(`/referto/drafts/${currentDraftId}`, { data, label });
        toast.success("Bozza aggiornata");
      } else {
        const { data: res } = await api.post("/referto/drafts", { data, label });
        setCurrentDraftId(res.id);
        toast.success("Bozza salvata — la segretaria può continuare");
      }
      loadDrafts();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore salvataggio bozza");
    }
  };

  const loadDraft = async (id) => {
    try {
      const { data } = await api.get(`/referto/drafts/${id}`);
      const d = data.data || {};
      const has = (v) => !!(v && (typeof v === "string" ? v.trim() : true));
      // --- Dati paziente ---
      setPfirst(d.patient_first_name || "");
      setPlast(d.patient_last_name || "");
      setPsex(d.patient_sex || "");
      setPbirth(d.patient_birth_date || "");
      setPbirthPlace(d.patient_birth_place || "");
      setPbirthProvince(d.patient_birth_province || "");
      setPfiscalCode(d.patient_fiscal_code || "");
      setPfiscalCodeManual(!!d.patient_fiscal_code);
      setPphone(d.patient_phone || "");
      setPemail(d.patient_email || "");
      // --- Screening/documentazione ---
      setPreviousScreenings(d.previous_screenings || "");
      setPresentedDocumentation(d.presented_documentation || "");
      // --- Anamnesi ---
      setFamHasIssues(has(d.family_history)); setFamilyHistory(d.family_history || "");
      setHasConditions(has(d.personal_conditions)); setPersonalConditions(d.personal_conditions || "");
      setHasMeds(has(d.current_medications)); setCurrentMedications(d.current_medications || "");
      setHasSurgeries(has(d.previous_surgeries)); setPreviousSurgeries(d.previous_surgeries || "");
      setHasAllergies(has(d.allergies)); setAllergies(d.allergies || "");
      setPathologyHistory(d.pathology_history || "");
      setSmokes(has(d.smoking)); setSmoking(d.smoking || "");
      setDiuresis(d.diuresis || ""); setBowel(d.bowel || ""); setOtherHabits(d.other_habits || "");
      setAiPolishAnamnesis(d.ai_polish_anamnesis !== false);
      // --- Visita ---
      setVisitReason(d.visit_reason || "");
      setObservations(d.observations || "");
      setInstrumentalExams(d.instrumental_exams || "");
      setDiagnosis(d.diagnosis || ""); setRecommendedExams(d.recommended_exams || "");
      setTherapy(d.therapy || ""); setRecommendations(d.recommendations || "");
      setNextCheck(d.next_check || "");
      if (d.visit_struct) {
        const mod = d.symptom_module || "", g = d.visit_struct.gyn || {};
        setGynVisitAll(mod ? { [mod]: g } : {}); setInstrStruct(d.visit_struct.instr || { types: [], values: {} });
        // i campi testuali della bozza contengono già la serializzazione: evita duplicati
        const strip = (txt, ser) => (ser && txt.startsWith(ser) ? txt.slice(ser.length).trim() : txt);
        const bm = Array.isArray(d.visit_struct.body_map) ? d.visit_struct.body_map : [];
        setBodyMarkers(bm); setShowBodyMap(bm.length > 0);
        setDermaPhotos(Array.isArray(d.derma_photos) ? d.derma_photos.map((p) => ({ ...p, created_at: p.created_at || data.updated_at || data.created_at || null })) : []);
        setObservations(strip(strip(d.observations || "", serializeGynGroup(mod, ["eo"], g)), bodyMapSitesText(bm)));
        setInstrumentalExams(strip(d.instrumental_exams || "", serializeInstrumental(d.visit_struct.instr, instrTypes)));
        setDiagnosis(strip(d.diagnosis || "", serializeGynGroup(mod, ["diag"], g)));
        setRecommendedExams(strip(d.recommended_exams || "", serializeGynGroup(mod, ["esami"], g)));
        setTherapy(strip(d.therapy || "", serializeGynGroup(mod, ["ter"], g)));
        setRecommendations(strip(d.recommendations || "", serializeGynGroup(mod, ["ter"], g)));
        setNextCheck(strip(d.next_check || "", serializeGynGroup(mod, ["fu"], g).replace(/^Controllo: /, "")).replace(/^· /, ""));
      }
      // --- Motivo visita adattivo (nutrizionista) ---
      setVisitMotive(d.visit_motive || "");
      const answersObj = {};
      (d.motive_answers || []).forEach((qa) => {
        // Ricava id domanda dalla label (best-effort)
        const q = (VISIT_MOTIVES.find(m => m.id === (d.visit_motive || ""))?.questions || [])
          .find(qq => qq.label === qa.label);
        if (q) answersObj[q.id] = qa.answer || "";
      });
      setMotiveAnswers(answersObj);
      // --- Antropometrici / gravidanza / BIA ---
      setWeightKg(d.weight_kg != null ? String(d.weight_kg) : "");
      setHeightCm(d.height_cm != null ? String(d.height_cm) : "");
      setWaistCm(d.waist_cm != null ? String(d.waist_cm) : "");
      setHipCm(d.hip_cm != null ? String(d.hip_cm) : "");
      setBellyCm(d.belly_cm != null ? String(d.belly_cm) : "");
      setArmCm(d.arm_cm != null ? String(d.arm_cm) : "");
      setThighCm(d.thigh_cm != null ? String(d.thigh_cm) : "");
      setIsPregnant(!!d.is_pregnant);
      setBiaLeanMassKg(d.bia_lean_mass_kg != null ? String(d.bia_lean_mass_kg) : "");
      setBiaFatMassKg(d.bia_fat_mass_kg != null ? String(d.bia_fat_mass_kg) : "");
      setBiaTbwPct(d.bia_tbw_pct != null ? String(d.bia_tbw_pct) : "");
      setBiaPhaseAngle(d.bia_phase_angle != null ? String(d.bia_phase_angle) : "");
      setBiaMuscleMassKg(d.bia_muscle_mass_kg != null ? String(d.bia_muscle_mass_kg) : "");
      // --- Valutazione stato nutrizionale + obiettivi ---
      setNutritionalStateEvaluation(d.nutritional_state_evaluation || "");
      setNutritionalGoals(d.nutritional_goals || "");
      // --- Anamnesi alimentare + stile di vita (checkbox) ---
      setFoodHabits(Array.isArray(d.food_habits) ? d.food_habits : []);
      setFoodHabitsNotes(d.food_habits_notes || "");
      setLifestyleHabits(Array.isArray(d.lifestyle_habits) ? d.lifestyle_habits : []);
      setLifestyleHabitsNotes(d.lifestyle_habits_notes || "");
      // --- Piano nutrizionale AI ---
      setNutritionActivity(d.nutrition_activity_level || "moderate");
      setNutritionOverrideKcal(d.nutrition_plan_target_kcal ? String(d.nutrition_plan_target_kcal) : "");
      setNutritionNotes(d.nutrition_notes || "");
      setNutritionDietGuidance(d.nutrition_diet_guidance || "");
      setNutritionPatientMessage(d.nutrition_patient_message || "");
      setNutritionAiRationale(d.nutrition_ai_rationale || "");
      setNutritionMultiWeek(!!d.nutrition_multi_week);
      setNutritionWeeksCount(d.nutrition_weeks_count || 2);
      setNutritionWeekly(d.nutrition_plan_weekly || "");
      setNutritionRecommended(d.nutrition_plan_recommended_foods || "");
      setNutritionAvoid(d.nutrition_plan_foods_to_avoid || "");
      setNutritionPhases(d.nutrition_plan_phases || "");
      setNutritionFollowUp(d.nutrition_plan_follow_up || "");
      setNutritionShoppingList(d.nutrition_plan_shopping_list || "");
      setNutritionDietType(d.nutrition_plan_diet_type || "");
      setNutritionDietTypeRationale(d.nutrition_plan_diet_type_rationale || "");
      if (d.nutrition_plan_plate_veg_pct) setPlatePctVeg(Number(d.nutrition_plan_plate_veg_pct));
      if (d.nutrition_plan_plate_protein_pct) setPlatePctProtein(Number(d.nutrition_plan_plate_protein_pct));
      if (d.nutrition_plan_plate_carbs_pct) setPlatePctCarbs(Number(d.nutrition_plan_plate_carbs_pct));
      // --- Lettera al MMG di completamento (nutrizionista) ---
      setMmgCompletionExams(d.mmg_completion_exams || "");
      setMmgCompletionLetter(d.mmg_completion_letter || "");
      setSendMmgCompletionLetter(!!d.send_mmg_completion_letter);
      setMmgHandLetter(d.mmg_hand_letter || "");
      setWantHandLetter(!!d.want_mmg_hand_letter);
      // --- Sede da cui viene emesso il referto ---
      if (d.location_index != null && d.location_index >= 0) setLocationIndex(d.location_index);
      // --- Sezioni collapse: apri quelle con contenuto ---
      setCurrentDraftId(id);
      setShowDrafts(false);
      setOpenFam(has(d.family_history));
      setOpenPers(has(d.personal_conditions) || has(d.current_medications) || has(d.allergies));
      setOpenHabits(has(d.smoking) || has(d.other_habits) || has(d.diuresis));
      setOpenVisit(true);
      // --- Nota: gli allegati NON vengono salvati in bozza (troppo pesanti) ---
      toast.success(`Bozza "${data.label}" caricata${(d.food_habits?.length || 0) > 0 || (d.lifestyle_habits?.length || 0) > 0 ? " (inclusi checkbox anamnesi)" : ""}`);
    } catch (e) {
      toast.error("Errore caricamento bozza");
    }
  };

  const deleteDraft = async (id, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/referto/drafts/${id}`);
      if (id === currentDraftId) setCurrentDraftId(null);
      loadDrafts();
      toast.success("Bozza eliminata");
    } catch { toast.error("Errore"); }
  };

  const generatePreview = async () => {
    if (!canSubmit) {
      toast.error("Compila almeno paziente, motivo visita e rilevazioni.");
      return;
    }
    if (isNutritionist && !evaluationComplete) {
      toast.error("Conferma prima che la valutazione nutrizionale è completa (checkbox in fondo).");
      return;
    }
    setBusy(true); setResult(null); setPreview(null);
    try {
      const payload = { ...buildFormPayload(), preview_only: true };
      const { data } = await api.post("/referto/standalone", payload);
      setPreview(data);
      setPreviewTab("pdf");
      // Auto-cleanup della bozza dopo generazione anteprima riuscita
      if (currentDraftId) {
        try { await api.delete(`/referto/drafts/${currentDraftId}`); } catch {}
        setCurrentDraftId(null);
        loadDrafts();
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore generazione anteprima");
    } finally { setBusy(false); }
  };

  const confirmAndSend = async () => {
    if (!preview?.referto_id) return;
    setConfirming(true);
    try {
      const { data } = await api.post("/referto/standalone/confirm", {
        referto_id: preview.referto_id,
      });
      setResult(data);
      setPreview(null);
      const parts = [];
      if (data.prescription_thread_id) parts.push(`prescrizione a ${data.prescription_mmg_name}`);
      if (data.referral_thread_id) parts.push(`consulto a ${data.referral_specialist_name}`);
      if (parts.length) {
        toast.success(`Referto PDF a ${data.sent_to} · ${parts.join(" + ")}`);
      } else {
        toast.success(`Referto PDF inviato a ${data.sent_to}`);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore invio referto");
    } finally { setConfirming(false); }
  };

  const discardPreview = async () => {
    if (!preview?.referto_id) { setPreview(null); return; }
    const rid = preview.referto_id;
    setPreview(null);
    try { await api.delete(`/referto/standalone/preview/${rid}`); } catch {}
  };

  const MAX_TOTAL_MB = 15;
  // ---- Modulo di accoglienza (stampa PDF compilabile / importa con AI) ----
  const [intakeBusy, setIntakeBusy] = useState(false);
  const [intakeInfo, setIntakeInfo] = useState(null);
  const openIntakeForm = () => {
    const q = new URLSearchParams({ first_name: pfirst, last_name: plast, fiscal_code: pfiscalCode }).toString();
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/referto/intake-form?${q}`, "_blank", "noopener");
  };
  const applyIntake = (f, narrative) => {
    const set = (v, fn) => { if (v) fn(v); };
    set(f.first_name, setPfirst); set(f.last_name, setPlast); set(f.phone, setPphone); set(f.email, setPemail);
    set(f.birth_place, setPbirthPlace); if (f.birth_province) setPbirthProvince(f.birth_province.trim().toUpperCase().slice(0, 2));
    const cf = (f.fiscal_code || "").toUpperCase().replace(/\s/g, "");
    if (/^[A-Z]{6}\d{2}[A-Z]\d{2}[A-Z]\d{3}[A-Z]$/.test(cf)) {
      // ricava data di nascita e sesso dal codice fiscale se mancanti nel modulo
      const months = "ABCDEHLMPRST"; const yy = +cf.slice(6, 8); const mm = months.indexOf(cf[8]) + 1; let dd = +cf.slice(9, 11);
      const female = dd > 40; if (female) dd -= 40;
      const yyyy = yy + (yy > new Date().getFullYear() % 100 ? 1900 : 2000);
      if (!f.birth_date && mm > 0) f = { ...f, birth_date: `${yyyy}-${String(mm).padStart(2, "0")}-${String(dd).padStart(2, "0")}` };
      if (!f.sex) f = { ...f, sex: female ? "F" : "M" };
    }
    if (f.fiscal_code) { setPfiscalCode(f.fiscal_code.toUpperCase()); setPfiscalCodeManual(true); }
    if (f.sex) setPsex(f.sex.toUpperCase().startsWith("F") ? "F" : "M");
    if (f.birth_date) { const m = f.birth_date.match(/(\d{1,2})[\/.-](\d{1,2})[\/.-](\d{4})/); if (m) setPbirth(`${m[3]}-${m[2].padStart(2, "0")}-${m[1].padStart(2, "0")}`); else if (/^\d{4}-\d{2}-\d{2}$/.test(f.birth_date)) setPbirth(f.birth_date); }
    if (f.family_history) { setFamHasIssues(true); setFamilyHistory(f.family_history); setOpenFam(true); }
    if (f.personal_conditions) { setHasConditions(true); setPersonalConditions(f.personal_conditions); setOpenPers(true); }
    if (f.previous_surgeries) { setHasSurgeries(true); setPreviousSurgeries(f.previous_surgeries); }
    if (f.allergies) { setHasAllergies(true); setAllergies(f.allergies); }
    if (f.current_medications) { setHasMeds(true); setCurrentMedications(f.current_medications); }
    if (f.smoking) { const no = /^(no|non|nessun)/i.test(f.smoking.trim()); setSmokes(!no); setSmoking(no ? "" : f.smoking); setOpenHabits(true); }
    set(f.diuresis, setDiuresis); set(f.bowel, setBowel); set(f.other_habits, setOtherHabits);
    const pick = (prefix) => Object.fromEntries(Object.entries(f).filter(([k]) => k.startsWith(prefix)).map(([k, v]) => [k.slice(prefix.length), v]));
    const g = pick("gyn_"); if (Object.keys(g).length) { setGyn((s) => ({ ...s, ...g })); setOpenGyn(true); }
    const o = pick("obst_"); if (Object.keys(o).length) { setObst((s) => ({ ...s, ...o })); setOpenObst(true); }
    const sx = pick("sex_"); if (Object.keys(sx).length) { setSexual((s) => ({ ...s, ...sx })); setOpenSexual(true); }
    const sc = pick("screen_"); if (Object.keys(sc).length) { setScreen((s) => ({ ...s, ...sc })); setOpenScreen(true); }
    const ex = {}; ["alcohol", "activity", "diet", "hospitalizations"].forEach((k) => { if (f[k]) ex[{ alcohol: "alcol", activity: "attivita", diet: "alimentazione", hospitalizations: "ricoveri" }[k]] = f[k]; });
    if (Object.keys(ex).length) setGynExtra((s) => ({ ...s, ...ex }));
    const of = pick("orlfam_"); if (Object.keys(of).length) { setOrlFam((s) => ({ ...s, ...of })); setOpenFam(true); }
    const op = pick("orlpers_"); if (Object.keys(op).length) { setOrlPers((s) => ({ ...s, ...op })); setOpenPers(true); }
    if (f.hospitalizations && isOrl) setOrlPers((s) => ({ ...s, ricoveri: f.hospitalizations }));
    const oh = Object.fromEntries(Object.entries(f).filter(([k]) => /^orl_/.test(k)).map(([k, v]) => [k.slice(4), v]));
    if (Object.keys(oh).length) { setOrlHist((s) => ({ ...s, ...oh })); setOpenOrl(true); }
    const oe = pick("orlexpo_"); if (Object.keys(oe).length) { setOrlExpo((s) => ({ ...s, ...oe })); setOpenHabits(true); }
    if (f.alcohol && isOrl) setOrlExpo((s) => ({ ...s, alcol: f.alcohol }));
    const ox = pick("orlexam_"); if (Object.keys(ox).length) { setOrlExams((s) => ({ ...s, ...ox })); setOpenExams(true); }
    const df = pick("dermfam_"); if (Object.keys(df).length) { setDermFam((s) => ({ ...s, ...df })); setOpenFam(true); }
    const dp = pick("dermpers_"); if (Object.keys(dp).length) { setDermPers((s) => ({ ...s, ...dp })); setOpenPers(true); }
    if (f.hospitalizations && isDerma) setDermPers((s) => ({ ...s, ricoveri: f.hospitalizations }));
    const dh = pick("derm_"); if (Object.keys(dh).length) { setDermHist((s) => ({ ...s, ...dh })); setOpenDerm(true); }
    const ds = pick("dermsun_"); if (Object.keys(ds).length) { setDermSun((s) => ({ ...s, ...ds })); setOpenSun(true); }
    const dpr = pick("dermprod_"); if (Object.keys(dpr).length) { setDermProd((s) => ({ ...s, ...dpr })); setOpenProd(true); }
    const dt = pick("dermtric_"); if (Object.keys(dt).length) { setDermTric((s) => ({ ...s, ...dt })); setOpenTric(true); }
    if (isDerma && (f.alcohol || f.activity || f.diet)) setOtherHabits((prev) => [prev, f.alcohol && `Alcol: ${f.alcohol}`, f.activity && `Attività fisica: ${f.activity}`, f.diet && `Alimentazione: ${f.diet}`].filter(Boolean).join(" · "));
    if (narrative) setPathologyHistory((prev) => [prev.trim(), `Sintesi anamnestica (da modulo paziente): ${narrative}`].filter(Boolean).join("\n\n"));
  };
  const onIntakeFiles = async (evt) => {
    const files = Array.from(evt.target.files || []);
    evt.target.value = "";
    if (!files.length) return;
    setIntakeBusy(true);
    try {
      const attachments = [];
      for (const file of files) attachments.push({ filename: file.name, mime_type: file.type || "application/octet-stream", data_b64: await readFileAsBase64(file) });
      const { data } = await api.post("/referto/intake-parse", { attachments });
      setIntakePreview(data); setIntakePreviewOpen(true);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Lettura del modulo non riuscita"); }
    finally { setIntakeBusy(false); }
  };
  const [intakePreview, setIntakePreview] = useState(null);
  const [intakePreviewOpen, setIntakePreviewOpen] = useState(false);
  const [intakeReturns, setIntakeReturns] = useState([]);
  const [intakeSending, setIntakeSending] = useState(false);
  const [intakeVisitDate, setIntakeVisitDate] = useState("");
  const confirmIntake = (fields, narrative) => {
    applyIntake(fields, narrative);
    setIntakeInfo({ n: Object.keys(fields).length, mode: intakePreview?.read_mode });
    toast.success(`${Object.keys(fields).length} campi inseriti nella visita`);
  };
  const sendIntakeEmail = async () => {
    if (!pemail.trim().includes("@")) { toast.error("Inserisci prima l'email della paziente nei dati anagrafici"); return; }
    setIntakeSending(true);
    try {
      await api.post("/referto/intake-send", { to_email: pemail.trim(), first_name: pfirst, last_name: plast, fiscal_code: pfiscalCode, visit_date: intakeVisitDate });
      toast.success(`Modulo inviato a ${pemail.trim()} con link per la riconsegna${intakeVisitDate ? " · promemoria attivo 2 giorni prima della visita" : ""}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Invio non riuscito"); }
    finally { setIntakeSending(false); }
  };
  useEffect(() => {
    if (!isDoctor) return;
    const cf = pfiscalCode.trim().toUpperCase();
    if (cf.length !== 16) { setIntakeReturns([]); return; }
    api.get("/referto/intake-returns", { params: { fiscal_code: cf } }).then((r) => setIntakeReturns(r.data.items || [])).catch(() => setIntakeReturns([]));
  }, [pfiscalCode, isDoctor]);
  useEffect(() => {
    const id = new URLSearchParams(window.location.search).get("intake");
    if (id && isDoctor) importReturned(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDoctor]);
  const importReturned = async (id) => {
    setIntakeBusy(true);
    try {
      const { data } = await api.post(`/referto/intake-returns/${id}/parse`);
      setIntakePreview(data); setIntakePreviewOpen(true);
      setIntakeReturns((l) => l.filter((x) => x.id !== id));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Lettura non riuscita"); }
    finally { setIntakeBusy(false); }
  };
  const readFileAsBase64 = (file) => new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(",", 2)[1] || "");
    r.onerror = reject;
    r.readAsDataURL(file);
  });

  const onAttachFiles = async (evt) => {
    const files = Array.from(evt.target.files || []);
    evt.target.value = "";
    if (!files.length) return;
    setAttachBusy(true);
    try {
      const current = attachments.reduce((s, a) => s + a.size, 0);
      let running = current;
      const added = [];
      for (const f of files) {
        const okMime = f.type.startsWith("image/") || f.type === "application/pdf";
        if (!okMime) { toast.error(`"${f.name}" non supportato — solo immagini o PDF`); continue; }
        if (running + f.size > MAX_TOTAL_MB * 1024 * 1024) {
          toast.error(`Superato il limite di ${MAX_TOTAL_MB} MB totali`); break;
        }
        const b64 = await readFileAsBase64(f);
        added.push({ filename: f.name, mime_type: f.type, size: f.size, data_b64: b64 });
        running += f.size;
      }
      if (added.length) {
        setAttachments((prev) => [...prev, ...added]);
        toast.success(`${added.length} allegat${added.length === 1 ? "o aggiunto" : "i aggiunti"}`);
      }
    } catch (e) {
      toast.error("Errore lettura file allegati");
    } finally { setAttachBusy(false); }
  };

  const removeAttachment = (idx) =>
    setAttachments((prev) => prev.filter((_, i) => i !== idx));

  // -------- OCR AI: legge i referti allegati e popola "Documentazione clinica presentata" --------
  const [ocrBusy, setOcrBusy] = useState(false);
  const scanAttachedExamsWithAI = async () => {
    if (!attachments.length) {
      toast.error("Allega almeno un referto (PDF o foto) prima di scansionare.");
      return;
    }
    setOcrBusy(true);
    toast.info("L'AI sta leggendo i referti allegati…");
    try {
      const { data } = await api.post("/ai/extract-lab-values", {
        attachments: attachments.map((a) => ({
          filename: a.filename, mime_type: a.mime_type, data_b64: a.data_b64,
        })),
        existing_text: (presentedDocumentation || "").trim(),
      });
      if (data?.text) {
        setPresentedDocumentation(data.text);
        toast.success(`Scansionati ${data.files_analyzed || attachments.length} allegat${(data.files_analyzed||attachments.length) === 1 ? "o" : "i"} — valori inseriti in Documentazione clinica.`);
      } else {
        toast.error("L'AI non ha restituito alcun valore leggibile.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nell'analisi AI dei referti.");
    } finally { setOcrBusy(false); }
  };

  // -------- On-demand AI previews --------
  // Dati antropometrici (biologo nutrizionista)
  const [weightKg, setWeightKg] = useState("");
  const [heightCm, setHeightCm] = useState("");
  const [waistCm, setWaistCm] = useState("");
  const [hipCm, setHipCm] = useState("");
  const [bodyFatPct, setBodyFatPct] = useState("");
  const [targetWeightKg, setTargetWeightKg] = useState("");
  const [dietPlan, setDietPlan] = useState("");
  const [nutritionalGoals, setNutritionalGoals] = useState("");
  const bmi = useMemo(() => {
    const w = parseFloat(weightKg), h = parseFloat(heightCm) / 100;
    if (!w || !h || h <= 0) return "";
    return (w / (h * h)).toFixed(1);
  }, [weightKg, heightCm]);

  const [recovering, setRecovering] = useState(false);
  // Storia clinica AI dai referti precedenti
  const [clinicalHistoryText, setClinicalHistoryText] = useState("");
  const [clinicalHistoryEditor, setClinicalHistoryEditor] = useState("");
  const [clinicalHistoryModal, setClinicalHistoryModal] = useState(false);
  const [clinicalHistoryBusy, setClinicalHistoryBusy] = useState(false);
  const [clinicalHistoryCount, setClinicalHistoryCount] = useState(0);

  // -------- VISITA DI CONTROLLO (follow-up) --------
  // Rileva visite precedenti quando l'utente inserisce CF o email
  const checkFollowupAvailable = async () => {
    if (!isNutritionist) return;
    if (!pfiscalCode.trim() && !pemail.trim()) { setFollowupAvailable(false); return; }
    try {
      const params = {};
      if (pfiscalCode.trim()) params.patient_fiscal_code = pfiscalCode.trim().toUpperCase();
      else params.patient_email = pemail.trim();
      const { data } = await api.get("/referto/patient-history", { params });
      setFollowupAvailable((data.items || []).length > 0);
    } catch { setFollowupAvailable(false); }
  };

  // Carica il contesto follow-up (peso + AI eval) — chiamato quando l'utente attiva la modalità
  const loadFollowupContext = async () => {
    if (!pfiscalCode.trim() && !pemail.trim()) {
      toast.error("Serve email o codice fiscale del paziente.");
      return;
    }
    setFollowupBusy(true);
    try {
      const body = {};
      if (pfiscalCode.trim()) body.patient_fiscal_code = pfiscalCode.trim().toUpperCase();
      else body.patient_email = pemail.trim();
      if (weightKg) body.current_weight_kg = Number(weightKg);
      if (targetWeightKg) body.target_weight_kg = Number(targetWeightKg);
      const { data } = await api.post("/referto/followup-context", body);
      if (!data.is_followup) {
        toast.info(data.message || "Nessuna visita precedente trovata.");
        setFollowupCtx(null);
        setIsFollowupVisit(false);
        setFollowupAvailable(false);
        return;
      }
      setFollowupCtx(data);
      setIsFollowupVisit(true);
      setFollowupAvailable(true);
      toast.success(`Visita di controllo N.${data.visit_number} — ${data.count_previous_visits} visita/e precedente/i trovate`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nel caricare il contesto della visita di controllo");
    } finally { setFollowupBusy(false); }
  };
  // Grafico peso (per nutrizionisti)
  const [showWeightChart, setShowWeightChart] = useState(false);
  const [weightHistory, setWeightHistory] = useState([]);
  const [weightHistoryBusy, setWeightHistoryBusy] = useState(false);

  // Lettera al MMG per esami di completamento (nutrizionista)
  const [mmgCompletionExams, setMmgCompletionExams] = useState("");
  const [mmgCompletionLetter, setMmgCompletionLetter] = useState("");
  const [mmgCompletionBusy, setMmgCompletionBusy] = useState(false);
  const [sendMmgCompletionLetter, setSendMmgCompletionLetter] = useState(false);

  const buildClinicalHistory = async () => {
    if (!pemail.trim() && !pfiscalCode.trim()) {
      toast.error("Inserisci email o codice fiscale del paziente.");
      return;
    }
    setClinicalHistoryBusy(true);
    try {
      const payload = {};
      if (pfiscalCode.trim()) payload.patient_fiscal_code = pfiscalCode.trim().toUpperCase();
      else payload.patient_email = pemail.trim();
      const { data } = await api.post("/referto/clinical-history-ai", payload);
      setClinicalHistoryText(data.summary || "");
      setClinicalHistoryEditor(data.summary || "");
      setClinicalHistoryCount(data.count || 0);
      setClinicalHistoryModal(true);
      toast.success(`Storia clinica generata dai ${data.count} referti precedenti`);
    } catch (e) {
      if (e.response?.status === 404) {
        toast.info("Nessun referto precedente trovato per questo paziente.");
      } else {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore generazione storia clinica");
      }
    } finally { setClinicalHistoryBusy(false); }
  };

  const loadWeightHistory = async () => {
    if (!pemail.trim() && !pfiscalCode.trim()) {
      toast.error("Inserisci email o codice fiscale del paziente.");
      return;
    }
    setWeightHistoryBusy(true);
    try {
      const params = {};
      if (pfiscalCode.trim()) params.patient_fiscal_code = pfiscalCode.trim().toUpperCase();
      else params.patient_email = pemail.trim();
      const { data } = await api.get("/referto/patient-history", { params });
      const items = (data.items || [])
        .filter(r => r.weight_kg != null && r.weight_kg !== "")
        .map(r => ({
          date: r.created_at,
          dateLabel: new Date(r.created_at).toLocaleDateString("it-IT", {day:"2-digit", month:"short", year:"2-digit"}),
          weight: parseFloat(r.weight_kg),
          target: r.target_weight_kg != null ? parseFloat(r.target_weight_kg) : null,
        }));
      setWeightHistory(items);
      // Se ci sono referti precedenti con peso, popola anche previousPlanInfo (auto-diff)
      if (items.length > 0) {
        const last = items[items.length - 1];
        const rawLast = (data.items || []).slice(-1)[0] || {};
        setPreviousPlanInfo({
          lastWeight: last.weight,
          lastTargetWeight: last.target,
          lastTargetKcal: rawLast.nutrition_plan_target_kcal || null,
          lastDate: last.date,
          lastDateLabel: last.dateLabel,
        });
      } else {
        setPreviousPlanInfo(null);
      }
      if (items.length === 0) {
        toast.info("Nessun peso registrato nei referti precedenti.");
      }
    } catch (e) {
      if (e.response?.status !== 404) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore recupero grafico peso");
      }
      setWeightHistory([]);
    } finally { setWeightHistoryBusy(false); }
  };

  // Curva teorica del piano (peso atteso in ogni data, calcolata linearmente
  // dalla prima visita con target impostato, assumendo 0,5 kg/settimana come rate)
  const planCurve = useMemo(() => {
    if (!weightHistory || weightHistory.length < 1) return null;
    const startIdx = weightHistory.findIndex((w) => w.target != null);
    if (startIdx === -1) return null;
    const start = weightHistory[startIdx];
    const t0 = new Date(start.date).getTime();
    const w0 = Number(start.weight);
    const wt = Number(start.target);
    if (Number.isNaN(w0) || Number.isNaN(wt) || wt === w0) return null;
    const kgDelta = Math.abs(wt - w0);
    // Durata attesa piano: 0,5 kg/settimana (14 giorni per kg), min 60 giorni
    const durationDays = Math.max(60, Math.round(kgDelta * 14));
    const tEnd = t0 + durationDays * 86400000;
    // Aggiunge peso atteso ad ogni misurazione reale
    const data = weightHistory.slice(startIdx).map((w) => {
      const t = new Date(w.date).getTime();
      const frac = Math.min(1, Math.max(0, (t - t0) / (tEnd - t0)));
      return { ...w, expected: +(w0 + (wt - w0) * frac).toFixed(1) };
    });
    // Aggiunge un punto "oggi" con solo la curva teorica (peso reale ancora ignoto)
    const now = Date.now();
    const lastT = new Date(weightHistory[weightHistory.length - 1].date).getTime();
    let expectedToday = null;
    if (now - lastT > 86400000) {
      const frac = Math.min(1, Math.max(0, (now - t0) / (tEnd - t0)));
      expectedToday = +(w0 + (wt - w0) * frac).toFixed(1);
      data.push({
        date: new Date(now).toISOString(),
        dateLabel: "Oggi",
        weight: null,
        target: wt,
        expected: expectedToday,
      });
    } else {
      expectedToday = data[data.length - 1]?.expected ?? null;
    }
    // Punto finale (fine piano teorica) — solo se non superata
    if (Date.now() < tEnd) {
      data.push({
        date: new Date(tEnd).toISOString(),
        dateLabel: new Date(tEnd).toLocaleDateString("it-IT", {day: "2-digit", month: "short", year: "2-digit"}),
        weight: null,
        target: wt,
        expected: wt,
      });
    }
    const daysElapsed = Math.max(0, Math.round((now - t0) / 86400000));
    const daysTotal = durationDays;
    const lastRealWeight = weightHistory[weightHistory.length - 1]?.weight ?? null;
    // Adherence: "in linea" se |delta| < 0,5, "leggero ritardo" se 0,5-1,5, "in ritardo" se >1,5
    // Direzione: se target < w0 (dimagrimento) → delta = real - expected (positivo = ritardo)
    //           se target > w0 (aumento) → delta = expected - real (positivo = ritardo)
    let adherence = "unknown";
    let deltaKg = null;
    if (lastRealWeight != null && expectedToday != null) {
      const goalDim = wt < w0;
      deltaKg = goalDim ? (lastRealWeight - expectedToday) : (expectedToday - lastRealWeight);
      adherence =
        Math.abs(deltaKg) < 0.5 ? "on_track" :
        deltaKg < 1.5 ? "slight_delay" :
        "behind";
    }
    return { data, t0, tEnd, w0, wt, durationDays, daysElapsed, daysTotal, expectedToday, adherence, deltaKg };
  }, [weightHistory]);

  const acceptClinicalHistory = () => {
    setClinicalHistoryText(clinicalHistoryEditor.trim());
    setClinicalHistoryModal(false);
    toast.success("Storia clinica inserita nel referto");
  };

  const removeClinicalHistory = () => {
    setClinicalHistoryText("");
    setClinicalHistoryEditor("");
  };

  // Auto-carica il piano precedente del paziente (per calcolare scostamento peso vs target)
  // Trigger: nutrizionista + email o CF impostati → chiama loadWeightHistory una sola volta.
  useEffect(() => {
    if (!isNutritionist) return;
    const hasIdentifier = (pemail || "").trim().length >= 5 || (pfiscalCode || "").trim().length >= 5;
    if (!hasIdentifier) {
      setPreviousPlanInfo(null);
      return;
    }
    // Debounce: aspetta 700ms dopo l'ultima modifica
    const t = setTimeout(() => {
      loadWeightHistory();
    }, 700);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pemail, pfiscalCode, isNutritionist]);

  const recoverPreviousAnamnesis = async () => {
    if (!pemail.trim() && !pfiscalCode.trim()) {
      toast.error("Inserisci email o codice fiscale del paziente prima di recuperare.");
      return;
    }
    setRecovering(true);
    try {
      const params = {};
      if (pfiscalCode.trim()) params.patient_fiscal_code = pfiscalCode.trim().toUpperCase();
      else params.patient_email = pemail.trim();
      const { data } = await api.get("/referto/last-payload", { params });
      const d = data.form_data || {};
      const has = (v) => !!(v && String(v).trim());
      if (d.patient_first_name && !pfirst) setPfirst(d.patient_first_name);
      if (d.patient_last_name && !plast) setPlast(d.patient_last_name);
      if (d.patient_sex && !psex) setPsex(d.patient_sex);
      if (d.patient_birth_date && !pbirth) setPbirth(d.patient_birth_date);
      if (d.patient_birth_place && !pbirthPlace) setPbirthPlace(d.patient_birth_place);
      if (d.patient_phone && !pphone) setPphone(d.patient_phone);
      if (d.family_history) { setFamHasIssues(true); setFamilyHistory(d.family_history); }
      if (d.personal_conditions) { setHasConditions(true); setPersonalConditions(d.personal_conditions); }
      if (d.current_medications) { setHasMeds(true); setCurrentMedications(d.current_medications); }
      if (d.previous_surgeries) { setHasSurgeries(true); setPreviousSurgeries(d.previous_surgeries); }
      if (d.allergies) { setHasAllergies(true); setAllergies(d.allergies); }
      if (d.pathology_history) setPathologyHistory(d.pathology_history);
      if (has(d.smoking)) { setSmokes(true); setSmoking(d.smoking); }
      if (d.diuresis) setDiuresis(d.diuresis);
      if (d.bowel) setBowel(d.bowel);
      if (d.other_habits) setOtherHabits(d.other_habits);
      if (d.previous_screenings) setPreviousScreenings(d.previous_screenings);
      if (d.presented_documentation) setPresentedDocumentation(d.presented_documentation);
      setOpenFam(true); setOpenPers(true); setOpenHabits(true);
      const when = new Date(data.created_at).toLocaleDateString("it-IT", {day:"2-digit", month:"short", year:"numeric"});
      toast.success(`Anamnesi recuperata dal referto del ${when} — verifica ed eventualmente modifica.`);
    } catch (e) {
      if (e.response?.status === 404) {
        toast.info("Nessun referto precedente trovato per questo paziente.");
      } else {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore recupero anamnesi");
      }
    } finally { setRecovering(false); }
  };

  const previewAnamnesisAI = async () => {
    setAiPreviewBusy(true);
    try {
      const { data } = await api.post("/referto/preview/anamnesis", {
        family_history: famHasIssues ? familyHistory : "",
        personal_conditions: hasConditions ? personalConditions : "",
        current_medications: hasMeds ? currentMedications : "",
        previous_surgeries: hasSurgeries ? previousSurgeries : "",
        allergies: hasAllergies ? allergies : "",
        pathology_history: pathologyHistory,
        smoking: smokes ? smoking : "",
        diuresis, bowel, other_habits: otherHabits,
      });
      setAiPreview({ kind: "anamnesis", title: "Sintesi anamnesi (AI)",
                     text: data.summary || "(Nessun contenuto da riassumere)", color: "purple" });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore anteprima anamnesi");
    } finally { setAiPreviewBusy(false); }
  };

  const previewMmgLetterAI = async () => {
    if (!recommendedExams.trim() && !therapy.trim()) {
      toast.error("Compila esami consigliati o terapia prima di generare la lettera.");
      return;
    }
    if (!pfirst.trim() || !plast.trim() || !pemail.trim()) {
      toast.error("Serve nome, cognome ed email del paziente per la lettera MMG.");
      return;
    }
    setAiPreviewBusy(true);
    try {
      const { data } = await api.post("/referto/preview/mmg-letter", {
        patient_first_name: pfirst.trim(), patient_last_name: plast.trim(),
        patient_birth_date: pbirth || "", patient_email: pemail.trim(),
        visit_reason: visitReason.trim(), diagnosis: diagnosis.trim(),
        recommended_exams: recommendedExams.trim(), therapy: therapy.trim(),
      });
      setAiPreview({ kind: "mmg", title: "Bozza lettera al MMG (AI)",
                     text: data.letter || "", color: "blue" });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore anteprima MMG");
    } finally { setAiPreviewBusy(false); }
  };

  const previewReferralAI = async () => {
    if (!selectedRef?.id) { toast.error("Seleziona prima un collega."); return; }
    if (!referralReason.trim()) { toast.error("Scrivi il motivo del consulto."); return; }
    if (!pfirst.trim() || !plast.trim() || !pemail.trim()) {
      toast.error("Serve nome, cognome ed email del paziente."); return;
    }
    setAiPreviewBusy(true);
    try {
      const { data } = await api.post("/referto/preview/referral-letter", {
        referral_specialist_id: selectedRef.id,
        patient_first_name: pfirst.trim(), patient_last_name: plast.trim(),
        patient_birth_date: pbirth || "", patient_email: pemail.trim(),
        visit_reason: visitReason.trim(), referral_reason: referralReason.trim(),
      });
      setAiPreview({ kind: "referral", title: "Bozza consulto al collega (AI)",
                     text: data.letter || "", color: "purple" });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore anteprima consulto");
    } finally { setAiPreviewBusy(false); }
  };

  const attachmentsSize = attachments.reduce((s, a) => s + a.size, 0);
  const fmtSize = (b) => b < 1024 ? `${b} B` : b < 1024 * 1024 ? `${(b / 1024).toFixed(0)} KB` : `${(b / 1024 / 1024).toFixed(1)} MB`;

  const copyLink = async () => {
    if (!result?.download_url) return;
    try { await navigator.clipboard.writeText(result.download_url); setCopied(true); setTimeout(() => setCopied(false), 1800); }
    catch { toast.error("Impossibile copiare"); }
  };

  if (!isSpecialist) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Card className="p-8 text-center text-slate-600 bg-white">
          Questa pagina è riservata ai medici specialisti.
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 max-w-4xl mx-auto pb-24" data-testid="nuovo-referto-page">
      <button onClick={() => navigate("/dashboard")}
              className="text-sm text-slate-500 hover:text-slate-800 flex items-center gap-1 mb-3">
        <ChevronLeft className="h-4 w-4" /> Dashboard
      </button>
      <div className="flex items-center gap-3 mb-4">
        <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white flex items-center justify-center shadow-lg shadow-emerald-500/20">
          <FileCheck2 className="h-6 w-6" />
        </div>
        <div className="flex-1">
          <h1 className="font-heading text-2xl md:text-3xl font-extrabold text-slate-900">
            {isNutritionist ? "Nuova valutazione nutrizionale" : "Nuova valutazione clinica"}
          </h1>
          <p className="text-sm text-slate-500">
            {isNutritionist
              ? "Valutazione nutrizionale con dati antropometrici, obiettivi e piano dietetico — PDF firmato e inviato al paziente."
              : "Referto standalone per una visita specialistica — PDF firmato con timbro digitale, inviato al paziente per email."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setShowDrafts(true)}
                  data-testid="referto-open-drafts"
                  className="border-slate-300 bg-white text-slate-700 hover:bg-slate-50 h-9">
            <FolderOpen className="h-4 w-4 mr-1.5" /> Bozze ({drafts.length})
          </Button>
          <Button variant="outline" onClick={saveDraft}
                  data-testid="referto-save-draft"
                  className="border-slate-300 bg-white text-slate-700 hover:bg-slate-50 h-9">
            <Save className="h-4 w-4 mr-1.5" /> Salva bozza
          </Button>
        </div>
      </div>
      {currentDraftId && (
        <div className="mb-3 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-1.5 inline-block">
          ✓ Stai lavorando su una bozza — le modifiche si salvano cliccando "Salva bozza"
        </div>
      )}

      {/* -------- Sede da cui viene emesso il referto (solo se il medico ha più sedi) -------- */}
      {hasMultipleLocations && (
        <Card className="p-4 md:p-5 rounded-2xl mb-4 border-amber-200 bg-amber-50/40">
          <div className="flex items-center gap-2 mb-2">
            <MapPin className="h-5 w-5 text-amber-600" />
            <h2 className="font-heading text-base font-bold text-slate-900">
              Sede da cui stai emettendo il referto
            </h2>
          </div>
          <p className="text-xs text-slate-600 mb-3">
            I dati della struttura selezionata verranno riportati nell'intestazione del referto (nome, indirizzo e recapiti).
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {(user?.locations || []).map((loc, i) => (
              <button
                key={loc.id || i}
                type="button"
                onClick={() => setLocationIndex(i)}
                data-testid={`referto-location-${i}`}
                className={`text-left p-3 rounded-xl border transition-colors ${
                  locationIndex === i
                    ? "border-amber-500 bg-white ring-2 ring-amber-400"
                    : "border-slate-200 bg-white hover:border-amber-300"
                }`}
              >
                <div className="flex items-start gap-2">
                  <div
                    className={`mt-1 h-3 w-3 rounded-full ${
                      locationIndex === i ? "bg-amber-500" : "border border-slate-300 bg-white"
                    }`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-900 text-sm truncate">{loc.name}</div>
                    <div className="text-xs text-slate-500 truncate">
                      {[loc.address, loc.city].filter(Boolean).join(" · ")}
                    </div>
                    {(loc.phone || loc.email) && (
                      <div className="text-[11px] text-slate-400 truncate">
                        {loc.phone && `Tel. ${loc.phone}`}
                        {loc.phone && loc.email && " · "}
                        {loc.email}
                      </div>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </Card>
      )}

      {/* -------- Barra sezioni (stile cartella clinica): una sezione alla volta, slide laterale -------- */}
      <style>{`
        @keyframes sec-in-right { from { opacity: 0; transform: translateX(48px); } to { opacity: 1; transform: none; } }
        @keyframes sec-in-left  { from { opacity: 0; transform: translateX(-48px); } to { opacity: 1; transform: none; } }
        ${sectionTabs.map((t) => `#${t.id}`).filter((s) => !sectionVisible.has(s.slice(1))).join(",")}${sectionTabs.length ? "{display:none !important}" : ""}
        #${activeSection} { animation: ${slideDir === "left" ? "sec-in-left" : "sec-in-right"} .32s cubic-bezier(.22,.61,.36,1) both; }
      `}</style>
      <div className="sticky top-0 z-20 -mx-1 mb-4 bg-white rounded-lg border border-slate-200 shadow-sm flex overflow-x-auto" data-testid="referto-section-tabs">
        {sectionTabs.map((t, i) => (
          <button key={t.id} type="button" data-testid={`referto-tab-${t.id}`}
                  onClick={() => goToSection(t.id)}
                  className={`flex flex-col items-center gap-1 px-5 py-2.5 text-[11px] font-semibold uppercase tracking-wide border-b-2 transition-colors ${
                    activeSection === t.id ? "bg-[#1E88E5] text-white border-[#1E88E5]" : "text-slate-600 border-transparent hover:bg-slate-50"}`}>
            <t.I className="h-4 w-4" /> <span>{i + 1}. {t.l}</span>
          </button>
        ))}
      </div>

      {/* -------- Card 1: Paziente + Visita -------- */}
      <Card id="sec-anagrafica" className="p-4 md:p-6 rounded-2xl border-emerald-200 shadow-sm bg-emerald-50/40
                       [&_label]:text-slate-700 [&_input]:bg-white [&_input]:text-slate-900
                       [&_input]:border-slate-300 [&_textarea]:bg-white [&_textarea]:text-slate-900
                       [&_textarea]:border-slate-300">
        <div className="flex items-center gap-2 mb-3">
          <User2 className="h-5 w-5 text-emerald-600" />
          <h2 className="font-heading text-lg font-bold text-slate-900">Dati del paziente</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div><Label>Nome <span className="text-red-500">*</span></Label>
            <Input value={pfirst} onChange={(e) => setPfirst(e.target.value)} placeholder="Maria" data-testid="referto-first" /></div>
          <div><Label>Cognome <span className="text-red-500">*</span></Label>
            <Input value={plast} onChange={(e) => setPlast(e.target.value)} placeholder="Rossi" data-testid="referto-last" /></div>
          <div>
            <Label>Sesso</Label>
            <select value={psex} onChange={(e) => setPsex(e.target.value)}
                    data-testid="referto-sex"
                    className="h-10 w-full px-3 rounded-md border border-slate-300 bg-white text-sm text-slate-900">
              <option value="">—</option>
              <option value="F">Femmina</option>
              <option value="M">Maschio</option>
            </select>
          </div>
          <div><Label>Data di nascita</Label>
            <Input type="date" value={pbirth} onChange={(e) => setPbirth(e.target.value)} data-testid="referto-birth" /></div>
          <div><Label>Luogo di nascita (comune)</Label>
            <Input list="comuni-list" value={pbirthPlace}
                   onChange={(e) => setPbirthPlace(e.target.value)}
                   placeholder="Es. Roma" data-testid="referto-birth-place" />
            <datalist id="comuni-list">
              {COMUNI_LIST.map(c => <option key={c} value={c} />)}
            </datalist>
          </div>
          <div><Label>Provincia</Label>
            <select value={pbirthProvince} onChange={(e) => setPbirthProvince(e.target.value)}
                    data-testid="referto-birth-province"
                    className="h-10 w-full px-3 rounded-md border border-slate-300 bg-white text-sm text-slate-900">
              <option value="">—</option>
              {provinces.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div>
            <Label>Età</Label>
            <Input value={patientAge !== "" ? `${patientAge} anni` : ""} readOnly
                   className="bg-slate-50" data-testid="referto-age" />
          </div>
          <div className="md:col-span-2">
            <Label className="flex items-center justify-between">
              Codice fiscale
              {!pfiscalCodeManual && pfiscalCode && (
                <span className="text-[10px] text-emerald-600 font-normal">✓ calcolato automaticamente</span>
              )}
            </Label>
            <Input value={pfiscalCode}
                   onChange={(e) => { setPfiscalCode(e.target.value.toUpperCase()); setPfiscalCodeManual(true); }}
                   onBlur={checkFollowupAvailable}
                   placeholder="Es. RSSMRA80A01H501U (16 caratteri)"
                   maxLength={16} data-testid="referto-fiscal-code"
                   className={pfiscalCodeManual ? "" : "bg-emerald-50/40"} />
            {pfiscalCodeManual && (
              <button type="button" onClick={() => { setPfiscalCodeManual(false); setPfiscalCode(""); }}
                      className="text-[11px] text-slate-500 hover:text-slate-700 underline mt-0.5">
                Ricalcola dai dati sopra
              </button>
            )}
          </div>
          <div><Label>Telefono</Label>
            <Input value={pphone} onChange={(e) => setPphone(e.target.value)}
                   placeholder="Es. +39 333 1234567" data-testid="referto-phone" /></div>
          <div className="md:col-span-2"><Label>Email <span className="text-red-500">*</span></Label>
            <Input type="email" value={pemail} onChange={(e) => setPemail(e.target.value.trim())}
                   onBlur={checkFollowupAvailable}
                   placeholder="paziente@dominio.it" data-testid="referto-email" /></div>
        </div>

        <div className="mt-3">
          <Button type="button" variant="outline" onClick={recoverPreviousAnamnesis}
                  disabled={recovering || (!pemail.trim() && !pfiscalCode.trim())}
                  data-testid="recover-anamnesis-btn"
                  className="w-full border-emerald-300 bg-emerald-50/50 text-emerald-700 hover:bg-emerald-50 h-10 disabled:opacity-40">
            {recovering
              ? <><SynapseLoader size="sm" inline className="mr-2" /> Cerco referto precedente…</>
              : <><FolderOpen className="h-4 w-4 mr-2" /> Recupera anamnesi da referto precedente</>}
          </Button>
          <p className="text-[11px] text-slate-500 mt-1 text-center">
            Se il paziente è già stato visitato, precompila anagrafica e anamnesi dall'ultimo referto (email o CF).
          </p>
        </div>

        {/* ============ VISITA DI CONTROLLO (follow-up) — solo nutrizionista ============ */}
        {isNutritionist && (
          <div className="mt-3">
            <div className={`rounded-xl border-2 ${isFollowupVisit ? "border-blue-400 bg-blue-50" : "border-slate-200 bg-slate-50"} p-3 transition-colors`}>
              <label className="flex items-start gap-2 cursor-pointer" data-testid="followup-toggle-wrapper">
                <input type="checkbox"
                       checked={isFollowupVisit}
                       onChange={(e) => {
                         const on = e.target.checked;
                         setIsFollowupVisit(on);
                         if (on && !followupCtx) loadFollowupContext();
                         if (!on) { setFollowupCtx(null); setIncludeFirstVisitSummary(false); }
                       }}
                       disabled={followupBusy}
                       data-testid="followup-toggle"
                       className="mt-0.5 h-4 w-4 accent-blue-600 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <History className="h-4 w-4 text-blue-700" />
                    <span className="font-semibold text-slate-900 text-[13px]">
                      Visita di controllo (successiva alla prima)
                    </span>
                    {followupAvailable && !isFollowupVisit && (
                      <span className="text-[10px] font-bold text-blue-700 bg-blue-100 px-1.5 py-0.5 rounded">
                        VISITE PRECEDENTI TROVATE
                      </span>
                    )}
                  </div>
                  <p className="text-[11px] text-slate-600 mt-0.5">
                    Il referto conterrà solo il confronto con la 1ª visita, un grafico dell'andamento
                    peso e la valutazione AI su mantenere o rimodulare la dieta.
                  </p>
                </div>
              </label>

              {isFollowupVisit && (
                <div className="mt-3 pl-6 space-y-2" data-testid="followup-panel">
                  <p className="text-[11px] text-slate-600 italic">
                    Il commento clinico integrerà già il richiamo alla prima visita, l'evoluzione
                    del percorso e la decisione sul piano alimentare — non serve una spunta separata.
                  </p>

                  {followupBusy ? (
                    <div className="text-[12px] text-slate-500 italic">Analizzo il progresso con AI…</div>
                  ) : followupCtx ? (
                    <>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
                        <div className="rounded-lg border border-blue-200 bg-white p-2" data-testid="followup-kpi-days">
                          <div className="text-[10px] text-slate-500 font-semibold">GIORNI</div>
                          <div className="text-lg font-bold text-blue-700">{followupCtx.delta_days ?? "—"}</div>
                        </div>
                        <div className="rounded-lg border border-blue-200 bg-white p-2" data-testid="followup-kpi-delta">
                          <div className="text-[10px] text-slate-500 font-semibold">Δ PESO</div>
                          <div className="text-lg font-bold text-slate-800">
                            {followupCtx.first_weight_kg && weightKg
                              ? `${(Number(weightKg) - followupCtx.first_weight_kg).toFixed(1)} kg`
                              : "—"}
                          </div>
                        </div>
                        <div className="rounded-lg border border-blue-200 bg-white p-2" data-testid="followup-kpi-visitn">
                          <div className="text-[10px] text-slate-500 font-semibold">VISITA N.</div>
                          <div className="text-lg font-bold text-purple-700">{followupCtx.visit_number}</div>
                        </div>
                        <div className="rounded-lg border border-blue-200 bg-white p-2" data-testid="followup-kpi-diet">
                          <div className="text-[10px] text-slate-500 font-semibold">DIETA 1ª VISITA</div>
                          <div className="text-[11px] font-semibold text-slate-700 truncate" title={followupCtx.first_diet_type}>
                            {followupCtx.first_diet_type || "—"}
                          </div>
                        </div>
                      </div>
                      {followupCtx.ai_evaluation && (
                        <div className="rounded-lg border border-amber-300 bg-amber-50 p-2.5 mt-2">
                          <div className="flex items-center gap-1.5 mb-1">
                            <Sparkles className="h-3.5 w-3.5 text-amber-700" />
                            <span className="text-[11px] font-bold text-amber-800">
                              Valutazione clinica del progresso
                            </span>
                          </div>
                          <p className="text-[11px] text-slate-700 leading-relaxed line-clamp-4"
                             data-testid="followup-ai-eval-preview">
                            {followupCtx.ai_evaluation}
                          </p>
                        </div>
                      )}
                    </>
                  ) : (
                    <Button type="button" size="sm" variant="outline" onClick={loadFollowupContext}
                            data-testid="followup-load-btn"
                            className="h-8 border-blue-300 text-blue-700 bg-white hover:bg-blue-50">
                      <History className="h-3.5 w-3.5 mr-1.5" /> Carica progresso e valutazione AI
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        <div className="mt-3">
          <Button type="button" variant="outline" onClick={buildClinicalHistory}
                  disabled={clinicalHistoryBusy || (!pemail.trim() && !pfiscalCode.trim())}
                  data-testid="build-clinical-history-btn"
                  className="w-full border-purple-300 bg-purple-50/50 text-purple-700 hover:bg-purple-50 h-10 disabled:opacity-40">
            {clinicalHistoryBusy
              ? <><SynapseLoader size="sm" inline className="mr-2" /> Analizzo la storia clinica…</>
              : <><History className="h-4 w-4 mr-2" /> Crea storia clinica dai referti precedenti (AI)</>}
          </Button>
          <p className="text-[11px] text-slate-500 mt-1 text-center">
            L'AI legge tutti i referti passati del paziente e ricostruisce una sintesi cronologica
            (motivo, riscontri, terapie, peso) — la leggi prima della visita e appare nel PDF.
          </p>
          {clinicalHistoryText && (
            <div className="mt-2 p-3 rounded-lg border border-purple-300 bg-purple-50 flex items-start gap-2"
                 data-testid="clinical-history-attached">
              <History className="h-4 w-4 text-purple-700 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold text-purple-900">Storia clinica AI allegata al referto</div>
                <div className="text-[11px] text-purple-700 truncate">
                  {clinicalHistoryText.slice(0, 140)}{clinicalHistoryText.length > 140 ? "…" : ""}
                </div>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button type="button" onClick={() => { setClinicalHistoryEditor(clinicalHistoryText); setClinicalHistoryModal(true); }}
                        className="text-[11px] text-purple-700 underline hover:text-purple-900">Modifica</button>
                <span className="text-purple-300">·</span>
                <button type="button" onClick={removeClinicalHistory}
                        data-testid="clinical-history-remove"
                        className="text-[11px] text-red-600 underline hover:text-red-800">Rimuovi</button>
              </div>
            </div>
          )}
        </div>

        {/* Antropometria spostata in Card 2.7 dedicata (dopo Anamnesi + Motivo). */}
      </Card>


      {/* -------- Card 1.5 (specialista): Motivo della visita + prima visita / controllo -------- */}
      {isDoctor && (
        <Card id="sec-motivo" className="p-4 md:p-6 rounded-2xl border-teal-200 shadow-sm bg-teal-50/40 mt-5
                         [&_label]:text-slate-700 [&_textarea]:bg-white [&_textarea]:text-slate-900
                         [&_textarea]:border-slate-300" data-testid="card-visit-reason">
          <div className="flex items-center gap-2 mb-3">
            <ClipboardList className="h-5 w-5 text-teal-600" />
            <h2 className="font-heading text-lg font-bold text-slate-900">Motivo della visita</h2>
          </div>
          {hasSpecReason && (
            <div className="mb-3">
              <Label>Problema principale</Label>
              <select value={orlReason} onChange={(e) => setOrlReason(e.target.value)} data-testid="orl-reason-select"
                      className="mt-1 h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900">
                <option value="">— seleziona il motivo —</option>
                {SPEC_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
          )}
          {hasSpecReason && <Label>Dettagli / note sul motivo (opz.)</Label>}
          <Textarea rows={2} value={visitReason} onChange={(e) => setVisitReason(e.target.value)}
                    placeholder={isOrl ? "Es. acufene sx da 3 mesi, peggiorato dopo concerto · ipoacusia dx progressiva" : isDerma ? "Es. neo al dorso che è cambiato di colore negli ultimi 2 mesi · acne al volto da 1 anno" : "Es. controllo di gravidanza alla 24ª settimana · dolore pelvico da 2 settimane · controllo cardiologico per ipertensione"}
                    data-testid="referto-visit-reason" />
          {knownPatient && !knownDismissed && !isFollowupVisit && (
            <div className="mt-3 rounded-xl border border-blue-300 bg-blue-50 p-3 flex items-start gap-3 flex-wrap"
                 data-testid="known-patient-banner">
              <History className="h-4 w-4 text-blue-700 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-[220px] text-sm">
                <div className="font-semibold text-slate-900">
                  Paziente già visitato · {knownPatient.count} {knownPatient.count === 1 ? "visita" : "visite"}
                </div>
                <div className="text-xs text-slate-600">
                  Ultima: {lastVisit?.at ? new Date(lastVisit.at).toLocaleDateString("it-IT") : "—"}
                  {lastVisit?.title ? ` · ${lastVisit.title}` : ""}{lastVisit?.pregnancy_week ? ` · sett. ${lastVisit.pregnancy_week}` : ""}
                </div>
              </div>
              <div className="flex gap-2">
                <Button type="button" size="sm" className="h-8 bg-blue-700 hover:bg-blue-800 text-white"
                        onClick={() => { setIsFollowupVisit(true); if (!pfirst.trim()) recoverPreviousAnamnesis(); }}
                        data-testid="known-patient-followup">
                  Visita di controllo
                </Button>
                <Button type="button" size="sm" variant="outline" className="h-8 bg-white"
                        onClick={() => setKnownDismissed(true)} data-testid="known-patient-new">
                  Nuova visita
                </Button>
              </div>
            </div>
          )}
          {hasSpecReason ? (
            <div className="mt-3">
              <Label className="mb-1.5 block">Tipo di visita</Label>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="visit-type-group">
                {VISIT_TYPES.map((t) => (
                  <button key={t.id} type="button" onClick={() => { setVisitType(t.id); setIsFollowupVisit(t.followup); }}
                          data-testid={`visit-type-${t.id}`}
                          className={`text-left rounded-xl border-2 p-2.5 transition-colors ${
                            visitType === t.id ? "border-teal-500 bg-white" : "border-slate-200 bg-white/60 hover:border-teal-300"}`}>
                    <div className="font-semibold text-slate-900 text-sm">{t.label}</div>
                    <div className="text-[11px] text-slate-500 leading-tight">{t.d}</div>
                  </button>
                ))}
              </div>
            </div>
          ) : (
          <div className="mt-3 grid grid-cols-2 gap-2">
            {[{ v: false, l: "Prima visita", d: "Compila anamnesi e storia clinica" },
              { v: true, l: "Visita di controllo", d: "Anamnesi già nota: solo il problema attuale" }].map((o) => (
              <button key={String(o.v)} type="button" onClick={() => setIsFollowupVisit(o.v)}
                      data-testid={o.v ? "visit-type-followup" : "visit-type-first"}
                      className={`text-left rounded-xl border-2 p-3 transition-colors ${
                        isFollowupVisit === o.v ? "border-teal-500 bg-white" : "border-slate-200 bg-white/60 hover:border-teal-300"}`}>
                <div className="font-semibold text-slate-900 text-sm">{o.l}</div>
                <div className="text-[11px] text-slate-500">{o.d}</div>
              </button>
            ))}
          </div>
          )}
        </Card>
      )}

      {/* -------- Card 2: Anamnesi -------- */}
      {isDoctor && isFollowupVisit ? (
        <div className="mt-5 rounded-xl border border-dashed border-rose-200 bg-rose-50/30 px-4 py-3 text-sm text-slate-600"
             data-testid="anamnesis-skipped-note">
          <div className="flex items-center gap-2 flex-wrap">
            <History className="h-4 w-4 text-rose-400" />
            <span className="flex-1">Visita di controllo: anamnesi e storia clinica non richieste (già raccolte alla prima visita).</span>
            {knownPatient && (
              <label className="inline-flex items-center gap-1.5 text-xs text-slate-700 cursor-pointer" data-testid="last-visit-toggle-label">
                <input type="checkbox" checked={showLastVisit} onChange={(e) => setShowLastVisit(e.target.checked)}
                       className="h-3.5 w-3.5 accent-blue-600" data-testid="last-visit-toggle" />
                Mostra riassunto ultima visita
              </label>
            )}
          </div>
          {showLastVisit && lastVisit && (
            <div className="mt-3 rounded-lg border border-blue-200 bg-white p-3 text-sm space-y-1" data-testid="last-visit-summary">
              <div className="font-semibold text-slate-900">
                {new Date(lastVisit.at).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" })} · {lastVisit.title}
                {lastVisit.pregnancy_week && <span className="ml-2 text-xs text-pink-700 font-normal">sett. {lastVisit.pregnancy_week}</span>}
              </div>
              {lastVisit.reason && <div><span className="text-slate-500">Motivo:</span> {lastVisit.reason}</div>}
              {lastVisit.summary && <div><span className="text-slate-500">Conclusioni:</span> {lastVisit.summary}</div>}
              {(lastVisit.final_hypothesis || lastVisit.hypotheses?.length > 0) && (
                <div><span className="text-slate-500">Ipotesi:</span> {lastVisit.final_hypothesis || lastVisit.hypotheses.join(", ")}</div>
              )}
              {knownPatient.items.length > 1 && (
                <div className="text-xs text-slate-500 pt-1 border-t border-slate-100">
                  Visite precedenti: {knownPatient.items.slice(1, 4).map((v) => `${new Date(v.at).toLocaleDateString("it-IT")} (${v.title})`).join(" · ")}
                </div>
              )}
              <button type="button" className="text-xs text-blue-700 underline"
                      onClick={() => navigate(`/cartella-paziente/${encodeURIComponent(pfiscalCode.trim().toUpperCase())}`)}
                      data-testid="last-visit-open-folder">Apri cartella completa</button>
            </div>
          )}
        </div>
      ) : (
      <Card id="sec-anamnesi" className="p-4 md:p-6 rounded-2xl border-rose-200 shadow-sm bg-rose-50/40 mt-5
                       [&_label]:text-slate-700 [&_input]:bg-white [&_input]:text-slate-900
                       [&_input]:border-slate-300 [&_textarea]:bg-white [&_textarea]:text-slate-900
                       [&_textarea]:border-slate-300">
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <Heart className="h-5 w-5 text-rose-500" />
          <h2 className="font-heading text-lg font-bold text-slate-900 flex-1">Anamnesi e storia clinica</h2>
          {isDoctor && (
            <div className="flex items-center gap-2 flex-wrap" data-testid="intake-actions">
              <Button type="button" size="sm" variant="outline" onClick={openIntakeForm} data-testid="intake-print-btn"
                      className="h-8 text-xs bg-white border-slate-300 text-slate-700 hover:bg-slate-50">
                <Printer className="h-3.5 w-3.5 mr-1" /> Modulo paziente (PDF compilabile)
              </Button>
              <div className="inline-flex items-center gap-1 h-8 rounded-md border border-slate-300 bg-white px-2" title="Data visita (opzionale): attiva il promemoria se il modulo non viene riconsegnato">
                <span className="text-[11px] text-slate-500">Visita</span>
                <input type="date" value={intakeVisitDate} onChange={(e) => setIntakeVisitDate(e.target.value)} data-testid="intake-visit-date"
                       className="text-xs bg-transparent outline-none text-slate-700 h-6" />
              </div>
              <Button type="button" size="sm" variant="outline" onClick={sendIntakeEmail} disabled={intakeSending} data-testid="intake-email-btn"
                      className="h-8 text-xs bg-white border-slate-300 text-slate-700 hover:bg-slate-50">
                <MailIcon className="h-3.5 w-3.5 mr-1" /> {intakeSending ? "Invio…" : "Invia via email"}
              </Button>
              <label className={`h-8 inline-flex items-center px-3 text-xs rounded-md border cursor-pointer transition-colors ${
                intakeBusy ? "bg-slate-100 text-slate-400 border-slate-200" : "bg-[#1E88E5] text-white border-[#1E88E5] hover:bg-[#1976D2]"}`}
                     data-testid="intake-import-label">
                {intakeBusy ? <><SynapseLoader size="sm" inline className="mr-1" /> Lettura AI…</> : <><Sparkles className="h-3.5 w-3.5 mr-1" /> Importa modulo compilato</>}
                <input type="file" accept="application/pdf,image/*" multiple className="hidden" onChange={onIntakeFiles} disabled={intakeBusy}
                       data-testid="intake-import-input" />
              </label>
            </div>
          )}
        </div>
        {isDoctor && (
          <p className="text-[11px] text-slate-500 -mt-2 mb-3" data-testid="intake-hint">
            Stampa o invia il modulo alla paziente (compilabile a mano o digitalmente in sala d'attesa / con la segreteria); al rientro carica il PDF o una foto: l'AI inserisce i dati nelle voci qui sotto e crea la sintesi anamnestica.
            {intakeInfo && <span className="ml-1 text-emerald-700 font-medium" data-testid="intake-info">Ultima importazione: {intakeInfo.n} campi ({intakeInfo.mode}).</span>}
          </p>
        )}
        {isDoctor && intakeReturns.length > 0 && (
          <div className="mb-3 rounded-lg border border-emerald-300 bg-emerald-50 p-3 flex items-center gap-3 flex-wrap" data-testid="intake-returned-banner">
            <CheckCircle2Icon className="h-4 w-4 text-emerald-700" />
            <div className="flex-1 text-sm text-slate-800">
              <b>Modulo compilato ricevuto dalla paziente</b>
              <span className="text-xs text-slate-600 ml-2">{new Date(intakeReturns[0].returned_at).toLocaleString("it-IT")}</span>
            </div>
            {(intakeReturns[0].files || []).map((f) => (
              <a key={f.idx} href={`${process.env.REACT_APP_BACKEND_URL}/api/referto/intake-returns/${intakeReturns[0].id}/file/${f.idx}`} target="_blank" rel="noreferrer"
                 data-testid={`intake-returned-download-${f.idx}`}
                 className="inline-flex items-center h-8 px-2.5 rounded-md border border-emerald-300 bg-white text-xs text-emerald-800 hover:bg-emerald-100">
                <Paperclip className="h-3.5 w-3.5 mr-1" /> {intakeReturns[0].files.length > 1 ? `File ${f.idx + 1}` : "Apri modulo"}
              </a>
            ))}
            <Button type="button" size="sm" onClick={() => importReturned(intakeReturns[0].id)} disabled={intakeBusy}
                    className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="intake-returned-import">
              <Sparkles className="h-3.5 w-3.5 mr-1" /> Leggi e importa
            </Button>
          </div>
        )}
        <IntakePreviewDialog open={intakePreviewOpen} onOpenChange={setIntakePreviewOpen} result={intakePreview} onApply={confirmIntake} />

        {/* Screening precedenti — nascosto per nutrizionista; per ginecologia è strutturato in fondo */}
        {!isNutritionist && !isGyn && (
        <div>
          <Label className="flex items-center gap-1.5">
            <ClipboardList className="h-4 w-4 text-slate-600" />
            Screening / controlli precedenti (opz.)
          </Label>
          <Textarea rows={2} value={previousScreenings}
                    onChange={(e) => setPreviousScreenings(e.target.value)}
                    placeholder="Es. ultimo ecocardio nov 2024 nella norma, MOC 2023, screening colon 2022…"
                    data-testid="referto-prev-screenings" />
        </div>
        )}

        {/* ---- Anamnesi (accordion) ---- */}
        <div className="mt-4 space-y-2">
          <h3 className="font-heading text-sm font-semibold text-slate-600 uppercase tracking-wide flex items-center gap-2 mb-1">
            Anamnesi
          </h3>

          {/* Familiare */}
          <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
            <button type="button" onClick={() => setOpenFam(!openFam)}
                    className="w-full px-3 py-2.5 flex items-center justify-between hover:bg-slate-50">
              <span className="text-sm font-semibold text-slate-900">Anamnesi familiare</span>
              {openFam ? <ChevronUp className="h-4 w-4 text-slate-500" /> : <ChevronDown className="h-4 w-4 text-slate-500" />}
            </button>
            {openFam && (
              <div className="px-3 py-3 border-t border-slate-100 space-y-2">
                <div className="flex items-center gap-4 text-sm">
                  <span className="text-slate-700">Patologie rilevanti in famiglia?</span>
                  <label className="flex items-center gap-1 cursor-pointer">
                    <input type="radio" name="fam" checked={!famHasIssues}
                           onChange={() => { setFamHasIssues(false); setFamilyHistory(""); }}
                           className="accent-emerald-600" data-testid="fam-no" /> No
                  </label>
                  <label className="flex items-center gap-1 cursor-pointer">
                    <input type="radio" name="fam" checked={famHasIssues}
                           onChange={() => setFamHasIssues(true)}
                           className="accent-emerald-600" data-testid="fam-yes" /> Sì
                  </label>
                </div>
                {famHasIssues && (
                  <Textarea rows={2} value={familyHistory}
                            onChange={(e) => setFamilyHistory(e.target.value)}
                            placeholder={isGyn ? "Es. madre K mammella a 52 anni · zia K ovaio · padre TVP · nessuna malattia genetica nota" : "Es. padre iperteso, madre diabete tipo 2, nonna neoplasia mammaria…"}
                            data-testid="fam-text" />
                )}
                {isGyn && famHasIssues && (
                  <p className="text-[11px] text-slate-500">Indagare: tumori mammella / ovaio / utero / colon · tromboembolismo · malattie genetiche rilevanti.</p>
                )}
                {isOrl && <AnFields obj={orlFam} setObj={setOrlFam} fields={ORL_FAM_FIELDS} prefix="orlfam" />}
                {isDerma && <AnFields obj={dermFam} setObj={setDermFam} fields={DERMA_FAM_FIELDS} prefix="dermfam" />}
              </div>
            )}
          </div>

          {/* Personale */}
          <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
            <button type="button" onClick={() => setOpenPers(!openPers)}
                    className="w-full px-3 py-2.5 flex items-center justify-between hover:bg-slate-50">
              <span className="text-sm font-semibold text-slate-900">
                {isNutritionist ? "Anamnesi medica" : "Anamnesi personale"}
              </span>
              {openPers ? <ChevronUp className="h-4 w-4 text-slate-500" /> : <ChevronDown className="h-4 w-4 text-slate-500" />}
            </button>
            {openPers && (
              <div className="px-3 py-3 border-t border-slate-100 space-y-3">
                {[
                  { label: "Patologie", flag: hasConditions, setFlag: setHasConditions, val: personalConditions, setVal: setPersonalConditions, placeholder: "Es. ipertensione arteriosa dal 2018, dislipidemia…", tid: "conditions" },
                  { label: "Farmaci attualmente assunti", flag: hasMeds, setFlag: setHasMeds, val: currentMedications, setVal: setCurrentMedications, placeholder: "Es. Ramipril 5mg 1cp/die, Atorvastatina 20mg…", tid: "meds" },
                  { label: "Interventi chirurgici precedenti", flag: hasSurgeries, setFlag: setHasSurgeries, val: previousSurgeries, setVal: setPreviousSurgeries, placeholder: "Es. colecistectomia 2015…", tid: "surgeries" },
                  { label: "Allergie", flag: hasAllergies, setFlag: setHasAllergies, val: allergies, setVal: setAllergies, placeholder: "Es. penicillina, pollini graminacee…", tid: "allergies" },
                ].map((f) => (
                  <div key={f.tid}>
                    <div className="flex items-center gap-3 text-sm">
                      <span className="text-slate-700 font-medium min-w-[210px]">{f.label}:</span>
                      <label className="flex items-center gap-1 cursor-pointer text-sm">
                        <input type="radio" checked={!f.flag} onChange={() => { f.setFlag(false); f.setVal(""); }}
                               className="accent-emerald-600" data-testid={`${f.tid}-no`} /> Nessuna
                      </label>
                      <label className="flex items-center gap-1 cursor-pointer text-sm">
                        <input type="radio" checked={f.flag} onChange={() => f.setFlag(true)}
                               className="accent-emerald-600" data-testid={`${f.tid}-yes`} /> Sì
                      </label>
                    </div>
                    {f.flag && (
                      <Textarea rows={2} value={f.val} onChange={(e) => f.setVal(e.target.value)}
                                placeholder={f.placeholder} className="mt-1"
                                data-testid={`${f.tid}-text`} />
                    )}
                  </div>
                ))}
                {isOrl && <AnFields obj={orlPers} setObj={setOrlPers} fields={ORL_PERS_FIELDS} prefix="orlpers" />}
                {isDerma && <AnFields obj={dermPers} setObj={setDermPers} fields={DERMA_PERS_FIELDS} prefix="dermpers" />}
                <div>
                  <Label>Anamnesi patologica specifica (opz.)</Label>
                  <Textarea rows={2} value={pathologyHistory}
                            onChange={(e) => setPathologyHistory(e.target.value)}
                            placeholder="Es. ginecologo: cicli regolari 28gg, ultima mestruazione 15/01/26, G1P0A0…  Cardio: ultimo ECG 2024 normale…"
                            data-testid="pathology-history" />
                </div>
              </div>
            )}
          </div>

          {/* Anamnesi alimentare (solo nutrizionista) */}
          {isNutritionist && (
          <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
            <button type="button" onClick={() => setOpenAlim(!openAlim)}
                    className="w-full px-3 py-2.5 flex items-center justify-between hover:bg-slate-50">
              <span className="text-sm font-semibold text-slate-900">Anamnesi alimentare</span>
              {openAlim ? <ChevronUp className="h-4 w-4 text-slate-500" /> : <ChevronDown className="h-4 w-4 text-slate-500" />}
            </button>
            {openAlim && (
              <div className="px-3 py-3 border-t border-slate-100 space-y-2">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
                  {FOOD_HABITS_OPTIONS.map((opt) => (
                    <label key={opt.id} className={`flex items-center gap-2 px-2 py-1.5 rounded-md border cursor-pointer text-[13px] transition-colors ${
                      foodHabits.includes(opt.id)
                        ? "bg-teal-50 border-teal-300 text-slate-900"
                        : "bg-white border-slate-200 text-slate-700 hover:border-teal-200"
                    }`}>
                      <input type="checkbox" checked={foodHabits.includes(opt.id)}
                             onChange={() => setFoodHabits((cur) => toggleInList(cur, opt.id))}
                             className="h-3.5 w-3.5 accent-teal-600"
                             data-testid={`food-habit-${opt.id}`} />
                      {opt.label}
                    </label>
                  ))}
                </div>
                <div>
                  <Label className="text-xs">Note aggiuntive (pasti tipici, preferenze, orari…)</Label>
                  <Textarea rows={2} value={foodHabitsNotes}
                            onChange={(e) => setFoodHabitsNotes(e.target.value)}
                            placeholder="Es. colazione dolce alle 7:30, pranzo veloce in ufficio, cena tardi verso le 21…"
                            data-testid="food-habits-notes" />
                </div>
              </div>
            )}
          </div>
          )}

          {/* Stile di vita (solo nutrizionista) */}
          {isNutritionist && (
          <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
            <button type="button" onClick={() => setOpenLife(!openLife)}
                    className="w-full px-3 py-2.5 flex items-center justify-between hover:bg-slate-50">
              <span className="text-sm font-semibold text-slate-900">Stile di vita</span>
              {openLife ? <ChevronUp className="h-4 w-4 text-slate-500" /> : <ChevronDown className="h-4 w-4 text-slate-500" />}
            </button>
            {openLife && (
              <div className="px-3 py-3 border-t border-slate-100 space-y-2">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
                  {LIFESTYLE_OPTIONS.map((opt) => (
                    <label key={opt.id} className={`flex items-center gap-2 px-2 py-1.5 rounded-md border cursor-pointer text-[13px] transition-colors ${
                      lifestyleHabits.includes(opt.id)
                        ? "bg-emerald-50 border-emerald-300 text-slate-900"
                        : "bg-white border-slate-200 text-slate-700 hover:border-emerald-200"
                    }`}>
                      <input type="checkbox" checked={lifestyleHabits.includes(opt.id)}
                             onChange={() => setLifestyleHabits((cur) => toggleInList(cur, opt.id))}
                             className="h-3.5 w-3.5 accent-emerald-600"
                             data-testid={`lifestyle-habit-${opt.id}`} />
                      {opt.label}
                    </label>
                  ))}
                </div>
                <div>
                  <Label className="text-xs">Note aggiuntive (professione, hobby, dettagli sonno/stress…)</Label>
                  <Textarea rows={2} value={lifestyleHabitsNotes}
                            onChange={(e) => setLifestyleHabitsNotes(e.target.value)}
                            placeholder="Es. impiegata in ufficio, palestra 2v/settimana, difficoltà ad addormentarsi…"
                            data-testid="lifestyle-habits-notes" />
                </div>
              </div>
            )}
          </div>
          )}

          {/* Anamnesi ORL (solo Otorinolaringoiatria) */}
          {isOrl && (
            <AnAccordion title="Anamnesi ORL" k="orl" open={openOrl} setOpen={setOpenOrl}>
              <AnFields obj={orlHist} setObj={setOrlHist} fields={ORL_HISTORY_FIELDS} prefix="orl" />
            </AnAccordion>
          )}

          {/* Anamnesi dermatologica + esposizione solare + prodotti + tricologica (solo Dermatologia) */}
          {isDerma && (<>
            <AnAccordion title="Anamnesi dermatologica" k="derma" open={openDerm} setOpen={setOpenDerm}>
              <AnFields obj={dermHist} setObj={setDermHist} fields={DERMA_HISTORY_FIELDS} prefix="derm" />
            </AnAccordion>
            <AnAccordion title="Esposizione solare" k="sun" open={openSun} setOpen={setOpenSun}>
              <AnFields obj={dermSun} setObj={setDermSun} fields={DERMA_SUN_FIELDS} prefix="dermsun" />
            </AnAccordion>
            <AnAccordion title="Farmaci / prodotti" k="prod" open={openProd} setOpen={setOpenProd}>
              <AnFields obj={dermProd} setObj={setDermProd} fields={DERMA_PROD_FIELDS} prefix="dermprod" />
            </AnAccordion>
            <AnAccordion title="Anamnesi tricologica (se pertinente)" k="tric" open={openTric} setOpen={setOpenTric}>
              <AnFields obj={dermTric} setObj={setDermTric} fields={DERMA_TRIC_FIELDS} prefix="dermtric" />
            </AnAccordion>
          </>)}

          {/* Ginecologica + Ostetrica + Contraccezione (solo Ginecologia) */}
          {isGyn && (<>
            <AnAccordion title="Anamnesi ginecologica" k="gyn" open={openGyn} setOpen={setOpenGyn}>
              <AnFields obj={gyn} setObj={setGyn} fields={GYN_FIELDS} prefix="gyn" />
            </AnAccordion>
            <AnAccordion title="Anamnesi ostetrica" k="obst" open={openObst} setOpen={setOpenObst}>
              <AnFields obj={obst} setObj={setObst} fields={OBST_FIELDS} prefix="obst" />
            </AnAccordion>
            <AnAccordion title="Contraccezione e salute sessuale (eventuale)" k="sexual" open={openSexual} setOpen={setOpenSexual}>
              <AnFields obj={sexual} setObj={setSexual} fields={SEX_FIELDS} prefix="sex" />
            </AnAccordion>
          </>)}

          {/* Abitudini (nascoste per nutrizionista — sostituite da Alimentare + Stile di vita) */}
          {!isNutritionist && (
          <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
            <button type="button" onClick={() => setOpenHabits(!openHabits)}
                    className="w-full px-3 py-2.5 flex items-center justify-between hover:bg-slate-50">
              <span className="text-sm font-semibold text-slate-900">{isOrl ? "Abitudini / esposizioni" : "Abitudini e informazioni generali"}<PriorityBadge k="expo" /></span>
              {openHabits ? <ChevronUp className="h-4 w-4 text-slate-500" /> : <ChevronDown className="h-4 w-4 text-slate-500" />}
            </button>
            {openHabits && (
              <div className="px-3 py-3 border-t border-slate-100 space-y-3">
                <div>
                  <div className="flex items-center gap-3 text-sm">
                    <Cigarette className="h-4 w-4 text-slate-500" />
                    <span className="text-slate-700 font-medium min-w-[100px]">Fumo:</span>
                    <label className="flex items-center gap-1 cursor-pointer">
                      <input type="radio" checked={!smokes} onChange={() => { setSmokes(false); setSmoking(""); }}
                             className="accent-emerald-600" data-testid="smokes-no" /> No
                    </label>
                    <label className="flex items-center gap-1 cursor-pointer">
                      <input type="radio" checked={smokes} onChange={() => setSmokes(true)}
                             className="accent-emerald-600" data-testid="smokes-yes" /> Sì
                    </label>
                  </div>
                  {smokes && (
                    <Input value={smoking} onChange={(e) => setSmoking(e.target.value)}
                           placeholder="Es. 10 sigarette/die da 15 anni" className="mt-1"
                           data-testid="smoking-detail" />
                  )}
                </div>
                {isGyn && (
                  <AnFields obj={gynExtra} setObj={setGynExtra} prefix="hab"
                            fields={[["alcol", "Alcol"], ["attivita", "Attività fisica"], ["alimentazione", "Alimentazione"], ["ricoveri", "Ricoveri"]]} />
                )}
                {isOrl && <AnFields obj={orlExpo} setObj={setOrlExpo} fields={ORL_EXPO_FIELDS} prefix="orlexpo" />}
                <div><Label>Diuresi</Label>
                  <Input value={diuresis} onChange={(e) => setDiuresis(e.target.value)}
                         placeholder="Es. nella norma / nicturia 2 volte / oliguria"
                         data-testid="diuresis" /></div>
                <div><Label>Alvo</Label>
                  <Input value={bowel} onChange={(e) => setBowel(e.target.value)}
                         placeholder="Es. regolare / stipsi / diarrea"
                         data-testid="bowel" /></div>
                <div><Label>Altre informazioni rilevanti</Label>
                  <Textarea rows={2} value={otherHabits}
                            onChange={(e) => setOtherHabits(e.target.value)}
                            placeholder="Es. attività sportiva 3v/settimana, alcol saltuario, dieta mediterranea…"
                            data-testid="other-habits" /></div>
              </div>
            )}
          </div>
          )}

          {isGyn && (
            <AnAccordion title="Screening / controlli precedenti" k="screen" open={openScreen} setOpen={setOpenScreen}>
              <AnFields obj={screen} setObj={setScreen} fields={SCREEN_FIELDS} prefix="screen" />
              <Textarea rows={2} value={previousScreenings} onChange={(e) => setPreviousScreenings(e.target.value)}
                        placeholder="Altre note sugli screening…" data-testid="referto-prev-screenings" />
            </AnAccordion>
          )}

          {isOrl && (
            <AnAccordion title="Esami precedenti" k="exams" open={openExams} setOpen={setOpenExams}>
              <AnFields obj={orlExams} setObj={setOrlExams} fields={ORL_EXAMS_FIELDS} prefix="orlexam" />
              <Textarea rows={2} value={previousScreenings} onChange={(e) => setPreviousScreenings(e.target.value)}
                        placeholder="Altre note su esami precedenti (date, esiti)…" data-testid="referto-prev-screenings" />
            </AnAccordion>
          )}

          <label className={`flex items-start gap-2 p-3 rounded-lg border cursor-pointer transition-all ${
            aiPolishAnamnesis ? "bg-purple-50 border-purple-300" : "bg-white border-slate-200"
          }`}>
            <input type="checkbox" checked={aiPolishAnamnesis}
                   onChange={(e) => setAiPolishAnamnesis(e.target.checked)}
                   className="mt-0.5 h-4 w-4 accent-purple-600"
                   data-testid="ai-anamnesis" />
            <div className="text-[13px] leading-snug flex-1">
              <span className="font-semibold text-slate-900 flex items-center gap-1.5">
                <Sparkles className="h-3.5 w-3.5 text-purple-600" /> Elabora anamnesi con AI (opz.)
              </span>
              <p className="text-slate-600 mt-0.5">
                Se spunti la casella, nel PDF finale l'AI aggiungerà una <strong>sintesi clinica strutturata</strong>
                dell'anamnesi. Se non ti serve, lascia deselezionato. Puoi vedere subito
                come sarà la sintesi con il pulsante qui a destra.
              </p>
            </div>
            <Button type="button" size="sm" variant="outline"
                    onClick={(e) => { e.preventDefault(); previewAnamnesisAI(); }}
                    disabled={aiPreviewBusy}
                    data-testid="ai-anamnesis-preview-btn"
                    className="border-purple-300 bg-white text-purple-700 hover:bg-purple-50 h-8 text-xs flex-shrink-0">
              {aiPreviewBusy ? <><SynapseLoader size="sm" inline className="mr-1" /> …</>
                             : <><Eye className="h-3 w-3 mr-1" /> Anteprima</>}
            </Button>
          </label>
        </div>
      </Card>
      )}

      {/* -------- Card 2.6 (specialista): Modulo della visita -------- */}
      {isDoctor && (
        <Card id="sec-modulo" className="p-4 md:p-6 rounded-2xl border-indigo-200 shadow-sm bg-indigo-50/40 mt-5
                         [&_label]:text-slate-700 [&_input]:bg-white [&_input]:text-slate-900 [&_input]:border-slate-300"
              data-testid="card-visit-module">
          <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
            <div className="flex items-center gap-2">
              <Stethoscope className="h-5 w-5 text-indigo-600" />
              <h2 className="font-heading text-lg font-bold text-slate-900">Modulo della visita</h2>
            </div>
            <Button type="button" size="sm" variant="outline" onClick={suggestModuleAI}
                    disabled={moduleSuggestBusy || (visitReason.trim().length < 3 && !(hasSpecReason && orlReason))}
                    data-testid="module-suggest-ai-btn"
                    className="h-8 text-xs border-indigo-300 text-indigo-700 bg-white hover:bg-indigo-50">
              {moduleSuggestBusy ? <><SynapseLoader size="sm" inline className="mr-1" /> Analizzo…</>
                                 : <><Sparkles className="h-3.5 w-3.5 mr-1" /> Suggerisci dal motivo</>}
            </Button>
          </div>
          <p className="text-xs text-slate-600 mb-3">
            Domande strutturate per la tua specializzazione: le risposte finiscono nel referto e alimentano la cartella del paziente (es. curva gravidanza).
          </p>
          {moduleSuggest && (
            <div className="mb-3 text-xs text-indigo-800 bg-indigo-100/70 border border-indigo-200 rounded-lg px-3 py-2 flex items-center gap-2 flex-wrap"
                 data-testid="module-suggest-hint">
              <Sparkles className="h-3.5 w-3.5" />
              {moduleSuggest.module_id
                ? <>Suggerito: <b>{moduleSuggest.module_label}</b>{moduleId !== moduleSuggest.module_id && (
                    <button type="button" className="underline" onClick={() => selectModule(moduleSuggest.module_id)}>usa</button>)}</>
                : "Nessun modulo specifico suggerito per questo motivo"}
            </div>
          )}
          <select value={moduleId} onChange={(e) => selectModule(e.target.value)} data-testid="module-select"
                  className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900">
            <option value="">Nessun modulo (visita generica)</option>
            {modules.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
          </select>
          {moduleDef && (
            <div className="mt-3 space-y-2" data-testid="module-questions">
              {moduleDef.questions.map((q, i) => {
                const val = moduleAnswers[q.id] || "";
                return (
                  <div key={q.id} className="rounded-lg border border-slate-200 bg-white p-3" data-testid={`module-q-${q.id}`}>
                    <Label className="text-[13px] font-semibold">{i + 1}. {q.label}</Label>
                    {q.hint && <p className="text-[11px] text-slate-500 mb-1.5">{q.hint}</p>}
                    {q.options ? (
                      <div className="flex gap-1.5 flex-wrap mt-1">
                        {q.options.map((opt) => (
                          <button key={opt} type="button" data-testid={`module-opt-${q.id}-${opt}`}
                                  onClick={() => setModuleAnswers((s) => ({ ...s, [q.id]: s[q.id] === opt ? "" : opt }))}
                                  className={`text-[12px] px-2 py-1 rounded border ${val === opt ? "bg-indigo-100 border-indigo-400 text-indigo-800" : "bg-white border-slate-200 text-slate-700 hover:border-indigo-300"}`}>
                            {opt}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <Input value={val} onChange={(e) => setModuleAnswers((s) => ({ ...s, [q.id]: e.target.value }))}
                             placeholder="Risposta (opzionale)" data-testid={`module-answer-${q.id}`} className="h-9 text-sm mt-1" />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      )}

      {/* -------- Card 2.5: Motivo della visita nutrizionale (solo nutrizionista) -------- */}
      {isNutritionist && (
        <Card id="sec-motivo-nutri" className="p-4 md:p-6 rounded-2xl border-teal-200 shadow-sm bg-teal-50/40 mt-5
                         [&_label]:text-slate-700 [&_input]:bg-white [&_input]:text-slate-900
                         [&_input]:border-slate-300 [&_textarea]:bg-white [&_textarea]:text-slate-900
                         [&_textarea]:border-slate-300">
          <div className="flex items-center gap-2 mb-3">
            <ClipboardList className="h-5 w-5 text-teal-600" />
            <h2 className="font-heading text-lg font-bold text-slate-900">
              Motivo della valutazione nutrizionale
            </h2>
          </div>
          <p className="text-xs text-slate-600 mb-3">
            Seleziona il motivo per il quale il paziente si rivolge al nutrizionista.
            Le domande sotto si adatteranno automaticamente per raccogliere le informazioni più utili.
          </p>
          <div>
            <Label>Motivo della visita <span className="text-red-500">*</span></Label>
            <select
              value={visitMotive}
              onChange={(e) => { setVisitMotive(e.target.value); setMotiveAnswers({}); }}
              data-testid="nut-visit-motive"
              className="h-10 w-full px-3 rounded-md border border-slate-300 bg-white text-sm text-slate-900"
            >
              <option value="">— seleziona un motivo —</option>
              {VISIT_MOTIVES.map((m) => (
                <option key={m.id} value={m.id}>{m.label}</option>
              ))}
            </select>
          </div>

          {visitMotive && (
            <div className="mt-4 space-y-3 border-l-2 border-teal-300 pl-4">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-teal-700">
                Domande specifiche per questo motivo
              </div>
              {(VISIT_MOTIVES.find((m) => m.id === visitMotive)?.questions || []).map((q) => (
                <div key={q.id}>
                  <Label>{q.label}</Label>
                  <Textarea
                    rows={2}
                    value={motiveAnswers[q.id] || ""}
                    onChange={(e) => setMotiveAnswers((prev) => ({ ...prev, [q.id]: e.target.value }))}
                    placeholder={q.placeholder}
                    data-testid={`nut-motive-answer-${q.id}`}
                  />
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {/* -------- Card 2.7: Dati antropometrici + BIA (solo nutrizionista, dopo Motivo) -------- */}
      {isNutritionist && (
        <Card id="sec-antropometria" className="p-4 md:p-6 rounded-2xl border-emerald-200 shadow-sm bg-emerald-50/40 mt-5
                         [&_label]:text-slate-700 [&_input]:bg-white [&_input]:text-slate-900
                         [&_input]:border-slate-300 [&_textarea]:bg-white [&_textarea]:text-slate-900
                         [&_textarea]:border-slate-300">
          <div className="flex items-center gap-2 mb-3">
            <Activity className="h-5 w-5 text-emerald-600" />
            <h2 className="font-heading text-lg font-bold text-slate-900">Dati antropometrici e bioimpedenziometria</h2>
          </div>

          {/* Scostamento peso vs piano precedente */}
          {previousPlanInfo && (
            <div className="mb-3 p-3 rounded-lg border border-amber-300 bg-amber-50/60"
                 data-testid="previous-plan-diff-v2">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="h-4 w-4 text-amber-700" />
                <span className="text-xs font-bold text-amber-900 uppercase tracking-wide">
                  Piano precedente ({previousPlanInfo.lastDateLabel})
                </span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[13px]">
                <div><div className="text-[10px] text-slate-600 uppercase">Peso allora</div>
                  <div className="font-semibold text-slate-900">{previousPlanInfo.lastWeight} kg</div></div>
                <div><div className="text-[10px] text-slate-600 uppercase">Target piano</div>
                  <div className="font-semibold text-slate-900">
                    {previousPlanInfo.lastTargetWeight != null ? `${previousPlanInfo.lastTargetWeight} kg` : "—"}
                  </div></div>
                <div><div className="text-[10px] text-slate-600 uppercase">Peso oggi</div>
                  <div className="font-semibold text-slate-900">{weightKg ? `${weightKg} kg` : "—"}</div></div>
                <div><div className="text-[10px] text-slate-600 uppercase">Scostamento</div>
                  {weightKg && previousPlanInfo.lastTargetWeight != null ? (() => {
                    const diff = parseFloat(weightKg) - parseFloat(previousPlanInfo.lastTargetWeight);
                    const goalDim = parseFloat(previousPlanInfo.lastTargetWeight) < parseFloat(previousPlanInfo.lastWeight);
                    const good = goalDim ? (diff <= 0) : (diff >= 0);
                    return <div className={`font-bold ${good ? "text-emerald-700" : "text-red-600"}`}>
                      {diff >= 0 ? "+" : ""}{diff.toFixed(1)} kg
                      {Math.abs(diff) < 0.5 ? " ✓" : ""}
                    </div>;
                  })() : <div className="text-slate-400 text-xs">—</div>}
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div><Label>Peso (kg)</Label>
              <Input type="number" step="0.1" value={weightKg}
                     onChange={(e) => setWeightKg(e.target.value)} placeholder="70.5"
                     data-testid="ant-weight" /></div>
            <div><Label>Altezza (cm)</Label>
              <Input type="number" step="0.1" value={heightCm}
                     onChange={(e) => setHeightCm(e.target.value)} placeholder="168"
                     data-testid="ant-height" /></div>
            <div><Label>BMI</Label>
              <Input value={bmi || ""} readOnly placeholder="—"
                     className="bg-slate-100 text-slate-700"
                     data-testid="ant-bmi" /></div>
            <div><Label>Addome (cm)</Label>
              <Input type="number" step="0.1" value={abdomenCm}
                     onChange={(e) => setAbdomenCm(e.target.value)} placeholder="88"
                     data-testid="ant-abdomen" /></div>
            <div><Label>Vita (cm)</Label>
              <Input type="number" step="0.1" value={waistCm}
                     onChange={(e) => setWaistCm(e.target.value)} placeholder="82"
                     data-testid="ant-waist" /></div>
            <div><Label>Fianchi (cm)</Label>
              <Input type="number" step="0.1" value={hipCm}
                     onChange={(e) => setHipCm(e.target.value)} placeholder="98"
                     data-testid="ant-hip" /></div>
            <div><Label>Braccio (cm)</Label>
              <Input type="number" step="0.1" value={armCm}
                     onChange={(e) => setArmCm(e.target.value)} placeholder="30"
                     data-testid="ant-arm" /></div>
            <div><Label>Coscia (cm)</Label>
              <Input type="number" step="0.1" value={thighCm}
                     onChange={(e) => setThighCm(e.target.value)} placeholder="55"
                     data-testid="ant-thigh" /></div>
            {isPregnant && (
              <div><Label>Pancia (gravidanza, cm)</Label>
                <Input type="number" step="0.1" value={bellyCm}
                       onChange={(e) => setBellyCm(e.target.value)} placeholder="90"
                       data-testid="ant-belly" /></div>
            )}
            <div className="col-span-2 md:col-span-4">
              <label className="flex items-center gap-2 text-[12px] text-slate-700 cursor-pointer">
                <input type="checkbox" checked={isPregnant}
                       onChange={(e) => setIsPregnant(e.target.checked)}
                       className="h-4 w-4 accent-pink-600"
                       data-testid="ant-is-pregnant" />
                <span>Paziente in gravidanza (attiva misurazione pancia + avatar dedicato)</span>
              </label>
            </div>
          </div>

          {/* Avatar corporeo — silhouette anatomica basata sulle misure (curve realistiche) */}
          {(weightKg && heightCm) && (
            <div className="mt-4 p-3 rounded-lg border border-emerald-200 bg-white">
              <div className="text-[11px] font-bold uppercase tracking-wider text-emerald-800 mb-2">
                Avatar corporeo (misure attuali)
              </div>
              <div className="flex items-center justify-center">
                {(() => {
                  const w = parseFloat(waistCm) || 80;
                  const h = parseFloat(hipCm) || 96;
                  const b = parseFloat(bellyCm) || 0;
                  const arm = parseFloat(armCm) || 28;
                  const thigh = parseFloat(thighCm) || 50;
                  const isFemale = (psex || "").toLowerCase().startsWith("f");
                  // Scale realistici (mm relativi al viewBox 240x420)
                  const cx = 120;
                  // Proporzioni anatomiche: la spalla è ~1.3x più larga del bacino M, ~0.95x per F
                  const shoulderHalf = Math.min(55, Math.max(38, (isFemale ? h * 0.32 : w * 0.42)));
                  const waistHalf   = Math.min(48, Math.max(24, w * 0.35));
                  const hipHalf     = Math.min(56, Math.max(30, h * 0.38));
                  const armThick    = Math.min(14, Math.max(7, arm * 0.28));
                  const thighThick  = Math.min(22, Math.max(11, thigh * 0.28));
                  const skin        = isFemale ? "#F8D7C4" : "#F0C09B";
                  const skinStroke  = "#8B5A3C";
                  const shirt       = isFemale ? "#F9A8D4" : "#93C5FD";
                  const pants       = "#475569";
                  // Punti chiave verticali
                  const headCy = 42, headR = 24;
                  const neckTop = headCy + headR - 2, neckBot = neckTop + 14;
                  const shoulderY = neckBot + 2;
                  const chestY = shoulderY + 22;
                  const waistY = shoulderY + 96;
                  const hipY = waistY + 46;
                  const kneeY = hipY + 96;
                  const ankleY = kneeY + 82;
                  return (
                    <svg width="220" height="380" viewBox="0 0 240 420" data-testid="body-avatar" xmlns="http://www.w3.org/2000/svg">
                      <defs>
                        <linearGradient id="skinG" x1="0" y1="0" x2="1" y2="0">
                          <stop offset="0" stopColor={skin} />
                          <stop offset="1" stopColor="#E5B69E" />
                        </linearGradient>
                        <radialGradient id="cheekG" cx="0.5" cy="0.5" r="0.5">
                          <stop offset="0" stopColor="#F5AAA0" stopOpacity="0.55" />
                          <stop offset="1" stopColor="#F5AAA0" stopOpacity="0" />
                        </radialGradient>
                      </defs>

                      {/* Capelli — retrostante alla testa */}
                      {isFemale ? (
                        <path d={`M ${cx-headR-2} ${headCy-2} Q ${cx-headR-6} ${headCy+headR+30} ${cx-headR-2} ${headCy+headR+38}
                                   L ${cx+headR+2} ${headCy+headR+38} Q ${cx+headR+6} ${headCy+headR+30} ${cx+headR+2} ${headCy-2}
                                   Q ${cx} ${headCy-headR-8} ${cx-headR-2} ${headCy-2} Z`}
                              fill="#6B4423" opacity="0.9" />
                      ) : (
                        <path d={`M ${cx-headR+2} ${headCy-headR+6} Q ${cx} ${headCy-headR-10} ${cx+headR-2} ${headCy-headR+6}
                                   Q ${cx+headR-4} ${headCy-headR+2} ${cx-headR+4} ${headCy-headR+2} Z`}
                              fill="#3F2D1E" />
                      )}

                      {/* Testa (ovale realistico) */}
                      <ellipse cx={cx} cy={headCy} rx={headR-2} ry={headR+2}
                               fill="url(#skinG)" stroke={skinStroke} strokeWidth="0.9" />
                      {/* Guance */}
                      <ellipse cx={cx-11} cy={headCy+6} rx="5" ry="4" fill="url(#cheekG)" />
                      <ellipse cx={cx+11} cy={headCy+6} rx="5" ry="4" fill="url(#cheekG)" />
                      {/* Occhi */}
                      <ellipse cx={cx-8} cy={headCy-2} rx="2.2" ry="1.4" fill="#1E293B" />
                      <ellipse cx={cx+8} cy={headCy-2} rx="2.2" ry="1.4" fill="#1E293B" />
                      {/* Sopracciglia */}
                      <path d={`M ${cx-11} ${headCy-8} Q ${cx-8} ${headCy-10} ${cx-5} ${headCy-8}`}
                            stroke="#3F2D1E" strokeWidth="1.4" fill="none" strokeLinecap="round" />
                      <path d={`M ${cx+5} ${headCy-8} Q ${cx+8} ${headCy-10} ${cx+11} ${headCy-8}`}
                            stroke="#3F2D1E" strokeWidth="1.4" fill="none" strokeLinecap="round" />
                      {/* Naso */}
                      <path d={`M ${cx} ${headCy-1} Q ${cx-1.5} ${headCy+5} ${cx} ${headCy+6}`}
                            stroke={skinStroke} strokeWidth="0.9" fill="none" strokeLinecap="round" />
                      {/* Bocca */}
                      <path d={`M ${cx-5} ${headCy+12} Q ${cx} ${headCy+15} ${cx+5} ${headCy+12}`}
                            stroke="#B94A48" strokeWidth="1.4" fill="none" strokeLinecap="round" />

                      {/* Collo */}
                      <path d={`M ${cx-6} ${neckTop} Q ${cx-6} ${neckBot} ${cx-shoulderHalf/3} ${shoulderY}
                                 L ${cx+shoulderHalf/3} ${shoulderY} Q ${cx+6} ${neckBot} ${cx+6} ${neckTop} Z`}
                            fill="url(#skinG)" stroke={skinStroke} strokeWidth="0.8" />

                      {/* Torso con curve realistiche (spalla → petto → vita → fianco) */}
                      <path d={`
                        M ${cx-shoulderHalf} ${shoulderY}
                        Q ${cx-shoulderHalf-4} ${chestY} ${cx-shoulderHalf*0.9} ${chestY+10}
                        Q ${cx-waistHalf*1.1} ${(chestY+waistY)/2} ${cx-waistHalf} ${waistY}
                        Q ${cx-hipHalf*0.95} ${(waistY+hipY)/2} ${cx-hipHalf} ${hipY}
                        L ${cx+hipHalf} ${hipY}
                        Q ${cx+hipHalf*0.95} ${(waistY+hipY)/2} ${cx+waistHalf} ${waistY}
                        Q ${cx+waistHalf*1.1} ${(chestY+waistY)/2} ${cx+shoulderHalf*0.9} ${chestY+10}
                        Q ${cx+shoulderHalf+4} ${chestY} ${cx+shoulderHalf} ${shoulderY} Z
                      `}
                            fill={shirt} stroke="#334155" strokeWidth="1.1" strokeLinejoin="round" />

                      {/* Pancia gravidanza (semicerchio anatomico morbido) */}
                      {isPregnant && b > 0 && (
                        <ellipse cx={cx} cy={(waistY + hipY)/2 + 4}
                                 rx={Math.min(58, Math.max(28, b * 0.42))}
                                 ry={Math.min(46, Math.max(22, b * 0.34))}
                                 fill="#FBCFE8" stroke="#334155" strokeWidth="1.2" opacity="0.92" />
                      )}

                      {/* Braccio SX (curva umerale + avambraccio) */}
                      <path d={`
                        M ${cx-shoulderHalf+1} ${shoulderY+2}
                        Q ${cx-shoulderHalf-armThick*1.4} ${chestY+30} ${cx-shoulderHalf-armThick*0.6} ${waistY+8}
                        Q ${cx-shoulderHalf-armThick*0.2} ${waistY+50} ${cx-shoulderHalf+armThick*0.4} ${waistY+60}
                        L ${cx-shoulderHalf+armThick*1.1} ${waistY+58}
                        Q ${cx-shoulderHalf+armThick*0.8} ${waistY+8} ${cx-shoulderHalf+armThick} ${chestY+30}
                        Q ${cx-shoulderHalf+armThick} ${shoulderY+6} ${cx-shoulderHalf+armThick*0.6} ${shoulderY+2} Z
                      `}
                            fill="url(#skinG)" stroke={skinStroke} strokeWidth="0.9" />
                      {/* Mano SX */}
                      <ellipse cx={cx-shoulderHalf-armThick*0.1} cy={waistY+66}
                               rx={armThick*0.85} ry={armThick*1.0}
                               fill="url(#skinG)" stroke={skinStroke} strokeWidth="0.9" />

                      {/* Braccio DX */}
                      <path d={`
                        M ${cx+shoulderHalf-1} ${shoulderY+2}
                        Q ${cx+shoulderHalf+armThick*1.4} ${chestY+30} ${cx+shoulderHalf+armThick*0.6} ${waistY+8}
                        Q ${cx+shoulderHalf+armThick*0.2} ${waistY+50} ${cx+shoulderHalf-armThick*0.4} ${waistY+60}
                        L ${cx+shoulderHalf-armThick*1.1} ${waistY+58}
                        Q ${cx+shoulderHalf-armThick*0.8} ${waistY+8} ${cx+shoulderHalf-armThick} ${chestY+30}
                        Q ${cx+shoulderHalf-armThick} ${shoulderY+6} ${cx+shoulderHalf-armThick*0.6} ${shoulderY+2} Z
                      `}
                            fill="url(#skinG)" stroke={skinStroke} strokeWidth="0.9" />
                      <ellipse cx={cx+shoulderHalf+armThick*0.1} cy={waistY+66}
                               rx={armThick*0.85} ry={armThick*1.0}
                               fill="url(#skinG)" stroke={skinStroke} strokeWidth="0.9" />

                      {/* Gamba SX (coscia + polpaccio con curve) */}
                      <path d={`
                        M ${cx-hipHalf+2} ${hipY-2}
                        L ${cx-4} ${hipY}
                        L ${cx-6} ${kneeY}
                        Q ${cx-thighThick*0.5} ${(kneeY+ankleY)/2} ${cx-thighThick*0.65} ${ankleY}
                        L ${cx-hipHalf+thighThick*0.4} ${ankleY}
                        Q ${cx-hipHalf+thighThick*0.2} ${(kneeY+ankleY)/2} ${cx-hipHalf+thighThick*0.5} ${kneeY}
                        L ${cx-hipHalf} ${hipY+8} Z
                      `}
                            fill={pants} stroke="#1E293B" strokeWidth="1.0" strokeLinejoin="round" />
                      {/* Scarpa SX */}
                      <ellipse cx={cx-hipHalf/2-3} cy={ankleY+8} rx={thighThick*0.9} ry="6"
                               fill="#1F2937" stroke="#0F172A" strokeWidth="0.8" />

                      {/* Gamba DX */}
                      <path d={`
                        M ${cx+hipHalf-2} ${hipY-2}
                        L ${cx+4} ${hipY}
                        L ${cx+6} ${kneeY}
                        Q ${cx+thighThick*0.5} ${(kneeY+ankleY)/2} ${cx+thighThick*0.65} ${ankleY}
                        L ${cx+hipHalf-thighThick*0.4} ${ankleY}
                        Q ${cx+hipHalf-thighThick*0.2} ${(kneeY+ankleY)/2} ${cx+hipHalf-thighThick*0.5} ${kneeY}
                        L ${cx+hipHalf} ${hipY+8} Z
                      `}
                            fill={pants} stroke="#1E293B" strokeWidth="1.0" strokeLinejoin="round" />
                      <ellipse cx={cx+hipHalf/2+3} cy={ankleY+8} rx={thighThick*0.9} ry="6"
                               fill="#1F2937" stroke="#0F172A" strokeWidth="0.8" />

                      {/* Etichette misure (piccoli riferimenti clinici sui punti chiave) */}
                      <g fontSize="7.5" fill="#64748B" fontFamily="system-ui, sans-serif">
                        <text x={cx+shoulderHalf+8} y={shoulderY+3}>spalla</text>
                        <text x={cx+waistHalf+8} y={waistY+3}>vita {waistCm ? `${waistCm}cm` : ""}</text>
                        <text x={cx+hipHalf+8} y={hipY+3}>fianchi {hipCm ? `${hipCm}cm` : ""}</text>
                        <text x={cx-shoulderHalf-38} y={chestY+20}>braccio {armCm ? `${armCm}cm` : ""}</text>
                        <text x={cx-hipHalf-30} y={kneeY-4}>coscia {thighCm ? `${thighCm}cm` : ""}</text>
                      </g>
                    </svg>
                  );
                })()}
              </div>
              <p className="text-[10px] text-center text-slate-500 mt-1">
                Silhouette anatomica con curve proporzionali (vita/fianchi/braccio/coscia). Verrà usata per confrontare l'andamento nelle prossime visite.
              </p>
            </div>
          )}

          <div className="mt-4 pt-4 border-t border-emerald-200">
            <button type="button"
                    onClick={() => setBiaExpanded(!biaExpanded)}
                    className="w-full flex items-center justify-between px-3 py-2 rounded-lg bg-emerald-100/50 hover:bg-emerald-100 border border-emerald-200"
                    data-testid="bia-toggle">
              <div className="text-[13px] font-bold uppercase tracking-wider text-emerald-800 flex items-center gap-2">
                <Activity className="h-4 w-4" />
                Bioimpedenziometria (BIA)
                <span className="text-[10px] font-normal normal-case text-slate-600">
                  {biaExpanded ? "— compila se eseguita" : "— clicca per aprire (solo se eseguita)"}
                </span>
              </div>
              <ChevronDown className={`h-4 w-4 text-emerald-700 transition-transform ${biaExpanded ? "rotate-180" : ""}`} />
            </button>
            {biaExpanded && (
              <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
              <div><Label>Massa magra (kg)</Label>
                <Input type="number" step="0.1" value={biaLeanMassKg}
                       onChange={(e) => setBiaLeanMassKg(e.target.value)}
                       placeholder="55.2" data-testid="bia-lean-kg" /></div>
              <div><Label>Massa magra (%)</Label>
                <Input type="number" step="0.1" value={biaLeanMassPct}
                       onChange={(e) => setBiaLeanMassPct(e.target.value)}
                       placeholder="78" data-testid="bia-lean-pct" /></div>
              <div><Label>Massa grassa (kg)</Label>
                <Input type="number" step="0.1" value={biaFatMassKg}
                       onChange={(e) => setBiaFatMassKg(e.target.value)}
                       placeholder="15.5" data-testid="bia-fat-kg" /></div>
              <div><Label>Massa grassa (%)</Label>
                <Input type="number" step="0.1" value={biaFatMassPct}
                       onChange={(e) => setBiaFatMassPct(e.target.value)}
                       placeholder="22" data-testid="bia-fat-pct" /></div>
              <div><Label>Acqua totale (%)</Label>
                <Input type="number" step="0.1" value={biaTbwPct}
                       onChange={(e) => setBiaTbwPct(e.target.value)}
                       placeholder="58" data-testid="bia-tbw" /></div>
              <div><Label>Angolo di fase (°)</Label>
                <Input type="number" step="0.1" value={biaPhaseAngle}
                       onChange={(e) => setBiaPhaseAngle(e.target.value)}
                       placeholder="6.5" data-testid="bia-phase-angle" /></div>
              <div><Label>Massa muscolare (kg)</Label>
                <Input type="number" step="0.1" value={biaMuscleMassKg}
                       onChange={(e) => setBiaMuscleMassKg(e.target.value)}
                       placeholder="32" data-testid="bia-muscle-kg" /></div>
            </div>
            )}
          </div>

          <div className="mt-4">
            <label className={`flex items-start gap-2 p-2.5 rounded-lg border cursor-pointer ${
              showWeightChart ? "bg-emerald-100 border-emerald-400" : "bg-white border-slate-200"
            }`}>
              <input type="checkbox" checked={showWeightChart}
                     onChange={(e) => { setShowWeightChart(e.target.checked); if (e.target.checked) loadWeightHistory(); }}
                     className="mt-0.5 h-4 w-4 accent-emerald-600"
                     data-testid="show-weight-chart-toggle-v2" />
              <div className="text-[13px] leading-snug">
                <span className="font-semibold text-slate-900 flex items-center gap-1.5">
                  <TrendingUp className="h-3.5 w-3.5 text-emerald-600" /> Valuta andamento del peso
                </span>
                <p className="text-slate-600 mt-0.5 text-[11px]">
                  Carica peso dei referti precedenti + grafico con curva teorica del piano.
                </p>
              </div>
            </label>
          </div>
        </Card>
      )}

      {/* -------- Card 3: Visita e conclusioni -------- */}
      <Card id="sec-visita" className="p-4 md:p-6 rounded-2xl border-blue-200 shadow-sm bg-blue-50/40 mt-5
                       [&_label]:text-slate-700 [&_input]:bg-white [&_input]:text-slate-900
                       [&_input]:border-slate-300 [&_textarea]:bg-white [&_textarea]:text-slate-900
                       [&_textarea]:border-slate-300">
        <div className="flex items-center gap-2 mb-4">
          <Activity className="h-5 w-5 text-blue-600" />
          <h2 className="font-heading text-lg font-bold text-slate-900">
            {isNutritionist ? "Valutazione della visita nutrizionale" : "Visita e conclusioni"}
          </h2>
        </div>
        <div className="space-y-4">
          {/* Motivo visita: per lo specialista è nella card dedicata più in alto */}
          <VisitBlock plain={isNutritionist} n={1} title="Documentazione clinica presentata" hint="esami e referti portati dal paziente, con lettura AI degli allegati" testid="visit-block-documentazione">
          <div>
            <div className="flex items-center justify-between gap-2 mb-1">
              <Label>Documentazione clinica presentata (opz.)</Label>
              {attachments.length > 0 && (
                <Button type="button" size="sm" variant="outline"
                        onClick={scanAttachedExamsWithAI} disabled={ocrBusy}
                        data-testid="referto-scan-exams-ai"
                        className="h-8 text-xs border-emerald-300 text-emerald-800 bg-emerald-50/70 hover:bg-emerald-100">
                  {ocrBusy
                    ? <><SynapseLoader size="sm" inline className="mr-1.5" /> Scansione…</>
                    : <><Sparkles className="h-3.5 w-3.5 mr-1.5" /> Scansiona esami allegati con AI</>}
                </Button>
              )}
            </div>
            <Textarea rows={4} value={presentedDocumentation}
                      onChange={(e) => setPresentedDocumentation(e.target.value)}
                      placeholder="Es. porta ECG del 10/09/2025 (ritmo sinusale FC 68), esami ematochimici recenti nella norma, precedente ecocolordoppler carotideo ok…"
                      data-testid="referto-presented-doc" />
            <p className="text-[11px] text-slate-500 mt-1">
              Descrivi esami/referti che il paziente porta in visita. Se alleghi PDF o foto qui sotto,
              premi <strong>“Scansiona esami allegati con AI”</strong>: l'AI legge i file e riporta
              qui i valori principali con le unità di misura.
            </p>

            {/* -------- Allega esami / scansioni (con OCR AI) -------- */}
            <div className="mt-4 rounded-xl border border-dashed border-emerald-300 bg-emerald-50/40 p-3">
              <div className="flex items-center gap-2 mb-1">
                <Paperclip className="h-4 w-4 text-emerald-700" />
                <Label className="text-slate-900 font-semibold text-[13px]">
                  Allega esami / scansioni (opz.)
                </Label>
              </div>
              <p className="text-[11px] text-slate-600 mb-2">
                PDF di laboratorio, foto ECG, ecografie, dermoscopia… <strong>Non vengono acclusi al
              referto PDF</strong>: servono solo come sorgente per l'AI che estrae i valori e li
              inserisce in <em>Documentazione clinica presentata</em>. Max <strong>{MAX_TOTAL_MB} MB</strong> totali.
              </p>

              <label className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 cursor-pointer text-sm text-slate-700"
                     data-testid="referto-attach-label">
                <Paperclip className="h-4 w-4" />
                {attachBusy ? "Caricamento…" : "Scegli file (immagini o PDF)"}
                <input type="file" multiple accept="image/*,application/pdf"
                       onChange={onAttachFiles} disabled={attachBusy}
                       data-testid="referto-attach-input"
                       className="hidden" />
              </label>

              {attachments.length > 0 && (
                <div className="mt-2 rounded-lg border border-slate-200 bg-white divide-y divide-slate-100"
                     data-testid="referto-attach-list">
                  {attachments.map((a, i) => (
                    <div key={i} className="flex items-center gap-3 px-3 py-2">
                      <div className={`h-8 w-8 rounded-md flex items-center justify-center flex-shrink-0 ${
                        a.mime_type.startsWith("image/") ? "bg-emerald-50 text-emerald-600" : "bg-blue-50 text-blue-600"
                      }`}>
                        {a.mime_type.startsWith("image/") ? <ImageIcon className="h-4 w-4" /> : <FileText className="h-4 w-4" />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-slate-900 truncate">{a.filename}</div>
                        <div className="text-[11px] text-slate-500">
                          {a.mime_type.startsWith("image/") ? "Immagine" : "PDF"} · {fmtSize(a.size)}
                        </div>
                      </div>
                      <button type="button" onClick={() => removeAttachment(i)}
                              data-testid={`referto-attach-remove-${i}`}
                              className="p-1.5 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                  <div className="px-3 py-1.5 text-[11px] text-slate-500 bg-slate-50">
                    Totale: <strong>{fmtSize(attachmentsSize)}</strong> / {MAX_TOTAL_MB} MB
                  </div>
                </div>
              )}
            </div>
          </div>
          </VisitBlock>
          <VisitBlock plain={isNutritionist} n={2} title="Esame obiettivo" hint="compila con i moduli del modulo visita oppure scrivi tutto manualmente" testid="visit-block-eo">
          {isNutritionist ? (
            <div>
              <div className="flex items-center justify-between gap-2 mb-1">
                <Label>Valutazione dello stato nutrizionale <span className="text-red-500">*</span></Label>
                <Button
                  type="button" size="sm" variant="outline"
                  onClick={async () => {
                    try {
                      const patientAge = pbirth ? calcAge(pbirth) : null;
                      const md = (VISIT_MOTIVES.find(m => m.id === visitMotive)?.questions || [])
                        .map(q => (motiveAnswers[q.id] || "").trim() ? `${q.label}: ${motiveAnswers[q.id].trim()}` : "")
                        .filter(Boolean).join("; ");
                      toast.info("Genero il commento professionale con l'AI…");
                      const { data } = await api.post("/ai/nutrition-clinical-summary", {
                        patient_first_name: pfirst.trim(),
                        patient_last_name: plast.trim(),
                        age: patientAge,
                        gender: psex || "",
                        visit_motive_label: VISIT_MOTIVES.find(m => m.id === visitMotive)?.label || "",
                        motive_details: md,
                        family_history: (familyHistory || "").trim(),
                        personal_conditions: (personalConditions || "").trim(),
                        current_medications: (currentMedications || "").trim(),
                        allergies: (allergies || "").trim(),
                        food_habits: foodHabits,
                        food_habits_notes: foodHabitsNotes.trim(),
                        lifestyle_habits: lifestyleHabits,
                        lifestyle_habits_notes: lifestyleHabitsNotes.trim(),
                      });
                      const summary = (data.summary || "").trim();
                      if (summary) {
                        setNutritionalStateEvaluation(summary);
                        toast.success("Commento professionale generato — modificabile");
                      } else {
                        toast.warning("Nessun commento generato — riprova");
                      }
                    } catch (e) {
                      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore generazione commento");
                    }
                  }}
                  data-testid="nut-clinical-summary-ai"
                  className="border-purple-500 bg-white text-purple-700 hover:bg-purple-50 h-8 text-[11px]"
                >
                  <Sparkles className="h-3.5 w-3.5 mr-1" /> Genera commento AI
                </Button>
              </div>
              <Textarea rows={4} value={nutritionalStateEvaluation}
                        onChange={(e) => setNutritionalStateEvaluation(e.target.value)}
                        placeholder="Stato di idratazione, composizione corporea osservata, tono muscolare, ritenzione, aspetto cute/mucose, sintomi correlati all'alimentazione (gonfiore, digestione, energia)… — oppure clicca 'Genera commento AI' per un'analisi interpretativa automatica del motivo visita + anamnesi + abitudini."
                        data-testid="referto-nutritional-state" />
            </div>
          ) : (
            <div>
              <div className="flex items-center justify-between gap-2 mb-1">
                <Label>Esame obiettivo / rilevazioni <span className="text-red-500">*</span></Label>
                {gynVisitModule && (
                  <div className="flex items-center gap-2 flex-wrap justify-end">
                    <div className="inline-flex rounded-md border border-blue-300 overflow-hidden text-[11px]" data-testid="eo-mode-toggle">
                      <button type="button" onClick={() => setEoMode("moduli")} data-testid="eo-mode-moduli"
                              className={`px-2.5 py-1 ${eoMode === "moduli" ? "bg-[#1E88E5] text-white" : "bg-white text-slate-700 hover:bg-blue-50"}`}>Compila con moduli</button>
                      <button type="button" onClick={() => setEoMode("manuale")} data-testid="eo-mode-manuale"
                              className={`px-2.5 py-1 border-l border-blue-300 ${eoMode === "manuale" ? "bg-[#1E88E5] text-white" : "bg-white text-slate-700 hover:bg-blue-50"}`}>Inserimento manuale</button>
                    </div>
                    {lastVisitStruct && eoMode === "moduli" && (
                      <Button type="button" size="sm" variant="outline" onClick={copyLastVisit} data-testid="copy-last-visit-btn"
                              className="h-7 text-[11px] border-blue-300 text-blue-800 bg-white hover:bg-blue-50">
                        <History className="h-3 w-3 mr-1" /> Copia ultima visita ({new Date(lastVisitStruct.created_at).toLocaleDateString("it-IT")})
                      </Button>
                    )}
                    <span className="text-[11px] px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 border border-blue-200" data-testid="visit-module-badge">
                      {VISIT_MODULES[gynVisitModule].title}
                    </span>
                  </div>
                )}
              </div>
              {gynVisitModule && eoMode === "moduli" && (
                <div className="mb-2"><GynVisitBlock moduleId={gynVisitModule} groupKeys={["eo"]} values={gynVisit} onChange={setGynVisit} /></div>
              )}
              {isDerma && (
                <div className="mb-2">
                  {!showBodyMap && bodyMarkers.length === 0 ? (
                    <Button type="button" size="sm" variant="outline" onClick={() => setShowBodyMap(true)} data-testid="body-map-toggle"
                            className="h-8 text-xs border-blue-900 text-blue-900 bg-white hover:bg-blue-50">
                      <MapPin className="h-3.5 w-3.5 mr-1" /> Mappa corporea — segna le sedi delle lesioni (opz.)
                    </Button>
                  ) : (
                    <div className="space-y-1.5">
                      <BodyMap value={bodyMarkers} onChange={setBodyMarkers} photoCountByMarker={dermaPhotos.reduce((acc, p) => { if (p.body_marker) acc[p.body_marker] = (acc[p.body_marker] || 0) + 1; return acc; }, {})} />
                      <button type="button" onClick={() => { setBodyMarkers([]); setShowBodyMap(false); }} data-testid="body-map-remove" className="text-[11px] text-slate-500 underline">Rimuovi mappa corporea</button>
                    </div>
                  )}
                </div>
              )}
              <Textarea rows={gynVisitModule && eoMode === "moduli" ? 2 : 5} value={observations} onChange={(e) => setObservations(e.target.value)}
                        placeholder={gynVisitModule && eoMode === "moduli" ? "Note / reperti aggiuntivi dell'esame obiettivo (opz.)…" : "Esame obiettivo completo in testo libero: PA, FC, ispezione, speculum, palpazione bimanuale, reperti…"}
                        data-testid="referto-observations" /></div>
          )}
          </VisitBlock>
          {!isNutritionist && (
          <VisitBlock plain={isNutritionist} n={3} title="Esami strumentali" hint="seleziona il tipo di esame e compila i reperti" testid="visit-block-strumentali">
            <div><Label>Esami strumentali (opz.)</Label>
              <div className="mt-1 mb-2"><InstrumentalExamsBuilder value={instrStruct} onChange={setInstrStruct} types={instrTypes} /></div>
              {gynVisitModule && <div className="mt-1 mb-2"><GynVisitBlock moduleId={gynVisitModule} groupKeys={["strum"]} values={gynVisit} onChange={setGynVisit} /></div>}
              {isOrl && (
                <div className="mt-1 mb-2">
                  {audiogram === null ? (
                    <Button type="button" size="sm" variant="outline" onClick={() => setAudiogram({ dx: {}, sx: {} })} data-testid="audiogram-add-btn"
                            className="h-8 text-xs border-violet-300 text-violet-800 bg-white hover:bg-violet-50">
                      <LineChartIcon className="h-3.5 w-3.5 mr-1" /> Aggiungi audiogramma grafico (opz.)
                    </Button>
                  ) : (
                    <div className="space-y-1.5">
                      <AudiogramEditor value={audiogram} onChange={setAudiogram} previous={prevAudiogram} compare={audiogramCompare} onToggleCompare={setAudiogramCompare} />
                      <button type="button" onClick={() => setAudiogram(null)} data-testid="audiogram-remove-btn" className="text-[11px] text-slate-500 underline">Rimuovi audiogramma</button>
                    </div>
                  )}
                </div>
              )}
              {isDerma && (
                <div className="mt-1 mb-2">
                  <DermaPhotos patientFiscalCode={pfiscalCode} photos={dermaPhotos} onChange={setDermaPhotos} siteSuggestions={bodyMarkers.map((m) => m.label)} markers={bodyMarkers} />
                </div>
              )}
              <Textarea rows={2} value={instrumentalExams}
                        onChange={(e) => setInstrumentalExams(e.target.value)}
                        placeholder="Note / reperti liberi su altri esami strumentali…"
                        data-testid="referto-instrumental" />
              <p className="text-[11px] text-slate-500 mt-1">
                Puoi anche allegare l'esito strumentale (immagine/PDF) nella sezione allegati più sotto.
              </p></div>
          </VisitBlock>
          )}
          {!isNutritionist && (
          <VisitBlock plain={isNutritionist} n={4} title="Valutazione e diagnosi" hint="con suggerimenti AI basati su motivo, anamnesi, EO ed esami" testid="visit-block-diagnosi">
            <div>
              <div className="flex items-center justify-between gap-2 mb-1">
                <Label>Valutazione / diagnosi (opz.)</Label>
                <Button type="button" size="sm" variant="outline" onClick={suggestDiagnosisAI} disabled={aiDxBusy}
                        data-testid="ai-suggest-diagnosis"
                        className="h-7 text-[11px] border-purple-300 text-purple-800 bg-purple-50/70 hover:bg-purple-100">
                  {aiDxBusy ? <><SynapseLoader size="sm" inline className="mr-1.5" /> Analizzo…</> : <><Sparkles className="h-3 w-3 mr-1" /> Suggerimenti AI</>}
                </Button>
              </div>
              {gynVisitModule && <div className="mt-1 mb-2"><GynVisitBlock moduleId={gynVisitModule} groupKeys={["diag"]} values={gynVisit} onChange={setGynVisit} /></div>}
              <Textarea rows={3} value={diagnosis} onChange={(e) => setDiagnosis(e.target.value)}
                        placeholder="Sintesi diagnostica alla luce delle rilevazioni… — oppure premi 'Suggerimenti AI' per orientamento diagnostico e differenziali."
                        data-testid="referto-diagnosis" /></div>
          </VisitBlock>
          )}

          <VisitBlock plain={isNutritionist} n={5} title="Esami consigliati" hint="collegamento al MMG per la prescrizione" testid="visit-block-esami">
          <div>
            <div className="flex items-center justify-between gap-2 mb-1">
              <Label>Esami consigliati (opz.)</Label>
              <div className="flex items-center gap-2">
                {isDoctor && recommendedExams.trim() && (
                  <label className={`flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-md border cursor-pointer transition-colors ${
                    examsToMmg ? "bg-[#DBEAFE] border-[#1E3A8A] text-[#1E3A8A]" : "bg-white border-slate-300 text-slate-700 hover:border-[#1E3A8A]"}`}
                         data-testid="exams-to-mmg-label">
                    <input type="checkbox" checked={examsToMmg} onChange={(e) => setExamsToMmg(e.target.checked)}
                           className="h-3 w-3 accent-[#1E3A8A]" data-testid="exams-to-mmg" />
                    <Send className="h-3 w-3" /> Invia al MMG per prescrizione
                  </label>
                )}
                {isNutritionist && (
                  <label className={`flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-md border cursor-pointer transition-colors ${
                    aiSuggestExamsBusy
                      ? "bg-purple-100 border-purple-400 text-purple-700"
                      : "bg-white border-purple-400 text-purple-700 hover:bg-purple-50"
                  }`}
                         data-testid="ai-suggest-exams-label">
                    <input type="checkbox"
                           checked={aiSuggestExamsBusy}
                           disabled={aiSuggestExamsBusy}
                           onChange={async (e) => {
                             if (!e.target.checked || aiSuggestExamsBusy) return;
                             setAiSuggestExamsBusy(true);
                             try {
                               const patientAge = pbirth ? calcAge(pbirth) : null;
                               const md = (VISIT_MOTIVES.find(m => m.id === visitMotive)?.questions || [])
                                 .map(q => (motiveAnswers[q.id] || "").trim() ? `${q.label}: ${motiveAnswers[q.id].trim()}` : "")
                                 .filter(Boolean).join("; ");
                               const { data } = await api.post("/ai/nutrition-suggest-exams", {
                                 visit_motive_label: VISIT_MOTIVES.find(m => m.id === visitMotive)?.label || "",
                                 motive_details: md,
                                 previous_screenings: (previousScreenings || "").trim(),
                                 presented_documentation: (presentedDocumentation || "").trim(),
                                 family_history: (familyHistory || "").trim(),
                                 personal_conditions: (personalConditions || "").trim(),
                                 current_medications: (currentMedications || "").trim(),
                                 allergies: (allergies || "").trim(),
                                 age: patientAge,
                                 gender: psex || "",
                                 food_habits: foodHabits,
                                 lifestyle_habits: lifestyleHabits,
                               });
                               const sugg = (data.suggested_exams || "").trim();
                               if (sugg) {
                                 // Se già presenti esami, chiedi conferma prima di sovrascrivere
                                 if (recommendedExams.trim() &&
                                     !window.confirm("Sostituisco gli esami attuali con quelli suggeriti dall'AI?\n(Puoi comunque modificarli dopo)")) {
                                   toast.info("Suggerimento annullato — esami mantenuti");
                                 } else {
                                   setRecommendedExams(sugg);
                                   toast.success("Esami suggeriti dall'AI — modificabili");
                                 }
                               } else {
                                 toast.warning("Nessun suggerimento generato");
                               }
                             } catch (er) {
                               toast.error(formatApiErrorDetail(er.response?.data?.detail) || "Errore suggerimento esami");
                             } finally {
                               setAiSuggestExamsBusy(false);
                             }
                           }}
                           className="h-3 w-3 accent-purple-600"
                           data-testid="ai-suggest-exams" />
                    <Sparkles className="h-3 w-3" />
                    {aiSuggestExamsBusy ? "Analizzo…" : "Suggerisci con AI"}
                  </label>
                )}
                {isNutritionist && recommendedExams.trim() && (
                  <label className={`flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-md border cursor-pointer transition-colors ${
                    (mmgCompletionExams.trim() && mmgCompletionExams.trim() === recommendedExams.trim())
                      ? "bg-[#DBEAFE] border-[#1E3A8A] text-[#1E3A8A]"
                      : "bg-white border-slate-300 text-slate-700 hover:border-[#1E3A8A]"
                  }`}
                         data-testid="copy-exams-to-mmg-label">
                    <input type="checkbox"
                           checked={mmgCompletionExams.trim() === recommendedExams.trim() && !!recommendedExams.trim()}
                           onChange={(e) => {
                             if (e.target.checked) {
                               setMmgCompletionExams(recommendedExams.trim());
                               toast.success("Esami copiati nella lettera al MMG");
                             } else {
                               setMmgCompletionExams("");
                             }
                           }}
                           className="h-3 w-3 accent-[#1E3A8A]"
                           data-testid="copy-exams-to-mmg" />
                    <FileText className="h-3 w-3" />
                    Copia nella lettera al MMG
                  </label>
                )}
              </div>
            </div>
            {gynVisitModule && <div className="mt-1 mb-2"><GynVisitBlock moduleId={gynVisitModule} groupKeys={["esami"]} values={gynVisit} onChange={setGynVisit} /></div>}
            <Textarea rows={2} value={recommendedExams}
                      onChange={(e) => setRecommendedExams(e.target.value)}
                      placeholder="Es. Emocromo, TSH, ecocardio, ECG holter 24h… — oppure spunta 'Suggerisci con AI' per un elenco personalizzato in base al quadro clinico."
                      data-testid="referto-recommended-exams" />
          </div>
          </VisitBlock>

          {!isNutritionist && (
          <VisitBlock plain={isNutritionist} n={6} title="Terapia consigliata" hint="collegamento al MMG per la prescrizione" testid="visit-block-terapia">
            <div>
              <div className="flex items-center justify-between gap-2 mb-1">
                <Label>Terapia consigliata (opz.)</Label>
                {therapy.trim() && (
                  <label className={`flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-md border cursor-pointer transition-colors ${
                    therapyToMmg ? "bg-[#DBEAFE] border-[#1E3A8A] text-[#1E3A8A]" : "bg-white border-slate-300 text-slate-700 hover:border-[#1E3A8A]"}`}
                         data-testid="therapy-to-mmg-label">
                    <input type="checkbox" checked={therapyToMmg} onChange={(e) => setTherapyToMmg(e.target.checked)}
                           className="h-3 w-3 accent-[#1E3A8A]" data-testid="therapy-to-mmg" />
                    <Send className="h-3 w-3" /> Invia al MMG per prescrizione
                  </label>
                )}
              </div>
              {gynTerInTherapy && (
                <div className="mt-1 mb-2"><GynVisitBlock moduleId={gynVisitModule} groupKeys={["ter"]} values={gynVisit} onChange={setGynVisit} /></div>
              )}
              <Textarea rows={2} value={therapy}
                        onChange={(e) => setTherapy(e.target.value)}
                        placeholder="Es. Ramipril 5mg 1cp/die, dieta iposodica, attività fisica moderata…"
                        data-testid="referto-therapy" />
              {(examsToMmg || therapyToMmg) && (
                <p className="text-[11px] text-[#1E3A8A] bg-[#DBEAFE]/60 rounded px-2 py-1.5 mt-2 flex items-start gap-1.5">
                  <Send className="h-3 w-3 mt-0.5 flex-shrink-0" />
                  <span>Richiesta di prescrizione precompilata: seleziona il MMG del paziente nel riquadro "Comunicazioni" in fondo.</span>
                </p>
              )}
            </div>
          </VisitBlock>
          )}

          <VisitBlock plain={isNutritionist} n={7} title="Indicazioni" testid="visit-block-indicazioni">
          <div><Label>Indicazioni e raccomandazioni (opz.)</Label>
            {gynVisitModule && !gynTerInTherapy && (
              <div className="mt-1 mb-2"><GynVisitBlock moduleId={gynVisitModule} groupKeys={["ter"]} values={gynVisit} onChange={setGynVisit} /></div>
            )}
            <Textarea rows={2} value={recommendations} onChange={(e) => setRecommendations(e.target.value)}
                      placeholder="Indicazioni comportamentali, altre raccomandazioni…"
                      data-testid="referto-recommendations" /></div>
          </VisitBlock>
          <VisitBlock plain={isNutritionist} n={8} title="Prossimo controllo" testid="visit-block-controllo">
          <div><Label>Prossimo controllo (opz.)</Label>
            {gynVisitModule && <div className="mt-1 mb-2"><GynVisitBlock moduleId={gynVisitModule} groupKeys={["fu"]} values={gynVisit} onChange={setGynVisit} /></div>}
            <Input value={nextCheck} onChange={(e) => setNextCheck(e.target.value)}
                   placeholder="Es. tra 6 mesi · Ricontattare la segretaria per riprogrammare tra 5 mesi · Controllo a 3 mesi con nuovi esami"
                   data-testid="referto-next-check" />
            <p className="text-[11px] text-slate-500 mt-1">
              Puoi scrivere una data ("15/09/2026"), un intervallo ("a 6 mesi") o indicazioni operative ("fissare con la segretaria fra 3 mesi").
            </p>
          </div>
          </VisitBlock>
        </div>

        <label className={`mt-4 flex items-start gap-2 p-3 rounded-lg border cursor-pointer transition-all ${
          useAi ? "bg-emerald-50 border-emerald-300" : "bg-white border-slate-200"
        }`}>
          <input type="checkbox" checked={useAi} onChange={(e) => setUseAi(e.target.checked)}
                 className="mt-0.5 h-4 w-4 accent-emerald-600" data-testid="referto-use-ai" />
          <div className="text-[13px] leading-snug">
            <span className="font-semibold text-slate-900 flex items-center gap-1.5">
              <Sparkles className="h-3.5 w-3.5 text-emerald-600" /> Rifinisci con AI (italiano clinico chiaro)
            </span>
            <p className="text-slate-600 mt-0.5">L'AI riscriverà in italiano medico professionale ogni voce del referto: motivo, modulo della visita, anamnesi, esame obiettivo, esami strumentali, diagnosi, esami consigliati, terapia, indicazioni e prossimo controllo — senza aggiungere fatti o diagnosi non presenti.</p>
          </div>
        </label>

        <div className="mt-4">
          <VisitBlock plain={isNutritionist} n={9} title="Firma" testid="visit-block-firma">
          <Label className="mb-1 block">Firma digitale del medico</Label>
          {user?.signature_url && useProfileSig ? (
            <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-3 flex items-center gap-3">
              <div className="h-16 w-40 rounded bg-white border border-slate-200 flex items-center justify-center flex-shrink-0">
                <img src={user.signature_url} alt="Firma salvata" className="max-h-full max-w-full object-contain" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-emerald-800 flex items-center gap-1">
                  <ShieldCheck className="h-4 w-4" /> Firma dal profilo (usata automaticamente)
                </div>
                <button type="button" onClick={() => setUseProfileSig(false)}
                        data-testid="referto-override-signature"
                        className="text-xs text-emerald-700 hover:text-emerald-900 underline mt-0.5">
                  Firma diversa per questo referto
                </button>
              </div>
            </div>
          ) : (
            <>
              <SignaturePad value={signature} onChange={setSignature} height={140} />
              {user?.signature_url && (
                <button type="button" onClick={() => { setUseProfileSig(true); setSignature(null); }}
                        className="mt-1 text-xs text-slate-600 hover:text-slate-900 underline">
                  ← Usa la firma salvata nel profilo
                </button>
              )}
            </>
          )}
          <p className="text-[11px] text-slate-500 mt-1">
            {user?.signature_url
              ? "Puoi ridisegnare la firma solo per questo referto, o modificarla definitivamente da Impostazioni."
              : "Suggerimento: salva la firma da Impostazioni per non ridisegnarla ogni volta."}
          </p>
          </VisitBlock>
        </div>

      </Card>

      {/* -------- Card 4: Comunicazioni collegate (per nutrizionista: contenitore trasparente,
           i figli hanno ognuno il proprio riquadro distinto) -------- */}
      <Card id="sec-comunicazioni" className={`p-4 md:p-6 rounded-2xl shadow-sm mt-5
                       [&_label]:text-slate-700 [&_input]:bg-white [&_input]:text-slate-900
                       [&_input]:border-slate-300 [&_textarea]:bg-white [&_textarea]:text-slate-900
                       [&_textarea]:border-slate-300 ${
                         isNutritionist
                           ? "bg-transparent border-none shadow-none p-0 md:p-0"
                           : "border-amber-200 bg-amber-50/40"
                       }`}>
        {!isNutritionist && (
          <div className="flex items-center gap-2 mb-4">
            <Send className="h-5 w-5 text-blue-600" />
            <h2 className="font-heading text-lg font-bold text-slate-900">
              Comunicazioni con MMG e altri specialisti
            </h2>
          </div>
        )}

        <div>
        {/* Prescrizione MMG e consulto specialista: solo per medici, nascosti al nutrizionista */}
        {!isNutritionist && (<>
          <label className={`flex items-start gap-2 p-3 rounded-lg border cursor-pointer transition-all ${
            sendPrescription ? "bg-[#DBEAFE] border-[#1E3A8A]/40" : "bg-white border-slate-200 hover:border-slate-300"
          }`} data-testid="prescription-toggle-wrap">
            <input type="checkbox" checked={sendPrescription}
                   onChange={(e) => setSendPrescription(e.target.checked)}
                   className="mt-0.5 h-4 w-4 accent-[#1E3A8A]"
                   data-testid="prescription-toggle" />
            <div className="text-[13px] leading-snug">
              <span className="font-semibold text-slate-900 flex items-center gap-1.5">
                <Send className="h-3.5 w-3.5 text-[#1E3A8A]" /> Invia anche prescrizione al MMG del paziente
              </span>
              <p className="text-slate-600 mt-0.5">
                Prima di emettere il referto, invia al medico di base una richiesta di prescrizione
                per esami/farmaci. L'email della paziente sarà inclusa così il MMG può inviare la
                ricetta direttamente. La stessa informazione apparirà nel referto PDF.
              </p>
            </div>
          </label>

          {sendPrescription && (
            <div className="mt-3 pl-2 border-l-2 border-[#1E3A8A]/30 space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <select value={mmgProvince} onChange={(e) => setMmgProvince(e.target.value)}
                          data-testid="referto-mmg-province"
                          className="h-10 w-full pl-9 pr-3 rounded-md border border-slate-300 bg-white text-sm text-slate-900">
                    <option value="all">Tutte le province</option>
                    {provinces.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input value={mmgQuery} onChange={(e) => setMmgQuery(e.target.value)}
                         placeholder="Cerca MMG per cognome…" className="pl-9"
                         data-testid="referto-mmg-search" />
                </div>
              </div>

              {selectedMmg ? (
                <div className="rounded-lg border border-[#1E3A8A]/30 bg-[#DBEAFE]/60 p-3 flex items-center justify-between">
                  <div>
                    <div className="text-xs text-[#1E3A8A] font-semibold uppercase tracking-wide">MMG selezionato</div>
                    <div className="font-bold text-slate-900">{selectedMmg.name || `${selectedMmg.first_name || ""} ${selectedMmg.last_name || ""}`.trim()}</div>
                    <div className="text-xs text-slate-500">
                      {selectedMmg.order_province || "—"} · {selectedMmg.email}
                    </div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => setSelectedMmg(null)}
                          className="border-slate-300 bg-white text-slate-700 hover:bg-slate-50">
                    Cambia
                  </Button>
                </div>
              ) : hasSearch ? (
                <div className="max-h-48 overflow-y-auto border border-slate-200 rounded-lg bg-white">
                  {mmgResults.length === 0 && <div className="p-3 text-sm text-slate-400">Nessun MMG trovato</div>}
                  {mmgResults.map((c) => (
                    <button key={c.id} onClick={() => setSelectedMmg(c)}
                            data-testid={`referto-mmg-${c.id}`}
                            className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-b-0 flex items-center justify-between">
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-slate-900 truncate">
                          {c.name || `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.email}
                        </div>
                        <div className="text-xs text-slate-500 truncate">
                          Medicina generale · {c.order_province || "—"}
                        </div>
                      </div>
                      <Badge variant="secondary" className="text-[10px]">Seleziona</Badge>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-[11px] text-slate-500 italic">
                  Digita un cognome o seleziona una provincia per trovare il MMG del paziente.
                </div>
              )}

              <div>
                <Label>Esami / farmaci da prescrivere</Label>
                <Textarea rows={4} value={prescriptionText}
                          onChange={(e) => setPrescriptionText(e.target.value)}
                          placeholder="Es. Emocromo completo, TSH, colesterolo HDL/LDL, ECG.  Oppure: Ramipril 5mg, 30 cpr."
                          data-testid="referto-prescription-text" />
                <p className="text-[11px] text-slate-500 mt-1">
                  Verrà inviato al MMG con: nome/DOB paziente, elenco esami, ed email paziente per l'invio diretto della ricetta.
                </p>
              </div>

              {/* Anteprima lettera AI al MMG (usa esami+terapia dal referto) */}
              {selectedMmg && (recommendedExams.trim() || therapy.trim()) && (
                <div className="flex items-center justify-between gap-2 bg-white border border-[#1E3A8A]/30 rounded-lg px-3 py-2">
                  <p className="text-[11px] text-[#1E3A8A] flex-1">
                    L'AI comporrà una <strong>lettera clinica</strong> con riassunto della visita
                    + esami / terapia consigliata. Il MMG riceve tutto via Synappsi + email.
                  </p>
                  <Button type="button" size="sm"
                          onClick={previewMmgLetterAI} disabled={aiPreviewBusy}
                          data-testid="ai-mmg-preview-btn"
                          className="bg-[#1E3A8A] text-white hover:bg-[#1E40AF] h-8 text-xs flex-shrink-0">
                    {aiPreviewBusy ? <><SynapseLoader size="sm" inline className="mr-1" /> …</>
                                   : <><Eye className="h-3 w-3 mr-1" /> Anteprima comunicazione MMG</>}
                  </Button>
                </div>
              )}
            </div>
          )}
        </>)}
        </div>

        {/* -------- Piano nutrizionale AI (solo biologo nutrizionista) --------
             Riquadro distinto VERDE per il piano AI. */}
        {isNutritionist && (
          <Card id="sec-piano" className="p-4 md:p-6 rounded-2xl border-emerald-300 shadow-sm bg-emerald-50/30 mt-5
                           [&_label]:text-slate-700 [&_input]:bg-white [&_input]:text-slate-900
                           [&_input]:border-slate-300 [&_textarea]:bg-white [&_textarea]:text-slate-900
                           [&_textarea]:border-slate-300"
                data-testid="card-nutrition-plan">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="h-5 w-5 text-emerald-600" />
              <h2 className="font-heading text-lg font-bold text-slate-900">
                Piano nutrizionale con AI
              </h2>
              <span className="text-[10px] uppercase tracking-wider text-emerald-700 bg-emerald-100 border border-emerald-200 rounded-full px-2 py-0.5">
                Novità
              </span>
            </div>
            <p className="text-xs text-slate-600 mb-3">
              L'AI prepara la <strong>settimana tipo con 2-3 varianti per pasto</strong>, alimenti consigliati,
              da evitare e — se il motivo è intolleranze o disturbi gastrointestinali — un piano progressivo
              in 3 fasi. Il tutto in base a peso, altezza, età, motivo e attività. Modifica liberamente prima
              del PDF.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
              <div>
                <Label>Livello di attività fisica</Label>
                <select
                  value={nutritionActivity}
                  onChange={(e) => setNutritionActivity(e.target.value)}
                  data-testid="nut-activity-level"
                  className="h-10 w-full px-3 rounded-md border border-slate-300 bg-white text-sm text-slate-900"
                >
                  <option value="sedentary">Sedentaria (lavoro d'ufficio)</option>
                  <option value="light">Leggera (1-3 giorni/settimana)</option>
                  <option value="moderate">Moderata (3-5 giorni/settimana)</option>
                  <option value="active">Intensa (6-7 giorni/settimana)</option>
                  <option value="very_active">Molto intensa / atleta</option>
                </select>
              </div>
              <div>
                <Label>Target kcal/die (opz., sovrascrive AI)</Label>
                <Input type="number" value={nutritionOverrideKcal}
                       onChange={(e) => setNutritionOverrideKcal(e.target.value)}
                       placeholder="Auto (calcolato)"
                       data-testid="nut-override-kcal" />
              </div>
              <div>
                <Label>Note / allergie / preferenze</Label>
                <Input value={nutritionNotes}
                       onChange={(e) => setNutritionNotes(e.target.value)}
                       placeholder="Es. allergia noci, non ama pesce"
                       data-testid="nut-plan-input-notes" />
              </div>
            </div>

            {/* Indicazioni cliniche per l'AI (2-3 righe) — passate al prompt del piano */}
            <div className="mb-3 p-3 rounded-xl border border-emerald-200 bg-emerald-50/40">
              <Label className="flex items-center gap-1.5 text-slate-800 font-semibold">
                <Sparkles className="h-4 w-4 text-emerald-600" />
                Indicazioni cliniche per l'AI (2-3 righe)
              </Label>
              <Textarea rows={3} value={nutritionDietGuidance}
                        onChange={(e) => setNutritionDietGuidance(e.target.value)}
                        placeholder="Es. paziente con insulino-resistenza, ridurre carboidrati raffinati e favorire pasti a basso indice glicemico. Alternare 2 giorni di low-carb con 5 giorni bilanciati."
                        data-testid="nut-diet-guidance"
                        className="mt-1 text-[13px] leading-relaxed" />
              <p className="text-[11px] text-emerald-800 mt-1">
                Descrivi in 2-3 righe la direzione clinica che vuoi imprimere al piano.
                L'AI userà queste indicazioni per orientare la composizione dei pasti e delle scelte alimentari.
              </p>
            </div>

            {/* Durata piano dettagliato: 1 settimana (default) oppure multi-settimane */}
            <div className="mb-3 p-3 rounded-xl border border-teal-200 bg-teal-50/50">
              <label className={`flex items-start gap-2 cursor-pointer transition-all ${
                nutritionMultiWeek ? "text-slate-900" : "text-slate-700"
              }`}>
                <input type="checkbox" checked={nutritionMultiWeek}
                       onChange={(e) => setNutritionMultiWeek(e.target.checked)}
                       className="mt-0.5 h-4 w-4 accent-teal-600"
                       data-testid="nut-multi-week-toggle" />
                <div className="flex-1">
                  <span className="text-[13px] font-semibold flex items-center gap-1.5">
                    <History className="h-3.5 w-3.5 text-teal-600" />
                    Piano dettagliato multi-settimane (giorno per giorno)
                  </span>
                  <p className="text-[11px] text-teal-800 mt-0.5">
                    Attiva per creare un piano dettagliato di più settimane con alimentazione
                    variata giorno per giorno (es. 2, 3 o 4 settimane con pasti diversi).
                    Se disattivato genera una settimana tipo standard.
                  </p>
                </div>
              </label>
              {nutritionMultiWeek && (
                <div className="mt-2 flex items-center gap-2">
                  <Label className="text-xs font-semibold text-teal-800">Numero di settimane:</Label>
                  <Input type="number" min="2" max="8" step="1"
                         value={nutritionWeeksCount}
                         onChange={(e) => setNutritionWeeksCount(e.target.value)}
                         className="h-8 w-20 text-sm"
                         data-testid="nut-weeks-count" />
                  <span className="text-[11px] text-slate-600">
                    (≈ {Math.max(2, Math.min(8, Number(nutritionWeeksCount) || 2)) * 7} giorni dettagliati)
                  </span>
                </div>
              )}
            </div>

            <Button
              type="button"
              onClick={async () => {
                if (!weightKg || !heightCm) {
                  toast.error("Compila prima peso e altezza nei Dati antropometrici.");
                  return;
                }
                setNutritionPlanBusy(true);
                try {
                  const patientAge = pbirth ? calcAge(pbirth) : null;
                  const { data } = await api.post("/ai/nutrition-plan", {
                    patient_first_name: pfirst.trim(),
                    patient_last_name: plast.trim(),
                    age: patientAge,
                    gender: psex || user?.gender || "",
                    weight_kg: Number(weightKg),
                    height_cm: Number(heightCm),
                    activity_level: nutritionActivity,
                    visit_motive: visitMotive || "",
                    visit_motive_label: VISIT_MOTIVES.find(m => m.id === visitMotive)?.label || "",
                    motive_answers: (VISIT_MOTIVES.find(m => m.id === visitMotive)?.questions || [])
                      .map(q => ({ label: q.label, answer: (motiveAnswers[q.id] || "").trim() }))
                      .filter(qa => qa.answer),
                    notes: nutritionNotes.trim(),
                    override_target_kcal: nutritionOverrideKcal ? Number(nutritionOverrideKcal) : null,
                    nutritional_goals: nutritionalGoals.trim(),
                    clinical_guidance: nutritionDietGuidance.trim(),
                    food_habits: foodHabits,
                    food_habits_notes: foodHabitsNotes.trim(),
                    lifestyle_habits: lifestyleHabits,
                    lifestyle_habits_notes: lifestyleHabitsNotes.trim(),
                    weeks: nutritionMultiWeek ? Math.max(1, Math.min(8, Number(nutritionWeeksCount) || 1)) : 1,
                  });
                  setNutritionPlanData(data);
                  setNutritionWeekly(data.weekly_plan || "");
                  setNutritionRecommended(data.recommended_foods || "");
                  setNutritionAvoid(data.foods_to_avoid || "");
                  setNutritionPhases(data.intolerance_phases || "");
                  setNutritionFollowUp(data.follow_up_timeline || "");
                  setNutritionShoppingList(data.shopping_list || "");
                  setNutritionDietType(data.diet_type || "");
                  setNutritionDietTypeRationale(data.diet_type_rationale || "");
                  // Ripartizione del piatto salutare (specifica per il tipo di dieta scelto)
                  if (data.plate_percentages) {
                    setPlatePctVeg(Number(data.plate_percentages.veg) || 50);
                    setPlatePctProtein(Number(data.plate_percentages.protein) || 25);
                    setPlatePctCarbs(Number(data.plate_percentages.carbs) || 25);
                  }
                  // Razionale AI (spiegazione clinica del perché quelle kcal e composizione)
                  setNutritionAiRationale(data.ai_rationale || "");
                  setNutritionRationaleAccepted(false);
                  setNutritionRationaleFeedback("");
                  toast.success("Piano dietetico generato — leggi il razionale AI e conferma");
                } catch (e) {
                  toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore generazione piano");
                } finally {
                  setNutritionPlanBusy(false);
                }
              }}
              disabled={nutritionPlanBusy}
              data-testid="nut-generate-plan"
              className="bg-emerald-600 text-white hover:bg-emerald-700 h-10"
            >
              {nutritionPlanBusy
                ? <><SynapseLoader size="sm" inline className="mr-2" /> Genero piano dietetico…</>
                : <><Sparkles className="h-4 w-4 mr-2" /> Genera piano dietetico con AI</>}
            </Button>

            {/* Razionale AI — mostrato PRIMA della settimana tipo; il nutrizionista lo conferma o lo affina */}
            {nutritionAiRationale && (
              <div className="mt-4 p-4 rounded-xl border-2 border-purple-300 bg-purple-50/60"
                   data-testid="nut-ai-rationale-box">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-purple-600" />
                    <span className="font-heading text-sm font-bold text-purple-900">
                      Razionale clinico dell'AI — "Ho pensato di impostare questa dieta perché…"
                    </span>
                  </div>
                  {nutritionRationaleAccepted && (
                    <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-300 text-[10px]">
                      <Check className="h-3 w-3 mr-1" /> Confermato
                    </Badge>
                  )}
                </div>
                <Textarea rows={7} value={nutritionAiRationale}
                          onChange={(e) => { setNutritionAiRationale(e.target.value); setNutritionRationaleAccepted(false); }}
                          data-testid="nut-ai-rationale-text"
                          className="bg-white text-[13px] leading-relaxed" />
                <p className="text-[11px] text-purple-700 mt-1">
                  Rileggi il razionale: puoi modificarlo direttamente, confermarlo così com'è, oppure chiedere all'AI di rivederlo con un tuo commento.
                </p>

                <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-2">
                  <Button
                    type="button" size="sm"
                    onClick={() => {
                      // Conferma → copia il razionale in "Perché ho predisposto questa dieta"
                      setNutritionPatientMessage(nutritionAiRationale.trim());
                      setNutritionRationaleAccepted(true);
                      toast.success("Razionale confermato — inserito nel messaggio al paziente");
                    }}
                    data-testid="nut-rationale-accept"
                    className="bg-emerald-600 text-white hover:bg-emerald-700 h-9 text-xs"
                  >
                    <Check className="h-3.5 w-3.5 mr-1" /> Conferma e usa nel referto
                  </Button>
                  <Input value={nutritionRationaleFeedback}
                         onChange={(e) => setNutritionRationaleFeedback(e.target.value)}
                         placeholder="Es. rendilo più diretto, aggiungi il tema colesterolo…"
                         data-testid="nut-rationale-feedback"
                         className="text-xs md:col-span-1" />
                  <Button
                    type="button" size="sm" variant="outline"
                    disabled={!nutritionRationaleFeedback.trim() || nutritionPlanBusy}
                    onClick={async () => {
                      setNutritionPlanBusy(true);
                      try {
                        const patientAge = pbirth ? calcAge(pbirth) : null;
                        const { data } = await api.post("/ai/nutrition-plan", {
                          patient_first_name: pfirst.trim(),
                          patient_last_name: plast.trim(),
                          age: patientAge,
                          gender: psex || user?.gender || "",
                          weight_kg: Number(weightKg),
                          height_cm: Number(heightCm),
                          activity_level: nutritionActivity,
                          visit_motive: visitMotive || "",
                          visit_motive_label: VISIT_MOTIVES.find(m => m.id === visitMotive)?.label || "",
                          motive_answers: (VISIT_MOTIVES.find(m => m.id === visitMotive)?.questions || [])
                            .map(q => ({ label: q.label, answer: (motiveAnswers[q.id] || "").trim() }))
                            .filter(qa => qa.answer),
                          notes: nutritionNotes.trim(),
                          override_target_kcal: nutritionOverrideKcal ? Number(nutritionOverrideKcal) : null,
                          nutritional_goals: nutritionalGoals.trim(),
                          clinical_guidance: nutritionDietGuidance.trim(),
                          food_habits: foodHabits,
                          food_habits_notes: foodHabitsNotes.trim(),
                          lifestyle_habits: lifestyleHabits,
                          lifestyle_habits_notes: lifestyleHabitsNotes.trim(),
                          rationale_only: true,
                          previous_rationale: nutritionAiRationale.trim(),
                          user_feedback: nutritionRationaleFeedback.trim(),
                        });
                        setNutritionAiRationale(data.ai_rationale || nutritionAiRationale);
                        setNutritionRationaleAccepted(false);
                        setNutritionRationaleFeedback("");
                        toast.success("Razionale aggiornato");
                      } catch (e) {
                        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore aggiornamento razionale");
                      } finally {
                        setNutritionPlanBusy(false);
                      }
                    }}
                    data-testid="nut-rationale-refine"
                    className="border-purple-500 bg-white text-purple-700 hover:bg-purple-50 h-9 text-xs"
                  >
                    <Sparkles className="h-3.5 w-3.5 mr-1" /> Chiedi all'AI di rivederlo
                  </Button>
                </div>
              </div>
            )}

            {nutritionPlanData && (
              <div className="mt-4 space-y-3">
                {/* Riquadro fabbisogno */}
                <div className="grid grid-cols-3 gap-2 p-3 rounded-xl bg-emerald-50 border border-emerald-200">
                  <div>
                    <div className="text-[10px] text-emerald-700 uppercase font-semibold">BMR</div>
                    <div className="text-lg font-bold text-slate-900">{nutritionPlanData.bmr_kcal} kcal</div>
                  </div>
                  <div>
                    <div className="text-[10px] text-emerald-700 uppercase font-semibold">TDEE</div>
                    <div className="text-lg font-bold text-slate-900">{nutritionPlanData.tdee_kcal} kcal</div>
                  </div>
                  <div>
                    <div className="text-[10px] text-emerald-700 uppercase font-semibold">Target</div>
                    <div className="text-lg font-bold text-emerald-800">{nutritionPlanData.target_kcal} kcal</div>
                  </div>
                  <div className="col-span-3 text-[11px] text-slate-600">
                    {nutritionPlanData.target_delta_note} · Macronutrienti: {nutritionPlanData.macros_summary}
                  </div>
                </div>

                {/* Tipologia di dieta consigliata dall'AI — modificabile */}
                <div className="p-3 rounded-xl border border-purple-300 bg-purple-50/50" data-testid="nut-diet-type-card">
                  <Label className="flex items-center gap-1.5 text-slate-800 font-semibold">
                    <Sparkles className="h-4 w-4 text-purple-600" />
                    Tipologia di dieta consigliata
                  </Label>
                  <p className="text-[11px] text-purple-800 mt-0.5 mb-2">
                    Suggerita dall'AI in base al quadro clinico. Puoi <strong>modificarla</strong> se
                    preferisci un altro pattern (Mediterranea, Chetogenica, Low FODMAP,
                    Iperproteica, DASH, Vegetariana, ecc.).
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                    <div className="md:col-span-1">
                      <Label className="text-[11px] text-slate-600">Nome della dieta</Label>
                      <select
                        value={nutritionDietType}
                        onChange={(e) => setNutritionDietType(e.target.value)}
                        data-testid="nut-diet-type-select"
                        className="w-full h-9 px-2 rounded-md border border-slate-300 bg-white text-[13px] text-slate-900"
                      >
                        <option value="">— seleziona —</option>
                        <option value="Dieta Mediterranea">Dieta Mediterranea</option>
                        <option value="Dieta Mediterranea ipocalorica">Dieta Mediterranea ipocalorica</option>
                        <option value="Dieta Chetogenica">Dieta Chetogenica</option>
                        <option value="Dieta Low FODMAP">Dieta Low FODMAP</option>
                        <option value="Dieta iperproteica">Dieta iperproteica</option>
                        <option value="Dieta DASH">Dieta DASH</option>
                        <option value="Dieta plant-based">Dieta plant-based</option>
                        <option value="Dieta vegetariana">Dieta vegetariana</option>
                        <option value="Dieta vegana">Dieta vegana</option>
                        <option value="Dieta ipoglicemica">Dieta ipoglicemica</option>
                        <option value="Digiuno intermittente 16:8">Digiuno intermittente 16:8</option>
                        <option value="Dieta personalizzata">Dieta personalizzata</option>
                        {nutritionDietType && !["Dieta Mediterranea","Dieta Mediterranea ipocalorica","Dieta Chetogenica","Dieta Low FODMAP","Dieta iperproteica","Dieta DASH","Dieta plant-based","Dieta vegetariana","Dieta vegana","Dieta ipoglicemica","Digiuno intermittente 16:8","Dieta personalizzata"].includes(nutritionDietType) && (
                          <option value={nutritionDietType}>{nutritionDietType}</option>
                        )}
                      </select>
                    </div>
                    <div className="md:col-span-2">
                      <Label className="text-[11px] text-slate-600">Motivazione clinica (modificabile)</Label>
                      <Textarea rows={2} value={nutritionDietTypeRationale}
                                onChange={(e) => setNutritionDietTypeRationale(e.target.value)}
                                placeholder="Perché questo tipo di dieta è indicato per il paziente…"
                                data-testid="nut-diet-type-rationale"
                                className="text-[12px] bg-white" />
                    </div>
                  </div>
                </div>

                <div>
                  <Label>Settimana tipo (2-3 varianti per pasto — modificabile)</Label>
                  <Textarea rows={14} value={nutritionWeekly}
                            onChange={(e) => setNutritionWeekly(e.target.value)}
                            className="font-mono text-[12px] leading-relaxed"
                            data-testid="nut-weekly-plan" />

                  {/* Chat AI sulla settimana tipo — modifiche mirate mantenendo target kcal */}
                  <div className="mt-2 p-3 rounded-xl border border-purple-300 bg-purple-50/50">
                    <Label className="flex items-center gap-1.5 text-slate-800 font-semibold">
                      <Sparkles className="h-4 w-4 text-purple-600" />
                      Chiedi all'AI di modificare la settimana
                    </Label>
                    <p className="text-[11px] text-purple-800 mt-0.5 mb-2">
                      Esempi: <em>«sostituisci il pesce del venerdì con legumi»</em>,
                      <em> «rendi le colazioni salate»</em>,
                      <em> «riduci le porzioni di pasta del pranzo»</em>.
                      Il target calorico e la ripartizione macro vengono mantenuti.
                    </p>
                    <div className="flex flex-col md:flex-row gap-2">
                      <Textarea rows={2} value={nutritionWeeklyFeedback}
                                onChange={(e) => setNutritionWeeklyFeedback(e.target.value)}
                                placeholder="Descrivi la modifica…"
                                data-testid="nut-weekly-feedback"
                                className="flex-1 text-[13px] bg-white" />
                      <Button
                        type="button" size="sm"
                        disabled={!nutritionWeeklyFeedback.trim() || nutritionWeeklyBusy || !nutritionWeekly.trim()}
                        onClick={async () => {
                          setNutritionWeeklyBusy(true);
                          try {
                            const patientAge = pbirth ? calcAge(pbirth) : null;
                            const { data } = await api.post("/ai/nutrition-plan", {
                              patient_first_name: pfirst.trim(),
                              patient_last_name: plast.trim(),
                              age: patientAge,
                              gender: psex || user?.gender || "",
                              weight_kg: Number(weightKg) || 0,
                              height_cm: Number(heightCm) || 0,
                              activity_level: nutritionActivity,
                              visit_motive: visitMotive || "",
                              visit_motive_label: VISIT_MOTIVES.find(m => m.id === visitMotive)?.label || "",
                              notes: nutritionNotes.trim(),
                              override_target_kcal: nutritionOverrideKcal ? Number(nutritionOverrideKcal) : null,
                              weekly_edit_only: true,
                              previous_weekly_plan: nutritionWeekly,
                              user_feedback: nutritionWeeklyFeedback.trim(),
                            });
                            const updated = data.weekly_plan || nutritionWeekly;
                            setNutritionWeekly(updated);
                            // Lista spesa auto-aggiornata (coerente con la settimana modificata)
                            const updatedShop = (data.shopping_list || "").trim();
                            if (updatedShop) {
                              setNutritionShoppingList(updatedShop);
                            }
                            setNutritionWeeklyHistory((prev) => [
                              ...prev,
                              { q: nutritionWeeklyFeedback.trim(), ts: new Date().toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }) },
                            ]);
                            setNutritionWeeklyFeedback("");
                            toast.success(updatedShop
                              ? "Settimana e lista della spesa aggiornate"
                              : "Settimana tipo aggiornata");
                          } catch (e) {
                            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore modifica settimana");
                          } finally {
                            setNutritionWeeklyBusy(false);
                          }
                        }}
                        data-testid="nut-weekly-ask-ai"
                        className="bg-purple-600 text-white hover:bg-purple-700 h-10 md:w-40 flex-shrink-0"
                      >
                        {nutritionWeeklyBusy
                          ? <><SynapseLoader size="sm" inline className="mr-1" /> Aggiorno…</>
                          : <><Sparkles className="h-3.5 w-3.5 mr-1" /> Chiedi all'AI</>}
                      </Button>
                    </div>
                    {nutritionWeeklyHistory.length > 0 && (
                      <div className="mt-2 text-[11px] text-purple-800 space-y-0.5"
                           data-testid="nut-weekly-history">
                        <div className="font-semibold text-purple-900">Modifiche applicate:</div>
                        {nutritionWeeklyHistory.slice(-4).map((h, i) => (
                          <div key={i} className="flex gap-2">
                            <span className="text-purple-500 font-mono">{h.ts}</span>
                            <span>«{h.q}»</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label>Alimenti consigliati</Label>
                    <Textarea rows={5} value={nutritionRecommended}
                              onChange={(e) => setNutritionRecommended(e.target.value)}
                              data-testid="nut-recommended-foods" />
                  </div>
                  <div>
                    <Label>Alimenti da limitare / evitare</Label>
                    <Textarea rows={5} value={nutritionAvoid}
                              onChange={(e) => setNutritionAvoid(e.target.value)}
                              data-testid="nut-avoid-foods" />
                  </div>
                </div>

                {nutritionPhases && (
                  <div>
                    <Label>Piano progressivo (intolleranze / disturbi digestivi)</Label>
                    <Textarea rows={6} value={nutritionPhases}
                              onChange={(e) => setNutritionPhases(e.target.value)}
                              data-testid="nut-phases" />
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label>Prossimi controlli</Label>
                    <Textarea rows={2} value={nutritionFollowUp}
                              onChange={(e) => setNutritionFollowUp(e.target.value)}
                              placeholder="Es. 2 settimane, 1 mese, 3 mesi"
                              data-testid="nut-followup" />
                  </div>
                  <div>
                    <Label>Note del nutrizionista</Label>
                    <Textarea rows={2} value={nutritionPlanNotes}
                              onChange={(e) => setNutritionPlanNotes(e.target.value)}
                              placeholder="Note personalizzate al paziente"
                              data-testid="nut-plan-notes" />
                  </div>
                </div>

                <div>
                  <Label>Lista della spesa settimanale (aggregata — modificabile)</Label>
                  <Textarea rows={6} value={nutritionShoppingList}
                            onChange={(e) => setNutritionShoppingList(e.target.value)}
                            placeholder="Verdure e frutta: … / Cereali e legumi: … / Proteine: … / Condimenti: …"
                            className="font-mono text-[12px] leading-relaxed"
                            data-testid="nut-shopping-list" />
                  <p className="text-[11px] text-slate-500 mt-1">
                    Aiuta il paziente a pianificare gli acquisti settimanali. Verrà stampata in fondo al piano nutrizionale.
                  </p>
                </div>

                {/* "Perché ho predisposto questa dieta" — messaggio al paziente stampato PRIMA della dieta */}
                <div className="mt-2 p-3 rounded-xl border border-teal-300 bg-teal-50/60">
                  <Label className="flex items-center gap-1.5 text-slate-800 font-semibold">
                    <Heart className="h-4 w-4 text-teal-600" />
                    Perché ho predisposto questa dieta (per il paziente)
                  </Label>
                  <Textarea rows={4} value={nutritionPatientMessage}
                            onChange={(e) => setNutritionPatientMessage(e.target.value)}
                            placeholder="Spiega al paziente il razionale della dieta e — soprattutto — l'obiettivo finale che si sta perseguendo. Es. «Ho scelto una dieta mediterranea ipocalorica di 1500 kcal, distribuita in 5 pasti, per riportare il peso corporeo verso i 65 kg entro 4-6 mesi, riducendo così il carico glicemico e sostenendo il tono muscolare.»"
                            data-testid="nut-patient-message"
                            className="mt-1 text-[13px] leading-relaxed" />
                  <p className="text-[11px] text-teal-800 mt-1">
                    <strong>Verrà stampato all'inizio del piano nutrizionale nel PDF</strong>, prima dei pasti.
                    Ricorda di specificare l'obiettivo finale (peso target, tempi, benefici attesi).
                  </p>
                </div>
              </div>
            )}
          </Card>
        )}

        {/* -------- Lettera al MMG per esami di completamento (solo nutrizionista) --------
             Riquadro distinto BLU per il nutrizionista, mostrato SOTTO il piano nutrizionale. */}
        {isNutritionist && (
          <Card className="p-4 md:p-6 rounded-2xl border-[#1E3A8A]/25 shadow-sm bg-white mt-8
                           [&_label]:text-slate-700 [&_input]:bg-white [&_input]:text-slate-900
                           [&_input]:border-slate-300 [&_textarea]:bg-white [&_textarea]:text-slate-900
                           [&_textarea]:border-slate-300"
                data-testid="card-mmg-letter">
            <div className="flex items-center gap-2 mb-4">
              <FileText className="h-5 w-5 text-[#1E3A8A]" />
              <h2 className="font-heading text-lg font-bold text-slate-900">
                Lettera al Medico di Base
              </h2>
            </div>
          <div className="mt-6 rounded-xl border-2 border-[#1E3A8A]/25 bg-gradient-to-br from-[#DBEAFE]/40 to-white p-4">
            <div className="flex items-center gap-2 mb-2">
              <FileText className="h-5 w-5 text-[#1E3A8A]" />
              <h3 className="font-heading text-base font-bold text-slate-900">
                Lettera al Medico di Base per esami di completamento
              </h3>
            </div>
            <p className="text-xs text-slate-700 mb-3">
              Prepara una lettera cortese al MMG del paziente per richiedere esami utili al completamento
              della valutazione nutrizionale (es. glicemia, TSH, vitamina D, profilo lipidico). L'AI genererà
              una bozza in tono deferente che puoi modificare prima dell'invio.
            </p>

            {/* Selettore MMG (ricerca su Synappsi) — sempre disponibile per il nutrizionista */}
            <div className="mb-3 p-3 rounded-lg border border-[#1E3A8A]/20 bg-white space-y-2">
              <Label className="text-xs font-semibold text-[#1E3A8A] uppercase tracking-wide">
                MMG del paziente su Synappsi (opzionale)
              </Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <select value={mmgProvince} onChange={(e) => setMmgProvince(e.target.value)}
                          data-testid="nut-mmg-province"
                          className="h-10 w-full pl-9 pr-3 rounded-md border border-slate-300 bg-white text-sm text-slate-900">
                    <option value="all">Tutte le province</option>
                    {provinces.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input value={mmgQuery} onChange={(e) => setMmgQuery(e.target.value)}
                         placeholder="Cerca MMG per cognome…" className="pl-9"
                         data-testid="nut-mmg-search" />
                </div>
              </div>

              {selectedMmg ? (
                <div className="rounded-lg border border-[#1E3A8A]/30 bg-[#DBEAFE]/60 p-3 flex items-center justify-between">
                  <div>
                    <div className="text-xs text-[#1E3A8A] font-semibold uppercase tracking-wide">MMG collegato</div>
                    <div className="font-bold text-slate-900">{selectedMmg.name || `${selectedMmg.first_name || ""} ${selectedMmg.last_name || ""}`.trim()}</div>
                    <div className="text-xs text-slate-500">
                      {selectedMmg.order_province || "—"} · {selectedMmg.email}
                    </div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => setSelectedMmg(null)}
                          className="border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
                          data-testid="nut-mmg-clear">
                    Cambia
                  </Button>
                </div>
              ) : hasSearch ? (
                <div className="max-h-48 overflow-y-auto border border-slate-200 rounded-lg bg-white">
                  {mmgResults.length === 0 && <div className="p-3 text-sm text-slate-400">Nessun MMG trovato su Synappsi</div>}
                  {mmgResults.map((c) => (
                    <button key={c.id} onClick={() => setSelectedMmg(c)}
                            data-testid={`nut-mmg-${c.id}`}
                            className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-b-0 flex items-center justify-between">
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-slate-900 truncate">
                          {c.name || `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.email}
                        </div>
                        <div className="text-xs text-slate-500 truncate">
                          Medicina generale · {c.order_province || "—"}
                        </div>
                      </div>
                      <Badge variant="secondary" className="text-[10px]">Seleziona</Badge>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-[11px] text-slate-500 italic">
                  Digita cognome o provincia per cercare il MMG del paziente su Synappsi. Se non lo trovi puoi comunque generare la lettera cartacea da consegnare a mano (in fondo alla card).
                </div>
              )}
            </div>

            <div className="space-y-3">
              <div>
                <Label>Esami di completamento da richiedere</Label>
                <Textarea rows={3} value={mmgCompletionExams}
                          onChange={(e) => setMmgCompletionExams(e.target.value)}
                          placeholder="Es. glicemia a digiuno, insulinemia + HOMA-IR, TSH FT3 FT4, vitamina D, profilo lipidico completo, emocromo, ferritina, sideremia."
                          data-testid="nut-mmg-exams" />
              </div>

              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  size="sm"
                  onClick={async () => {
                    if (!mmgCompletionExams.trim()) {
                      toast.error("Inserisci almeno un esame di completamento.");
                      return;
                    }
                    if (!pfirst.trim() || !plast.trim()) {
                      toast.error("Compila prima nome e cognome del paziente.");
                      return;
                    }
                    setMmgCompletionBusy(true);
                    try {
                      const { data } = await api.post("/ai/mmg-completion-letter", {
                        patient_first_name: pfirst.trim(),
                        patient_last_name: plast.trim(),
                        patient_birth_date: pbirth || "",
                        patient_email: pemail.trim(),
                        mmg_id: selectedMmg?.id || null,
                        visit_motive: VISIT_MOTIVES.find(m => m.id === visitMotive)?.label || "consulenza nutrizionale",
                        motive_details: Object.entries(motiveAnswers)
                          .filter(([, v]) => (v || "").trim())
                          .map(([k, v]) => {
                            const q = (VISIT_MOTIVES.find(m => m.id === visitMotive)?.questions || [])
                              .find(qq => qq.id === k);
                            return q ? `${q.label}: ${v.trim()}` : "";
                          })
                          .filter(Boolean)
                          .join("; "),
                        requested_exams: mmgCompletionExams.trim(),
                      });
                      setMmgCompletionLetter(data.letter || "");
                      toast.success("Bozza lettera generata — puoi modificarla prima dell'invio");
                    } catch (e) {
                      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore generazione lettera");
                    } finally {
                      setMmgCompletionBusy(false);
                    }
                  }}
                  disabled={mmgCompletionBusy}
                  data-testid="nut-generate-mmg-letter"
                  className="bg-teal-600 text-white hover:bg-teal-700 h-9 text-xs"
                >
                  {mmgCompletionBusy
                    ? <><SynapseLoader size="sm" inline className="mr-1" /> Genero bozza…</>
                    : <><Sparkles className="h-3.5 w-3.5 mr-1" /> Genera bozza con AI</>}
                </Button>
                {mmgCompletionLetter && (
                  <Button type="button" size="sm" variant="outline"
                          onClick={() => setMmgCompletionLetter("")}
                          className="border-slate-300 bg-white text-slate-700 hover:bg-slate-50 h-9 text-xs">
                    <X className="h-3.5 w-3.5 mr-1" /> Svuota bozza
                  </Button>
                )}
              </div>

              {mmgCompletionLetter && (
                <div>
                  <Label>Bozza lettera (modificabile)</Label>
                  <Textarea rows={12} value={mmgCompletionLetter}
                            onChange={(e) => setMmgCompletionLetter(e.target.value)}
                            data-testid="nut-mmg-letter-editor"
                            className="font-mono text-[13px] leading-relaxed" />
                  <p className="text-[11px] text-slate-500 mt-1">
                    La lettera verrà stampata come pagina a parte nel PDF referto e — se selezioni la casella qui sotto — inviata al MMG.
                  </p>
                </div>
              )}

              {mmgCompletionLetter && selectedMmg && (
                <label className={`flex items-start gap-2 p-2.5 rounded-lg border cursor-pointer transition-all ${
                  sendMmgCompletionLetter ? "bg-teal-100 border-teal-400" : "bg-white border-slate-200 hover:border-teal-300"
                }`}>
                  <input type="checkbox" checked={sendMmgCompletionLetter}
                         onChange={(e) => setSendMmgCompletionLetter(e.target.checked)}
                         className="mt-0.5 h-4 w-4 accent-teal-600"
                         data-testid="nut-send-mmg-completion-toggle" />
                  <div className="text-[13px] leading-snug">
                    <span className="font-semibold text-slate-900">
                      Invia questa lettera al MMG {selectedMmg.name || `${selectedMmg.first_name || ""} ${selectedMmg.last_name || ""}`.trim()} insieme al referto
                    </span>
                    <p className="text-slate-600 mt-0.5 text-[11px]">
                      Il MMG riceverà via email il referto nutrizionale + questa lettera con gli esami richiesti.
                    </p>
                  </div>
                </label>
              )}

              {/* Lettera cartacea (consegna a mano) — quando il MMG NON è su Synappsi */}
              <div className="mt-4 pt-4 border-t border-slate-200">
                <label className={`flex items-start gap-2 p-3 rounded-lg border cursor-pointer transition-all ${
                  wantHandLetter ? "bg-amber-50 border-amber-300" : "bg-white border-slate-200 hover:border-amber-200"
                }`}>
                  <input type="checkbox" checked={wantHandLetter}
                         onChange={(e) => setWantHandLetter(e.target.checked)}
                         className="mt-0.5 h-4 w-4 accent-amber-600"
                         data-testid="want-hand-letter-toggle" />
                  <div className="text-[13px] leading-snug flex-1">
                    <span className="font-semibold text-slate-900 flex items-center gap-1.5">
                      <FileText className="h-3.5 w-3.5 text-amber-600" />
                      Il MMG del paziente non è su Synappsi — genera lettera da consegnare a mano
                    </span>
                    <p className="text-slate-600 mt-0.5 text-[11px]">
                      Verrà stampata come pagina PDF aggiuntiva, in formato "consegna a mano",
                      con l'email del paziente evidenziata in modo che il MMG possa inviare la prescrizione
                      via email direttamente al/la paziente.
                    </p>
                  </div>
                </label>

                {wantHandLetter && (
                  <div className="mt-3 pl-2 border-l-2 border-amber-300 space-y-3">
                    <Button
                      type="button"
                      size="sm"
                      onClick={async () => {
                        if (!mmgCompletionExams.trim()) {
                          toast.error("Compila prima gli esami di completamento da richiedere.");
                          return;
                        }
                        if (!pfirst.trim() || !plast.trim()) {
                          toast.error("Compila prima nome e cognome del paziente.");
                          return;
                        }
                        if (!pemail.trim()) {
                          toast.error("Inserisci l'email del paziente (il MMG la userà per inviare la prescrizione).");
                          return;
                        }
                        setMmgHandLetterBusy(true);
                        try {
                          const { data } = await api.post("/ai/mmg-completion-letter", {
                            patient_first_name: pfirst.trim(),
                            patient_last_name: plast.trim(),
                            patient_birth_date: pbirth || "",
                            patient_email: pemail.trim(),
                            mmg_id: null,
                            hand_delivery: true,
                            visit_motive: VISIT_MOTIVES.find(m => m.id === visitMotive)?.label || "consulenza nutrizionale",
                            motive_details: Object.entries(motiveAnswers)
                              .filter(([, v]) => (v || "").trim())
                              .map(([k, v]) => {
                                const q = (VISIT_MOTIVES.find(m => m.id === visitMotive)?.questions || [])
                                  .find(qq => qq.id === k);
                                return q ? `${q.label}: ${v.trim()}` : "";
                              })
                              .filter(Boolean)
                              .join("; "),
                            requested_exams: mmgCompletionExams.trim(),
                          });
                          setMmgHandLetter(data.letter || "");
                          toast.success("Bozza lettera cartacea generata — modificabile");
                        } catch (e) {
                          toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore generazione lettera cartacea");
                        } finally {
                          setMmgHandLetterBusy(false);
                        }
                      }}
                      disabled={mmgHandLetterBusy}
                      data-testid="generate-hand-letter"
                      className="bg-amber-600 text-white hover:bg-amber-700 h-9 text-xs"
                    >
                      {mmgHandLetterBusy
                        ? <><SynapseLoader size="sm" inline className="mr-1" /> Genero lettera cartacea…</>
                        : <><Sparkles className="h-3.5 w-3.5 mr-1" /> Genera lettera cartacea con AI</>}
                    </Button>

                    {mmgHandLetter && (
                      <div>
                        <Label>Lettera per consegna a mano (modificabile)</Label>
                        <Textarea rows={12} value={mmgHandLetter}
                                  onChange={(e) => setMmgHandLetter(e.target.value)}
                                  data-testid="hand-letter-editor"
                                  className="font-mono text-[13px] leading-relaxed" />
                        <p className="text-[11px] text-slate-500 mt-1">
                          Verrà stampata come pagina PDF a parte con intestazione "AL MEDICO DI MEDICINA GENERALE — CONSEGNATA A MANO"
                          e l'email del/la paziente evidenziata per l'invio della prescrizione.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          </Card>
        )}

        {/* -------- Consulto ad altro specialista (per nutrizionista card viola distinta; per medico sezione nello stesso riquadro) -------- */}
        <Card id="sec-consulto" className={`${isNutritionist
                            ? `p-4 md:p-6 rounded-2xl shadow-sm mt-8 ${sendReferral ? "border-purple-300 bg-purple-50/40" : "border-slate-200 bg-white"}`
                            : "mt-5 pt-5 border-0 border-t border-amber-200 rounded-none shadow-none bg-transparent p-0"}
                          [&_label]:text-slate-700
                          [&_input]:bg-white [&_input]:text-slate-900 [&_input]:border-slate-300
                          [&_textarea]:bg-white [&_textarea]:text-slate-900 [&_textarea]:border-slate-300`}
              data-testid="card-referral">
          <div className="flex items-center gap-2 mb-3">
            <Stethoscope className="h-5 w-5 text-purple-600" />
            <h2 className={`font-heading font-bold text-slate-900 ${isNutritionist ? "text-lg" : "text-base"}`}>
              Crea sinapsi con altro specialista
            </h2>
          </div>
          <label className={`flex items-start gap-2 p-3 rounded-lg border cursor-pointer transition-all ${
            sendReferral ? "bg-purple-50 border-purple-300" : "bg-white border-slate-200 hover:border-slate-300"
          }`} data-testid="referral-toggle-wrap">
            <input type="checkbox" checked={sendReferral}
                   onChange={(e) => setSendReferral(e.target.checked)}
                   className="mt-0.5 h-4 w-4 accent-purple-600"
                   data-testid="referral-toggle" />
            <div className="text-[13px] leading-snug">
              <span className="font-semibold text-slate-900 flex items-center gap-1.5">
                <Stethoscope className="h-3.5 w-3.5 text-purple-600" /> Attiva sinapsi con altro specialista
              </span>
              <p className="text-slate-600 mt-0.5">
                Es. ginecologo → nutrizionista per la nutrizione in gravidanza. Spiega
                brevemente il motivo (2-3 righe), <strong>l'AI genera il testo professionale</strong> del
                consulto — sarà inviato via Synappsi al collega e incluso nel referto PDF
                che la paziente porterà in visita.
              </p>
            </div>
          </label>

          {sendReferral && (
            <div className="mt-3 pl-2 border-l-2 border-purple-300 space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <select value={refProvince} onChange={(e) => setRefProvince(e.target.value)}
                          data-testid="referto-ref-province"
                          className="h-10 w-full pl-9 pr-3 rounded-md border border-slate-300 bg-white text-sm text-slate-900">
                    <option value="all">Tutte le province</option>
                    {provinces.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div className="relative">
                  <Stethoscope className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input value={refSpecialization}
                         onChange={(e) => setRefSpecialization(e.target.value)}
                         placeholder="Specializzazione (es. nutrizionista)" className="pl-9"
                         data-testid="referto-ref-specialization" />
                </div>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input value={refQuery} onChange={(e) => setRefQuery(e.target.value)}
                         placeholder="Cerca per cognome…" className="pl-9"
                         data-testid="referto-ref-search" />
                </div>
              </div>

              {selectedRef ? (
                <div className="rounded-lg border border-purple-300 bg-purple-50/60 p-3 flex items-center justify-between">
                  <div>
                    <div className="text-xs text-purple-700 font-semibold uppercase tracking-wide">Collega selezionato</div>
                    <div className="font-bold text-slate-900">
                      {selectedRef.name || `${selectedRef.first_name || ""} ${selectedRef.last_name || ""}`.trim()}
                    </div>
                    <div className="text-xs text-slate-500">
                      {selectedRef.specialization || "Specialista"} · {selectedRef.order_province || "—"} · {selectedRef.email}
                    </div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => setSelectedRef(null)}
                          className="border-slate-300 bg-white text-slate-700 hover:bg-slate-50">
                    Cambia
                  </Button>
                </div>
              ) : hasRefSearch ? (
                <div className="max-h-48 overflow-y-auto border border-slate-200 rounded-lg bg-white">
                  {refResults.length === 0 && <div className="p-3 text-sm text-slate-400">Nessun collega trovato</div>}
                  {refResults.map((c) => (
                    <button key={c.id} onClick={() => setSelectedRef(c)}
                            data-testid={`referto-ref-${c.id}`}
                            className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-b-0 flex items-center justify-between">
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-slate-900 truncate">
                          {c.name || `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.email}
                        </div>
                        <div className="text-xs text-slate-500 truncate">
                          {c.specialization || "Specialista"} · {c.order_province || "—"}
                        </div>
                      </div>
                      <Badge variant="secondary" className="text-[10px]">Seleziona</Badge>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-[11px] text-slate-500 italic">
                  Digita specializzazione, cognome o provincia per trovare il collega.
                </div>
              )}

              <div>
                <Label>Motivo del consulto (breve) {useAi && <span className="text-purple-600 text-xs">· AI rifinirà il testo</span>}</Label>
                <Textarea rows={3} value={referralReason}
                          onChange={(e) => setReferralReason(e.target.value)}
                          placeholder="Es. gravidanza al 2° trimestre, aumento ponderale sopra la norma, richiedo piano nutrizionale personalizzato."
                          data-testid="referto-referral-reason" />
                <div className="mt-1 flex items-center justify-between gap-2">
                  <p className="text-[11px] text-slate-500">
                    Scrivi solo l'essenziale — l'AI leggendo la specializzazione del collega genererà la lettera professionale.
                  </p>
                  <Button type="button" size="sm" variant="outline"
                          onClick={previewReferralAI} disabled={aiPreviewBusy || !selectedRef}
                          data-testid="ai-referral-preview-btn"
                          className="border-purple-600 bg-purple-600 text-white hover:bg-purple-700 h-8 text-xs flex-shrink-0 disabled:opacity-50">
                    {aiPreviewBusy ? <><SynapseLoader size="sm" inline className="mr-1" /> …</>
                                   : <><Eye className="h-3 w-3 mr-1" /> Anteprima comunicazione specialista</>}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </Card>

        <div className="mt-5">
          {!canSubmit && missingRequired.length > 0 && (
            <div className="mb-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5"
                 data-testid="missing-required-alert">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-[13px] leading-snug text-amber-900">
                  <span className="font-semibold">Per generare l'anteprima PDF completa questi campi obbligatori:</span>
                  <ul className="mt-1 ml-4 list-disc space-y-0.5">
                    {missingRequired.map((f) => (
                      <li key={f} className="text-amber-800">{f}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-end">
            {isNutritionist && (
              <label className="flex items-start gap-2 rounded-xl border-2 border-emerald-300 bg-emerald-50 px-3 py-2.5 md:max-w-md cursor-pointer hover:bg-emerald-100/60 transition"
                     data-testid="evaluation-complete-wrapper">
                <input type="checkbox"
                       checked={evaluationComplete}
                       onChange={(e) => setEvaluationComplete(e.target.checked)}
                       data-testid="evaluation-complete-checkbox"
                       className="mt-0.5 h-4 w-4 accent-emerald-600 flex-shrink-0" />
                <span className="text-[13px] leading-snug text-emerald-900">
                  <strong>Valutazione nutrizionale completa</strong> — confermo che la visita è
                  conclusa e verrà <strong>archiviata nella cartella del paziente</strong> con
                  tutte le visite precedenti (identificate per Codice Fiscale).
                </span>
              </label>
            )}
            <Button onClick={generatePreview} disabled={busy || !canSubmit || (isNutritionist && !evaluationComplete)}
                    data-testid="referto-submit"
                    className="h-11 rounded-xl bg-gradient-to-r from-[#1E3A8A] via-[#2563EB] to-[#10B981]
                               hover:from-[#1E40AF] hover:via-[#3B82F6] hover:to-[#059669]
                               text-white font-semibold shadow-md shadow-emerald-500/10 disabled:opacity-50">
              {busy ? <><SynapseLoader size="sm" inline className="mr-2" /> Genero anteprima PDF…</>
                    : <><Eye className="h-4 w-4 mr-2" /> Anteprima referto PDF</>}
            </Button>
          </div>
        </div>

        {result && (
          <div className="mt-4 rounded-xl border border-emerald-300 bg-emerald-50 p-4" data-testid="referto-success">
            <div className="flex items-center gap-2 mb-2">
              <ShieldCheck className="h-5 w-5 text-emerald-700" />
              <strong className="text-emerald-900">Referto emesso e inviato a {result.sent_to}</strong>
            </div>
            {result.prescription_thread_id && (
              <div className="mb-2 text-xs text-[#1E3A8A] bg-[#DBEAFE] rounded px-2 py-1 inline-block">
                ✓ Prescrizione inviata a <strong>{result.prescription_mmg_name}</strong>
                {" "}·{" "}
                <button onClick={() => navigate(`/messaggi/${result.prescription_thread_id}`)}
                        className="underline hover:text-[#1E40AF]">Apri sinapsi</button>
              </div>
            )}
            <div className="text-xs text-emerald-800 space-y-0.5">
              <div>Hash SHA-256: <span className="font-mono text-[11px]">{result.content_hash?.slice(0, 24)}…{result.content_hash?.slice(-8)}</span></div>
              <div>PDF: {Math.round((result.pdf_size || 0) / 100) / 10} KB · Link valido fino al {new Date(result.expires_at).toLocaleDateString("it-IT")}</div>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <a href={result.download_url} target="_blank" rel="noopener noreferrer"
                 className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700">
                <ExternalLink className="h-3.5 w-3.5" /> Scarica PDF
              </a>
              <button onClick={copyLink}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-emerald-300 bg-white text-emerald-800 text-xs font-semibold hover:bg-emerald-50">
                {copied ? <><Check className="h-3.5 w-3.5" /> Copiato</> : <><Copy className="h-3.5 w-3.5" /> Copia link</>}
              </button>
            </div>
          </div>
        )}
      </Card>

      {/* -------- Navigazione sezioni Indietro / Avanti -------- */}
      <div className="flex items-center justify-between mt-5" data-testid="section-nav">
        <Button type="button" variant="outline" onClick={() => stepSection(-1)} disabled={sectionIndex <= 0} data-testid="section-prev-btn"
                className="h-10 bg-white border-slate-300 text-slate-700 hover:bg-slate-50">
          <ChevronLeft className="h-4 w-4 mr-1" /> {sectionIndex > 0 ? sectionTabs[sectionIndex - 1].l : "Indietro"}
        </Button>
        <span className="text-xs text-slate-500" data-testid="section-progress">{sectionIndex + 1} / {sectionTabs.length}</span>
        <Button type="button" onClick={() => stepSection(1)} disabled={sectionIndex >= sectionTabs.length - 1} data-testid="section-next-btn"
                className="h-10 bg-[#1E88E5] hover:bg-[#1976D2] text-white">
          {sectionIndex < sectionTabs.length - 1 ? sectionTabs[sectionIndex + 1].l : "Avanti"} <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      </div>

      <p className="text-[11px] text-slate-500 italic text-center mt-6">
        Synappsi non modifica il contenuto clinico. Rivedi sempre il referto prima di emetterlo.
      </p>

      {/* -------- Modale bozze -------- */}
      {showDrafts && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
             onClick={() => setShowDrafts(false)}
             data-testid="drafts-modal">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[80vh] flex flex-col overflow-hidden shadow-xl"
               onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FolderOpen className="h-5 w-5 text-emerald-600" />
                <h3 className="font-semibold text-slate-900">Bozze salvate</h3>
              </div>
              <button onClick={() => setShowDrafts(false)}
                      className="text-slate-400 hover:text-slate-700"><X className="h-4 w-4" /></button>
            </div>
            <div className="flex-1 overflow-y-auto">
              {drafts.length === 0 ? (
                <div className="p-8 text-center text-slate-500 text-sm">
                  Nessuna bozza salvata. Compila il modulo e clicca "Salva bozza".
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {drafts.map((d) => (
                    <button key={d.id} onClick={() => loadDraft(d.id)}
                            data-testid={`draft-item-${d.id}`}
                            className="w-full text-left px-4 py-3 hover:bg-slate-50 flex items-center gap-3">
                      <FileCheck2 className="h-5 w-5 text-emerald-500 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-slate-900 truncate">{d.label}</div>
                        <div className="text-[11px] text-slate-500">
                          Aggiornata {new Date(d.updated_at).toLocaleString("it-IT", {
                            day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit"
                          })}
                        </div>
                      </div>
                      <button onClick={(e) => deleteDraft(d.id, e)}
                              className="p-1.5 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* -------- Modale anteprima PDF -------- */}
      {preview && (
        <div className="fixed inset-0 z-50 bg-slate-900/80 backdrop-blur-sm flex flex-col"
             data-testid="referto-preview-modal">
          <div className="flex-shrink-0 bg-white border-b border-slate-200 px-4 py-3">
            <div className="flex items-center justify-between gap-3 mb-2">
              <div className="min-w-0">
                <div className="flex items-center gap-2 text-slate-900 font-semibold">
                  <Eye className="h-4 w-4 text-emerald-600" />
                  <span className="text-sm md:text-base">Anteprima invii — controlla tutto prima di confermare</span>
                </div>
                <div className="text-xs text-slate-500 truncate">
                  Paziente: <strong>{preview.patient_email || pemail}</strong>
                  {preview.mmg_letter_pending && preview.prescription_mmg_name && (
                    <> · MMG: <strong>{selectedMmg?.name || selectedMmg?.email || "collega MMG"}</strong></>
                  )}
                  {preview.referral_pending && preview.referral_specialist_name && (
                    <> · Consulto: <strong>{preview.referral_specialist_name}</strong></>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Button variant="outline" onClick={discardPreview} disabled={confirming}
                        data-testid="referto-preview-edit"
                        className="border-slate-300 bg-white text-slate-700 hover:bg-slate-50 h-9">
                  <Pencil className="h-4 w-4 mr-1.5" /> Modifica
                </Button>
                <Button onClick={confirmAndSend} disabled={confirming}
                        data-testid="referto-preview-confirm"
                        className="h-9 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white font-semibold">
                  {confirming
                    ? <><SynapseLoader size="sm" inline className="mr-1.5" /> Invio in corso…</>
                    : <><Send className="h-4 w-4 mr-1.5" /> Conferma e invia tutto</>}
                </Button>
                <button onClick={discardPreview} disabled={confirming}
                        aria-label="Chiudi"
                        className="hidden md:inline-flex h-9 w-9 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100">
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
            {/* Tabs */}
            <div className="flex items-center gap-1 border-b -mb-3">
              <button onClick={() => setPreviewTab("pdf")}
                      data-testid="preview-tab-pdf"
                      className={`px-3 py-2 text-sm font-medium transition-all -mb-px border-b-2 ${
                        previewTab === "pdf" ? "text-emerald-700 border-emerald-600" : "text-slate-500 border-transparent hover:text-slate-800"
                      }`}>
                <FileCheck2 className="h-4 w-4 inline mr-1.5" /> Referto paziente
              </button>
              {preview.mmg_letter_pending && (
                <button onClick={() => setPreviewTab("mmg")}
                        data-testid="preview-tab-mmg"
                        className={`px-3 py-2 text-sm font-medium transition-all -mb-px border-b-2 ${
                          previewTab === "mmg" ? "text-[#1E3A8A] border-[#1E3A8A]" : "text-slate-500 border-transparent hover:text-slate-800"
                        }`}>
                  <Send className="h-4 w-4 inline mr-1.5" /> Lettera al MMG
                </button>
              )}
              {preview.referral_pending && (
                <button onClick={() => setPreviewTab("ref")}
                        data-testid="preview-tab-ref"
                        className={`px-3 py-2 text-sm font-medium transition-all -mb-px border-b-2 ${
                          previewTab === "ref" ? "text-purple-700 border-purple-600" : "text-slate-500 border-transparent hover:text-slate-800"
                        }`}>
                  <Stethoscope className="h-4 w-4 inline mr-1.5" /> Consulto al collega
                </button>
              )}
            </div>
          </div>
          <div className="flex-1 bg-slate-800 overflow-hidden">
            {previewTab === "pdf" && (
              <iframe src={preview.download_url} title="Anteprima referto PDF"
                      className="w-full h-full border-0 bg-white"
                      data-testid="referto-preview-iframe" />
            )}
            {previewTab === "mmg" && (
              <div className="w-full h-full overflow-y-auto bg-white p-8">
                <div className="max-w-2xl mx-auto">
                  <div className="text-xs text-[#1E3A8A] uppercase tracking-widest font-semibold mb-2">
                    Lettera clinica AI · destinatario: MMG del paziente
                  </div>
                  <h3 className="text-lg font-heading font-bold text-slate-900 mb-4">
                    Anteprima messaggio Synappsi + email
                  </h3>
                  <div className="whitespace-pre-wrap text-[13px] text-slate-800 bg-slate-50 border border-slate-200 rounded-lg p-4 leading-[1.65]"
                       style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, 'Liberation Sans', sans-serif" }}>
                    {preview.mmg_letter_preview || "(Lettera in generazione…)"}
                  </div>
                  <p className="text-[11px] text-slate-500 italic mt-3">
                    Questa lettera verrà inviata come messaggio Synappsi al MMG selezionato e via email al suo indirizzo, alla conferma.
                  </p>
                </div>
              </div>
            )}
            {previewTab === "ref" && (
              <div className="w-full h-full overflow-y-auto bg-white p-8">
                <div className="max-w-2xl mx-auto">
                  <div className="text-xs text-purple-700 uppercase tracking-widest font-semibold mb-2">
                    Consulto al collega · destinatario: {preview.referral_specialist_name}
                  </div>
                  <h3 className="text-lg font-heading font-bold text-slate-900 mb-4">
                    Anteprima messaggio Synappsi + email
                  </h3>
                  <div className="whitespace-pre-wrap text-[13px] text-slate-800 bg-purple-50 border border-purple-200 rounded-lg p-4 leading-[1.65]"
                       style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, 'Liberation Sans', sans-serif" }}>
                    {preview.referral_letter_preview || "(Lettera in generazione…)"}
                  </div>
                  <p className="text-[11px] text-slate-500 italic mt-3">
                    Questo messaggio verrà inviato via sinapsi Synappsi al collega + email di notifica, alla conferma.
                    Una copia della lettera d'invio apparirà anche nel PDF che il paziente porterà in visita.
                  </p>
                </div>
              </div>
            )}
          </div>
          <div className="flex-shrink-0 bg-white border-t border-slate-200 px-4 py-2 text-[11px] text-slate-500 text-center">
            {previewTab === "pdf" && "Se il PDF non appare, apri in una "}
            {previewTab === "pdf" && (
              <a href={preview.download_url} target="_blank" rel="noopener noreferrer" className="underline text-emerald-700">nuova scheda</a>
            )}
            {previewTab === "pdf" && ". "}
            Nessun invio viene effettuato finché non premi "Conferma e invia tutto".
          </div>
        </div>
      )}
      {/* -------- Modale anteprima AI (on-demand) -------- */}
      {aiPreview && (
        <div className="fixed inset-0 z-[60] bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4"
             onClick={() => setAiPreview(null)}
             data-testid="ai-preview-modal">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden shadow-2xl"
               onClick={(e) => e.stopPropagation()}>
            <div className={`px-5 py-4 border-b flex items-center justify-between gap-3 ${
              aiPreview.color === "blue" ? "border-blue-200 bg-blue-50" :
              aiPreview.color === "purple" ? "border-purple-200 bg-purple-50" : "border-slate-200"
            }`}>
              <div className="flex items-center gap-2">
                <Sparkles className={`h-5 w-5 ${
                  aiPreview.color === "blue" ? "text-blue-600" :
                  aiPreview.color === "purple" ? "text-purple-600" : "text-slate-600"
                }`} />
                <h3 className="font-semibold text-slate-900">{aiPreview.title}</h3>
              </div>
              <button onClick={() => setAiPreview(null)} className="text-slate-400 hover:text-slate-700">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5">
              <div className="whitespace-pre-wrap text-[13px] text-slate-800 leading-[1.65] tracking-normal"
                 style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, 'Liberation Sans', sans-serif" }}>
                {aiPreview.text}
              </div>
              <p className="text-[11px] text-slate-500 italic mt-4">
                Bozza generata dall'AI — non ancora inviata. Chiudi per continuare la compilazione, poi conferma dalla schermata di anteprima finale.
              </p>
            </div>
            <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-2">
              <Button variant="outline" onClick={() => setAiPreview(null)}
                      className="border-slate-300 bg-white text-slate-700 hover:bg-slate-100">
                Chiudi
              </Button>
              <Button onClick={() => {
                        navigator.clipboard.writeText(aiPreview.text).then(() => toast.success("Copiato"));
                      }}
                      variant="outline"
                      className="border-slate-300 bg-white text-slate-700 hover:bg-slate-100">
                <Copy className="h-4 w-4 mr-1.5" /> Copia
              </Button>
              {(aiPreview.kind === "mmg" || aiPreview.kind === "referral") && (
                <Button onClick={() => {
                          setAiPreview(null);
                          toast.success(
                            aiPreview.kind === "mmg"
                              ? "Comunicazione MMG confermata — verrà inviata alla conferma del referto"
                              : "Comunicazione allo specialista confermata — verrà inviata alla conferma del referto"
                          );
                        }}
                        data-testid="ai-preview-confirm-send"
                        className={`text-white ${
                          aiPreview.color === "blue" ? "bg-[#1E3A8A] hover:bg-[#1E40AF]"
                                                     : "bg-purple-600 hover:bg-purple-700"
                        }`}>
                  <Check className="h-4 w-4 mr-1.5" /> Ok, invia alla conferma
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
      {/* -------- Modale Storia Clinica AI -------- */}
      {clinicalHistoryModal && (
        <div className="fixed inset-0 z-[60] bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4"
             onClick={() => setClinicalHistoryModal(false)}
             data-testid="clinical-history-modal">
          <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[88vh] flex flex-col overflow-hidden shadow-2xl"
               onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-purple-200 bg-purple-50 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 min-w-0">
                <History className="h-5 w-5 text-purple-600 flex-shrink-0" />
                <div className="min-w-0">
                  <h3 className="font-semibold text-slate-900 truncate">Storia clinica (AI)</h3>
                  <div className="text-[11px] text-slate-500">
                    Ricostruita da {clinicalHistoryCount} referti precedenti · rivedi e modifica se necessario
                  </div>
                </div>
              </div>
              <button onClick={() => setClinicalHistoryModal(false)}
                      className="text-slate-400 hover:text-slate-700 flex-shrink-0">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5">
              <Label className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide">
                Sintesi generata dall'AI (modificabile)
              </Label>
              <Textarea
                rows={16}
                value={clinicalHistoryEditor}
                onChange={(e) => setClinicalHistoryEditor(e.target.value)}
                data-testid="clinical-history-editor"
                style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, 'Liberation Sans', sans-serif" }}
                className="mt-1 text-[13px] leading-[1.65]" />
              <p className="text-[11px] text-slate-500 italic mt-2">
                Questo testo apparirà nel nuovo PDF come sezione "STORIA CLINICA (dai referti precedenti)"
                subito prima dell'anamnesi. Puoi correggerlo o eliminarlo prima di inviare.
              </p>
            </div>
            <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-2">
              <Button variant="outline" onClick={() => setClinicalHistoryModal(false)}
                      data-testid="clinical-history-cancel"
                      className="border-slate-300 bg-white text-slate-700 hover:bg-slate-100">
                Annulla
              </Button>
              <Button onClick={acceptClinicalHistory}
                      data-testid="clinical-history-accept"
                      className="bg-purple-600 hover:bg-purple-700 text-white">
                <Check className="h-4 w-4 mr-1.5" /> Inserisci nel referto
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
